import { useState, useRef, useCallback, useEffect } from 'react';
import './App.css';
import { PrintPlan } from './PrintPlan';
import { CEPortfolioReport } from './CEPortfolioReport';
import { RiskScoreModal, fmtRiskDate } from './RiskScoreModal';
import { LineChart, Line, XAxis, YAxis, Tooltip, Legend, ReferenceLine, ResponsiveContainer } from 'recharts';
import { supabase } from './supabaseClient';
import { snCreditMultiple } from './lib/snPayoff';
import {
  calcAnnualTable, calcBaselineTable, calcBreakEven, scenarioSummary,
  buildV2Params, calcJBFTwoBucketAnnualTable, buildJBFTwoBucketParams, RISK_TIERS,
  RMD_TABLE, deriveConversionSchedule, ssClaimFactor, rmdAgeForBirthYear, BASE_YEAR,
  CE_TIERS, tierForRiskScore,
} from './calcEngine';
import { ConfirmScreen } from './ConfirmScreen';
import SignIn from './SignIn';
import { proposeSchedule, boundLabel } from './scheduleOptimizer';

const INITIAL_STATE = {
  clientFirstName: '',
  clientLastName: '',
  email: '',
  state: '',
  clientDOB: '',
  retirementAge: 65,
  currentlyRetired: false,
  includeSpouse: false,
  spouseFirstName: '',
  spouseLastName: '',
  spouseDOB: '',
  balance401k: '',
  balanceIRA: '',
  balanceRothIRA: '',
  balanceOther: '',
  annuityBalance: '',
  annuityROR: 4,
  monthlyContribution: '',           // kept for backward-compat load migration
  monthlyRothContribution: '',       // → Roth balance pre-retirement
  monthlyOtherContribution: '',      // → Other/NQ balance pre-retirement
  preRetirementIncome: '',           // legacy field — migrated into an earned income row on load
  // Typed income streams (Stage A). Each row stacks into every year's tax within its age
  // window, independent of the retirement flag. SS + pension keep their own fields below.
  // Default amounts blank → no tax effect until entered.
  incomeRows: [
    { id: 1, label: 'Earned income',       amount: '', startAge: '', endAge: '', treatment: 'earned' },
    { id: 2, label: 'Qualified dividends',  amount: '', startAge: '', endAge: '', treatment: 'preferential' },
    { id: 3, label: 'Ordinary dividends',   amount: '', startAge: '', endAge: '', treatment: 'ordinary' },
    { id: 4, label: 'Capital gains',        amount: '', startAge: '', endAge: '', treatment: 'preferential' },
  ],
  clientSSMonthly: '',
  clientSSClaimingAge: 67,
  clientSSBasisAge: 67,   // age the entered SS benefit is stated at (SSA statement age)
  spouseSSMonthly: '',
  spouseSSClaimingAge: 67,
  spouseSSBasisAge: 67,
  pensionMonthly: '',
  monthlyExpenses: '',
  growthRate: 7,
  currentTaxRate: '',
  retirementTaxRate: '',
  ssCOLARate: 1.8,
  rothScenarios: [],
  conversionGoal: 'full_roth',
  customTarget: '',
  // Zoom 2 appointment (set after booking on the embedded GHL calendar). Together with
  // a completed riskScore, this unlocks the CE tier selector (Zoom 1 runs the T2 middle
  // tier, 10% — provisional until the Risk Score picks the band; revised Aug 6 2026).
  zoom2Date: '',
  // V2 global assumptions
  filingStatus: 'mfj',
  dependents: 0,          // # of dependents — CA exemption credit only ($475 each). Default 0.
  lifeExpectancy: 90,
  inflationRate: 2.75,
  survivorEnabled: false,
  survivorAge: 80,
  stateFlatRate: '',
  // Display-only (never passed to the engine): CE Year-1 dividend months, LTFI yield, goal lean.
  year1CEMonths: 12,
  ltfiYield: 6,
  goalLean: 'ltfi',   // 'legacy' | 'ltfi' — Zoom 1 defaults to the Tax-Free Lifetime Income lens
                      // (locked Aug 4 2026): income/tax wins headline the quick build at any tier;
                      // flip to Legacy deliberately when that's the prospect's stated goal.
                      // Saved cases load their own stored lean (old cases fall back to 'legacy').
  // Optional annual-table columns — all default OFF so existing plans (Fox) stay byte-identical.
  // showAdvisoryFee also drives the model (deducts a flat-by-tier fee from Bucket A), so it must
  // trigger a live recompute (wired into the recompute useEffect deps).
  showAdvisoryFee: false,   // CE-only advisory fee, per-year + cumulative (Bucket A, in arrears)
  showRothIncome: false,    // Roth Distribution ("Roth Income") column
  showBaseTaxes: false,     // Total Base Taxes = all taxes EXCEPT the Roth conversion tax
};

const INFLATION = 0.0275;

// GHL Zoom 2 booking URL — "CE — Roth Convert Engine — Zoom 2" calendar (slug set Aug 4 2026).
const ZOOM2_BOOKING_URL = 'https://api.leadconnectorhq.com/widget/bookings/rce-zoom-2-plan-review';
// GHL secure statement upload page (embedded JBF Secure Uploader form; tag: "statement uploaded").
const UPLOAD_PAGE_URL = 'https://rothconvertengine.com/upload-page';

function dobToAge(dob) {
  if (!dob) return null;
  const today = new Date();
  const birth = new Date(dob);
  let age = today.getFullYear() - birth.getFullYear();
  const m = today.getMonth() - birth.getMonth();
  if (m < 0 || (m === 0 && today.getDate() < birth.getDate())) age--;
  return age;
}

// Age the client ATTAINS during the projection's base year (BASE_YEAR − birth year).
// The annual tables are calendar-year rows anchored to BASE_YEAR, so everything that
// feeds the engines must start from this age. dobToAge (age today) runs one year low
// for anyone whose birthday falls later in the year, which fabricated a phantom extra
// pre-RMD conversion year (RMDs land in the calendar year age 73 is ATTAINED).
// dobToAge remains for display-only spots where "age today" is the right answer.
function attainedAge(dob) {
  if (!dob) return null;
  const y = new Date(dob).getFullYear();
  return Number.isFinite(y) ? BASE_YEAR - y : null;
}

function num(val) {
  const n = parseFloat(val);
  return isNaN(n) ? 0 : n;
}

// Default typed-income rows for a freshly loaded/normalized form. If the loaded case has
// no incomeRows but carries the legacy preRetirementIncome, seed it into the Earned row
// (window = current age → retirement age) and clear the legacy field so it isn't double
// counted. Idempotent: forms that already have incomeRows are returned unchanged.
function migrateIncomeRows(form) {
  if (Array.isArray(form.incomeRows) && form.incomeRows.length > 0) return form;
  const pre = parseFloat(form.preRetirementIncome) || 0;
  const currentAge = form.clientDOB ? dobToAge(form.clientDOB) : null;
  const retAge = parseFloat(form.retirementAge) || 65;
  const rows = [
    { id: 1, label: 'Earned income',
      amount:   pre > 0 ? String(pre) : '',
      startAge: pre > 0 && currentAge != null ? currentAge : '',
      endAge:   pre > 0 ? retAge : '',
      treatment: 'earned' },
    { id: 2, label: 'Qualified dividends', amount: '', startAge: '', endAge: '', treatment: 'preferential' },
    { id: 3, label: 'Ordinary dividends',  amount: '', startAge: '', endAge: '', treatment: 'ordinary' },
    { id: 4, label: 'Capital gains',       amount: '', startAge: '', endAge: '', treatment: 'preferential' },
  ];
  return { ...form, incomeRows: rows, preRetirementIncome: '' };
}

function formatDollar(val) {
  if (val === null || val === undefined) return '—';
  const abs = Math.abs(val);
  if (abs >= 1_000_000) return '$' + (val / 1_000_000).toFixed(2) + 'M';
  return '$' + Math.round(val).toLocaleString();
}

function formatDollarFull(val) {
  if (val === null || val === undefined) return '—';
  return '$' + Math.round(val).toLocaleString();
}

function calcAfterTaxLegacy(row) {
  const ira = (row.iraBalance || 0) * 0.75;
  const roth = (row.rothBalance || 0) * 1.0;
  const nq = (row.nonQualBalance || 0) * 0.95;
  const annuity = (row.annuityBalance || 0) * 0.75;
  return Math.round(ira + roth + nq + annuity);
}

// Auto-calculate RMD-driven conversion amounts for JBF CE scenarios.
// Returns null if insufficient data (no DOB or no IRA balance).
function calcRMDAutoFill(form, riskTier = 'M') {
  if (!form.clientDOB) return null;
  const currentAge = attainedAge(form.clientDOB);
  if (!currentAge) return null;

  const birthYear = new Date(form.clientDOB).getFullYear();
  const rmdStartAge = rmdAgeForBirthYear(birthYear);
  const iraBalance = (parseFloat(form.balance401k) || 0) + (parseFloat(form.balanceIRA) || 0);

  // Post-RMD-age clients have NO pre-RMD runway to auto-size against. The old
  // window `rmdStartAge − currentAge` went ≤ 0 here, collapsed to n=1, and dumped
  // the ENTIRE IRA into Year 1. Never auto-front-load: leave the schedule blank so
  // conversion amounts stay advisor-controlled at any age (every field is editable).
  if (currentAge >= rmdStartAge) {
    const horizon = Math.max(1, Math.min(10, (parseFloat(form.lifeExpectancy) || 90) - currentAge));
    return { rmdStartAge, conversionWindow: horizon, annualAmount: 0 };
  }

  const n = Math.max(1, rmdStartAge - currentAge);
  // NOTE: no auto-sizing. The per-year conversion schedule is the source of truth and
  // IRA depletion is an OUTPUT, not a target — we no longer amortize the IRA to $0 by
  // the RMD age (which inflated a small schedule up to a huge annual conversion).
  return { rmdStartAge, conversionWindow: n, annualAmount: 0 };
}

function calculatePlan(form) {
  const cola = num(form.ssCOLARate) / 100;
  const growthRate = num(form.growthRate) / 100;
  const monthlyRate = growthRate / 12;

  // --- CLIENT AGE & YEARS TO RETIREMENT ---
  const clientAge = attainedAge(form.clientDOB);
  if (clientAge === null) return { error: 'Please enter client date of birth.' };
  // Sanity guard (added after a DOB year typo of "11959" computed age -9933 and
  // projected 10,000 years of compounding into a live Zoom 1 export): any age
  // outside a plausible planning range is an input error, never a projection.
  if (clientAge < 18 || clientAge > 105) {
    return { error: `Client DOB looks mistyped — it computes to age ${clientAge}. Please correct the birth year.` };
  }
  if (form.includeSpouse && form.spouseDOB) {
    const _sa = attainedAge(form.spouseDOB);
    if (_sa != null && (_sa < 18 || _sa > 105)) {
      return { error: `Spouse DOB looks mistyped — it computes to age ${_sa}. Please correct the birth year.` };
    }
  }
  // If client is already at or past retirement age, treat as retired now
  const effectiveClientAge = Math.min(clientAge, parseFloat(form.retirementAge) || clientAge);
  const yearsToRetirement = num(form.retirementAge) - effectiveClientAge;
  if (yearsToRetirement < 0) return { error: 'Retirement age must be greater than current age.' };

  // --- 1. INCOME GAP ---
  // SS: rescale the entered benefit (stated at its basis age) to the default
  // claiming age using the same FRA-based factors the engine uses, so this
  // baseline household figure matches the projected checks. Global/baseline
  // figure — per-scenario claiming ages are applied inside the V2 engine.
  const clientBirthYearForSS = form.clientDOB ? new Date(form.clientDOB).getFullYear() : (new Date().getFullYear() - (clientAge ?? 65));
  const clientSSClaim = num(form.clientSSClaimingAge) || 67;
  const clientSSBasis = num(form.clientSSBasisAge) || clientSSClaim;
  const clientSSAtRetirement = num(form.clientSSMonthly) * ssClaimFactor(clientSSClaim, clientSSBasis, clientBirthYearForSS);

  let spouseSSAtRetirement = 0;
  if (form.includeSpouse && form.spouseDOB) {
    const spouseBirthYearForSS = new Date(form.spouseDOB).getFullYear();
    const spouseSSClaim = num(form.spouseSSClaimingAge) || 67;
    const spouseSSBasis = num(form.spouseSSBasisAge) || spouseSSClaim;
    spouseSSAtRetirement = num(form.spouseSSMonthly) * ssClaimFactor(spouseSSClaim, spouseSSBasis, spouseBirthYearForSS);
  }

  const totalGuaranteedMonthly = clientSSAtRetirement + spouseSSAtRetirement + num(form.pensionMonthly);
  const inflatedMonthlyExpenses = num(form.monthlyExpenses) * Math.pow(1 + INFLATION, yearsToRetirement);
  const rawGap = inflatedMonthlyExpenses - totalGuaranteedMonthly;
  const monthlyGap = Math.max(0, rawGap);
  const profile = rawGap <= 0 ? 'B' : 'A';

  // --- 2. INCOME SLEEVE ---
  const sleeveRates = [0.08, 0.09, 0.10, 0.11];
  const primarySleeveRate = 0.11;
  const primarySleeveSize = profile === 'A' ? (monthlyGap * 12) / primarySleeveRate : 0;
  const primarySleeveMonthly = primarySleeveSize * primarySleeveRate / 12;

  const sleeveSensitivity = sleeveRates.map(r => ({
    rate: r,
    sleeveSize: profile === 'A' ? (monthlyGap * 12) / r : 0,
    monthlyIncome: profile === 'A' ? (monthlyGap * 12) / r * r / 12 : 0,
  }));

  // --- 3. PORTFOLIO PROJECTION ---
  const totalAssets =
    num(form.balance401k) +
    num(form.balanceIRA) +
    num(form.annuityBalance) +
    num(form.balanceRothIRA) +
    num(form.balanceOther);

  const months = yearsToRetirement * 12;
  const growthFactor = Math.pow(1 + monthlyRate, months);
  const contrib = (num(form.monthlyRothContribution) || 0) + (num(form.monthlyOtherContribution) || 0) || num(form.monthlyContribution);

  const fvContrib = monthlyRate > 0
    ? contrib * ((growthFactor - 1) / monthlyRate)
    : contrib * months;
  const portfolioAtRetirement = totalAssets * growthFactor + fvContrib;

  const annualWithdrawal = monthlyGap * 12;
  const effectiveWithdrawalRate = portfolioAtRetirement > 0
    ? (annualWithdrawal / portfolioAtRetirement) * 100
    : 0;

  // Year-by-year depletion from retirement to age 95, inflating withdrawal at 2.75%/yr
  let portfolioValue = portfolioAtRetirement;
  let inflatingWithdrawal = monthlyGap * 12;
  let depletionAge = null;
  const projection = [];

  for (let age = num(form.retirementAge); age <= 95; age++) {
    projection.push({ age, balance: Math.max(0, portfolioValue) });
    portfolioValue = portfolioValue * (1 + growthRate) - inflatingWithdrawal;
    inflatingWithdrawal = inflatingWithdrawal * 1.0275;
    if (portfolioValue <= 0 && depletionAge === null) {
      depletionAge = age;
    }
  }

  const survivesTo95 = depletionAge === null;

  // ── V2 Engine integration ──────────────────────────────────────────────────
  const spouseAge = form.includeSpouse && form.spouseDOB ? attainedAge(form.spouseDOB) : null;
  // Commit 1: grow the baseline's existing (pre-conversion) Roth on the SAME basis the
  // Convert Engine grows it — the scenario's risk-tier Roth ROR — instead of a hardcoded
  // 7%, so its balance is identical in both. Keyed off the primary JBF scenario's tier
  // (risk tier M = 7% → byte-identical to today).
  const primaryScen = form.rothScenarios.find(s => s.vehicle === 'jbf') || form.rothScenarios[0];
  const baselineRothROR = primaryScen ? (RISK_TIERS[primaryScen.riskTier ?? 'M'] ?? 0.07) : 0.07;
  const baseParams = buildV2Params(form, null, { clientAge, spouseAge, rothROR: baselineRothROR });
  const v2BaselineTable = calcBaselineTable(baseParams);

  const v2ScenarioTables = {};
  const v2BreakEvenAges = {};
  const v2Summaries = {};
  const v2JBFExtras = {}; // JBF CE-specific summary extras per scenario

  form.rothScenarios.forEach(scenario => {
    let tbl;
    if (scenario.vehicle === 'jbf') {
      const jbfParams = buildJBFTwoBucketParams(form, scenario, { clientAge, spouseAge });
      const result = calcJBFTwoBucketAnnualTable(jbfParams);
      tbl = result.rows;
      const riskROR = RISK_TIERS[scenario.riskTier ?? 'M'] ?? 0.07;
      const lifeExp = num(form.lifeExpectancy) || 90;
      // Self-Converting Benefit: for each conversion year, the tax that would have been
      // withheld under standard internal withholding (= conversionTaxCost) compounds
      // forward to life expectancy at riskROR. Sum = extra Roth dollars that exist
      // solely because taxes were paid externally instead of withheld from the IRA.
      const selfConvertingBenefit = Math.round(
        tbl.reduce((sum, row) => {
          const taxCost = row.conversionTaxCost || 0;
          if (taxCost <= 0) return sum;
          const yearsToGrow = Math.max(0, lifeExp - row.age);
          return sum + taxCost * Math.pow(1 + riskROR, yearsToGrow);
        }, 0)
      );
      const ceFundedAge = result.ceFullyFundedAge;
      const ceFundedYear = ceFundedAge != null ? (ceFundedAge - clientAge + 1) : null;
      v2JBFExtras[scenario.id] = {
        ceTargetSize: parseFloat(scenario.ceTargetSize) || 0,
        ceFundedAge,
        ceFundedYear,
        totalExternalTaxPaid:  result.totalExternalTaxPaid,
        totalJBFTaxFunded:     result.totalJBFTaxFunded,
        conversionCompleteRow: result.conversionCompleteRow,
        // SAFETY: does the CE target's 11% distribution cover the PEAK conversion-tax year?
        peakConvTaxCost:       result.peakConvTaxCost,
        ceMinTargetForPeak:    result.ceMinTargetForPeak,
        ceTargetCoversPeak:    result.ceTargetCoversPeak,
        cePeakTargetShortfall: result.cePeakTargetShortfall,
        year1Amount:  parseFloat(scenario.year1Amount) || parseFloat(scenario.conversionAmount) || 0,
        annualAmount: parseFloat(scenario.conversionAmount) || 0,
        riskTier:     scenario.riskTier || 'M',
        selfConvertingBenefit,
        engineSeedSource:  scenario.engineSeedSource || 'nonqual',
        year1TopUpFromNQ:  result.year1TopUpFromNQ || 0,
        // Year-1 CE offset (display-only): the CE bucket after Year-1 top-up throws 11%;
        // cost-to-start = Year-1 conversion tax minus a (year1CEMonths/12)-prorated year of it.
        year1CESeed:         result.year1CESeed || 0,
        year1ConversionTax:  tbl.find(r => r.conversionAmount > 0)?.conversionTaxCost || 0,
        // Reconciled with the engine: cost-to-start IS the Year-1 external tax; cost-to-convert
        // is the lifetime external tax (so cost-to-start can never exceed cost-to-convert).
        year1ExternalTax:    tbl.find(r => r.conversionAmount > 0)?.externalTaxPaid || 0,
        scheduleTotal:       tbl.reduce((s, r) => s + (r.conversionAmount || 0), 0),
      };
    } else {
      const sParams = buildV2Params(form, scenario, { clientAge, spouseAge });
      tbl = calcAnnualTable(sParams);
    }
    v2ScenarioTables[scenario.id] = tbl;
    v2BreakEvenAges[scenario.id] = calcBreakEven(tbl, v2BaselineTable);
    v2Summaries[scenario.id] = scenarioSummary(tbl, v2BaselineTable);
  });

  // ── DIAGNOSTIC: first 3 rows per scenario ────────────────────────────────
  form.rothScenarios.forEach(scenario => {
    const tbl = v2ScenarioTables[scenario.id];
    if (!tbl) return;
    const isJBF = scenario.vehicle === 'jbf';
    const label = scenario.name || `Scenario ${scenario.id}`;
    console.group(`=== DIAGNOSTIC: ${label} (vehicle: ${scenario.vehicle}) ===`);
    tbl.slice(0, 3).forEach((r, i) => {
      const iraStart = i === 0
        ? (num(form.balance401k) + num(form.balanceIRA))
        : tbl[i - 1].iraBalance;
      const base = {
        Year: i + 1,
        Age: r.age,
        'IRA Start': iraStart,
        'Conversion': r.conversionAmount,
        ...(isJBF ? {
          'Conversion Cost': r.conversionTaxCost,
          'CE Pays': r.jbfYieldUsedForTax,
          'You Pay': r.externalTaxPaid,
        } : {}),
        'RMD Amt': r.rmd,
        'IRA End': r.iraBalance,
        'Taxable Income': r.taxableIncome,
        'Federal Tax': r.fedTax,
        'State Tax': r.stateTax,
        'Total Tax': r.totalTax,
      };
      if (isJBF) {
        base['Convert Engine Bal'] = r.jbfBucketBalance;
      }
      console.log(`  Row ${i + 1}:`, base);
    });
    console.groupEnd();
  });
  // ── END DIAGNOSTIC ────────────────────────────────────────────────────────

  return {
    profile,
    clientAge,
    yearsToRetirement,
    clientSSAtRetirement,
    spouseSSAtRetirement,
    totalGuaranteedMonthly,
    inflatedMonthlyExpenses,
    monthlyGap,
    primarySleeveSize,
    primarySleeveMonthly,
    sleeveSensitivity,
    totalAssets,
    portfolioAtRetirement,
    annualWithdrawal,
    effectiveWithdrawalRate,
    depletionAge,
    survivesTo95,
    projection,
    // V2 results
    v2BaselineTable,
    v2ScenarioTables,
    v2BreakEvenAges,
    v2Summaries,
    v2JBFExtras,
    error: null,
  };
}

// ---- REUSABLE FIELD COMPONENTS ----

function TextField({ label, value, onChange, placeholder, hint }) {
  return (
    <div className="field">
      <label className="field-label">{label}{hint && <span className="field-hint">{hint}</span>}</label>
      <input className="field-input" type="text" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder || ''} />
    </div>
  );
}

function EmailField({ label, value, onChange, placeholder }) {
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      <input className="field-input" type="email" value={value} onChange={e => onChange(e.target.value)} placeholder={placeholder || ''} />
    </div>
  );
}

function DateField({ label, value, onChange }) {
  // min/max stop browser date pickers from accepting typo years (e.g. 11959).
  return (
    <div className="field">
      <label className="field-label">{label}</label>
      <input
        className="field-input"
        type="date"
        min="1920-01-01"
        max="2010-12-31"
        value={value}
        onChange={e => onChange(e.target.value)}
      />
    </div>
  );
}

function CurrencyField({ label, value, onChange, hint }) {
  return (
    <div className="field">
      <label className="field-label">{label}{hint && <span className="field-hint">{hint}</span>}</label>
      <div className="field-adornment-wrap">
        <span className="field-adornment">$</span>
        <input className="field-input adornment-left" type="number" min="0" step="1000" value={value} onChange={e => onChange(e.target.value)} placeholder="0" />
      </div>
    </div>
  );
}

function NumberField({ label, value, onChange, min, max, step, suffix, hint, disabled }) {
  return (
    <div className="field">
      <label className="field-label">{label}{hint && <span className="field-hint">{hint}</span>}</label>
      <div className="field-adornment-wrap">
        <input
          className={`field-input${suffix ? ' adornment-right' : ''}${disabled ? ' field-disabled' : ''}`}
          type="number" min={min} max={max} step={step || 1} value={value}
          onChange={e => !disabled && onChange(Number(e.target.value))}
          readOnly={disabled}
        />
        {suffix && <span className={`field-adornment right${disabled ? ' adornment-disabled' : ''}`}>{suffix}</span>}
      </div>
    </div>
  );
}

function AgeField({ label, value, onChange, hint }) {
  return <NumberField label={label} value={value} onChange={onChange} min={50} max={80} step={1} suffix="yrs" hint={hint} />;
}

function SelectField({ label, value, onChange, options, hint }) {
  return (
    <div className="field">
      <label className="field-label">{label}{hint && <span className="field-hint">{hint}</span>}</label>
      <select className="field-input field-select" value={value} onChange={e => onChange(e.target.value)}>
        {options.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
      </select>
    </div>
  );
}

// ── Typed income rows (Stage A) ───────────────────────────────────────────────
const INCOME_TREATMENTS = [
  { value: 'ordinary',     label: 'Ordinary' },
  { value: 'earned',       label: 'Earned' },
  { value: 'preferential', label: 'Preferential (QDI / LTCG)' },
];
// Picker presets — SS + pension deliberately excluded (they keep their own fields).
const INCOME_PICKER = [
  { label: 'Rental',          treatment: 'ordinary' },
  { label: 'Annuity',         treatment: 'ordinary' },
  { label: 'Structured note', treatment: 'ordinary' },
  { label: 'Other',           treatment: 'ordinary' },
];

function IncomeSection({ form, set }) {
  const rows = Array.isArray(form.incomeRows) ? form.incomeRows : [];
  const currentAge = dobToAge(form.clientDOB) || 65;
  const retAge     = parseFloat(form.retirementAge) || 65;
  const lifeExp    = parseFloat(form.lifeExpectancy) || 90;
  // Earned income defaults to ending at the age they stop working (retirement); other
  // streams default to running through life expectancy. Fully editable per row.
  const defEnd  = (t) => (t === 'earned' ? retAge : lifeExp);
  const nextId  = () => rows.reduce((m, r) => Math.max(m, Number(r.id) || 0), 0) + 1;
  const commit  = (next) => set('incomeRows')(next);
  const update  = (id, patch) => commit(rows.map(r => (r.id === id ? { ...r, ...patch } : r)));
  const remove  = (id) => commit(rows.filter(r => r.id !== id));
  const add     = (preset) => commit([...rows, {
    id: nextId(), label: preset.label, amount: '', treatment: preset.treatment,
    startAge: currentAge, endAge: defEnd(preset.treatment),
  }]);
  // Entering an amount auto-fills a sensible window so the row takes effect immediately.
  const onAmount = (r, v) => update(r.id, {
    amount: v,
    startAge: r.startAge === '' || r.startAge == null ? currentAge : r.startAge,
    endAge:   r.endAge === '' || r.endAge == null ? defEnd(r.treatment) : r.endAge,
  });

  return (
    <div className="income-section">
      <div className="income-head">
        <span className="income-col-label">Income stream</span>
        <span className="income-col-amt">Annual amount</span>
        <span className="income-col-age">Start age</span>
        <span className="income-col-age">End age</span>
        <span className="income-col-treat">Tax treatment</span>
        <span className="income-col-x" />
      </div>
      {rows.map(r => (
        <div className="income-row" key={r.id}>
          <input className="income-input income-label-input" type="text" value={r.label}
            onChange={e => update(r.id, { label: e.target.value })} placeholder="Label" />
          <input className="income-input income-amt-input" type="number" min="0" step="1000" value={r.amount}
            onChange={e => onAmount(r, e.target.value)} placeholder="$0" />
          <input className="income-input income-age-input" type="number" min="40" max="100" step="1" value={r.startAge}
            onChange={e => update(r.id, { startAge: e.target.value })} placeholder={currentAge} />
          <input className="income-input income-age-input" type="number" min="40" max="105" step="1" value={r.endAge}
            onChange={e => update(r.id, { endAge: e.target.value })} placeholder={defEnd(r.treatment)} />
          <select className="income-input income-treat-input" value={r.treatment}
            onChange={e => update(r.id, { treatment: e.target.value })}>
            {INCOME_TREATMENTS.map(t => <option key={t.value} value={t.value}>{t.label}</option>)}
          </select>
          <button type="button" className="income-remove" title="Remove row" onClick={() => remove(r.id)}>×</button>
        </div>
      ))}
      <div className="income-picker">
        <span className="income-picker-label">Add income:</span>
        {INCOME_PICKER.map(p => (
          <button type="button" key={p.label} className="income-add-btn" onClick={() => add(p)}>+ {p.label}</button>
        ))}
      </div>
      <p className="income-note">
        Earned income applies whether or not the client is marked retired. Social Security and
        pension stay on their own fields above. Preferential (qualified dividends / long-term
        capital gains) is taxed at a flat 15% in this stage.
      </p>
    </div>
  );
}

const VEHICLE_OPTIONS = [
  { value: 'jbf',      label: 'JBF Convert Engine (tiered income portfolio)' },
  { value: 'proposed', label: 'Accelerated Conversion (custom ROR)' },
];

const TAX_METHOD_OPTIONS = [
  { value: 'cash',     label: 'Cash Account — Pay tax from outside savings' },
  { value: 'internal', label: 'Internal — Withhold from converted amount' },
];

function fmt(val) {
  if (val === null || val === undefined) return '—';
  return '$' + Math.round(val).toLocaleString();
}

function ComputeDetailTable({ scenario, results, initialIRA, form }) {
  const [open, setOpen] = useState(false);

  const table  = results?.v2ScenarioTables?.[scenario.id];

  if (!table || table.length === 0) return null;

  // Real RMD start age from the client's DOB (SECURE 2.0), not a hardcoded 73.
  const rmdStartAge = form?.clientDOB ? rmdAgeForBirthYear(new Date(form.clientDOB).getFullYear()) : 73;

  const vehicle  = scenario.vehicle;
  const isJBF    = vehicle === 'jbf';
  // Tier-aware engine rates (legacy scenarios without a tier stay on 11%/3%).
  const _tier    = CE_TIERS[scenario.tier] || null;
  const distRate = _tier ? _tier.planningRate : 0.11;
  const retRate  = _tier ? 0 : 0.03;
  const distPct  = Math.round(distRate * 100);
  const retPct   = Math.round(retRate * 100);

  // Totals
  let totConv = 0, totRMD = 0, totFed = 0, totState = 0, totIRMAA = 0, totTax = 0;
  let totConvCost = 0, totCEPays = 0, totExtTax = 0;
  table.forEach(r => {
    totConv  += r.conversionAmount || 0;
    totRMD   += r.rmd || 0;
    totFed   += r.fedTax || 0;
    totState += r.stateTax || 0;
    totIRMAA += r.irmaa || 0;
    totTax   += r.totalTax || 0;
    totConvCost += r.conversionTaxCost || 0;   // Conversion Cost
    totCEPays   += r.jbfYieldUsedForTax || 0;  // CE Pays
    totExtTax   += r.externalTaxPaid || 0;     // You Pay
  });
  // Cost-story strip: Y1 = first conversion year's You-Pay; total = You-Pay across plan;
  // converted = total Conversion. Same figures used on the Excel Summary block.
  const y1ExtTax  = table.find(r => (r.conversionAmount || 0) > 0)?.externalTaxPaid || 0;
  const finalRow = table[table.length - 1];
  const finalRoth = finalRow ? (isJBF ? finalRow.rothBalance : finalRow.rothBalance) : 0;

  let cumTax = 0;

  return (
    <div className="cdt-wrap">
      <button className="cdt-toggle-btn" onClick={() => setOpen(o => !o)}>
        {open ? 'Hide Compute Detail ▲' : 'Show Compute Detail ▼'}
      </button>
      {open && (
        <div className="cdt-scroll">
          <table className="cdt-table">
            <thead>
              <tr>
                <th>Year</th>
                <th>Age</th>
                <th>IRA Start</th>
                <th>Conversion</th>
                {isJBF && <>
                  <th>Conversion Cost</th>
                  <th>CE Pays</th>
                  <th>You Pay</th>
                </>}
                <th>RMD Div</th>
                <th>RMD Amt</th>
                <th>IRA End</th>
                <th>Gross Income</th>
                <th>Std Ded</th>
                <th>Sr Bonus</th>
                <th>Taxable Inc</th>
                <th>Fed Tax</th>
                <th>State Tax</th>
                <th>IRMAA</th>
                <th>Total Tax</th>
                <th>Cum Tax</th>
                {isJBF && <>
                  <th>Convert Engine Start</th>
                  <th>Convert Engine Dist ({distPct}%)</th>
                  <th>Convert Engine Ret ({retPct}%)</th>
                  <th>Convert Engine End</th>
                  <th>Std Roth Start</th>
                  <th>Std Roth Contrib</th>
                  <th>Std Roth Growth</th>
                  <th>Std Roth End</th>
                  <th>Combined Roth</th>
                </>}
              </tr>
            </thead>
            <tbody>
              {table.map((row, i) => {
                const iraStart  = i === 0 ? initialIRA : table[i - 1].iraBalance;
                const divisor   = (row.rmd > 0 && row.age >= rmdStartAge) ? (RMD_TABLE[Math.min(row.age, 95)] ?? '—') : '—';
                cumTax += row.totalTax || 0;

                // JBF bucket start
                const jbfStart = isJBF
                  ? (i === 0 ? 0 : (table[i - 1].jbfBucketBalance || 0))
                  : null;
                const jbfEnd   = isJBF ? (row.jbfBucketBalance || 0) : null;
                const jbfDist  = isJBF && jbfStart > 0 ? Math.round(jbfStart * distRate) : 0;
                const jbfRet   = isJBF && jbfStart > 0 ? Math.round(jbfStart * retRate) : 0;

                const stdRothStart = isJBF
                  ? (i === 0 ? 0 : (table[i - 1].standardRothBalance || 0))
                  : null;
                const stdRothEnd  = isJBF ? (row.standardRothBalance || 0) : null;
                const stdRothContrib = isJBF ? (row.conversionToRoth || 0) : null;
                const stdRothGrowth = (isJBF && stdRothStart != null && stdRothEnd != null && stdRothContrib != null)
                  ? stdRothEnd - stdRothStart - stdRothContrib
                  : null;

                return (
                  <tr key={row.age} className={row.isRetired ? 'cdt-retired' : ''}>
                    <td>{i + 1}</td>
                    <td>{row.age}</td>
                    <td>{fmt(iraStart)}</td>
                    <td>{fmt(row.conversionAmount)}</td>
                    {isJBF && <>
                      <td>{fmt(row.conversionTaxCost)}</td>
                      <td>{fmt(row.jbfYieldUsedForTax)}</td>
                      <td>{fmt(row.externalTaxPaid)}</td>
                    </>}
                    <td className="cdt-num">{divisor !== '—' ? divisor : '—'}</td>
                    <td>{fmt(row.rmd)}</td>
                    <td>{fmt(row.iraBalance)}</td>
                    <td>{fmt(row.grossTaxableIncome)}</td>
                    <td>{fmt(row.standardDeduction)}</td>
                    <td>{fmt(row.seniorBonusDeduction)}</td>
                    <td>{fmt(row.taxableIncome)}</td>
                    <td>{fmt(row.fedTax)}</td>
                    <td>{fmt(row.stateTax)}</td>
                    <td>{fmt(row.irmaa)}</td>
                    <td>{fmt(row.totalTax)}</td>
                    <td>{fmt(cumTax)}</td>
                    {isJBF && <>
                      <td>{fmt(jbfStart)}</td>
                      <td>{fmt(jbfDist)}</td>
                      <td>{fmt(jbfRet)}</td>
                      <td>{fmt(jbfEnd)}</td>
                      <td>{fmt(stdRothStart)}</td>
                      <td>{fmt(stdRothContrib)}</td>
                      <td>{stdRothGrowth != null ? fmt(stdRothGrowth) : '—'}</td>
                      <td>{fmt(stdRothEnd)}</td>
                      <td>{fmt(row.rothBalance)}</td>
                    </>}
                  </tr>
                );
              })}
            </tbody>
            <tfoot>
              <tr className="cdt-totals">
                <td colSpan={2}><strong>TOTALS</strong></td>
                <td>—</td>
                <td><strong>{fmt(totConv)}</strong></td>
                {isJBF && <>
                  <td><strong>{fmt(totConvCost)}</strong></td>
                  <td><strong>{fmt(totCEPays)}</strong></td>
                  <td><strong>{fmt(totExtTax)}</strong></td>
                </>}
                <td>—</td>
                <td><strong>{fmt(totRMD)}</strong></td>
                <td>{fmt(finalRow?.iraBalance)}</td>
                <td>—</td>
                <td>—</td>
                <td>—</td>
                <td>—</td>
                <td><strong>{fmt(totFed)}</strong></td>
                <td><strong>{fmt(totState)}</strong></td>
                <td><strong>{fmt(totIRMAA)}</strong></td>
                <td><strong>{fmt(totTax)}</strong></td>
                <td><strong>{fmt(totTax)}</strong></td>
                {isJBF    && <>
                  <td colSpan={8}>—</td>
                  <td><strong>{fmt(finalRoth)}</strong></td>
                </>}
              </tr>
            </tfoot>
          </table>
          {isJBF && (
            <div className="cdt-story-strip">
              You pay <strong>{fmt(y1ExtTax)}</strong> to start, <strong>{fmt(totExtTax)}</strong> total, to turn <strong>{fmt(totConv)}</strong> into tax-free Roth.
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function ScenarioForm({ scenario, index, onChange, onRemove, onSetLive, isLive, clientAge, results, form, set }) {
  const upd = key => val => onChange({ ...scenario, [key]: val });
  // updManual clears rmdAutoCalc when the user edits an auto-filled field
  const updManual = key => val => onChange({ ...scenario, [key]: val, rmdAutoCalc: false });

  const numConvYears = Math.max(1, (parseFloat(scenario.endYear) || 1) - (parseFloat(scenario.startYear) || 1) + 1);
  const totalConverted = (parseFloat(scenario.conversionAmount) || 0) * numConvYears;
  const convEndAge = (clientAge || 0) + (parseFloat(scenario.endYear) || 0);

  // Flexible per-year conversion schedule (JBF). The stored array is the source of
  // truth; if empty (new/legacy scenario) we show the two-tier preset expansion so
  // the grid is never blank. All edits write the full materialized array back.
  const schedule = (Array.isArray(scenario.conversionSchedule) && scenario.conversionSchedule.length)
    ? scenario.conversionSchedule
    : (clientAge != null ? deriveConversionSchedule(scenario, clientAge) : []);
  const scheduleTotal = schedule.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);

  // Summary readout (replaces the old DOB placeholder). Out-of-Pocket Cost = the sum of
  // the "You Pay" (external conversion-tax) column NOT funded by the Convert Engine.
  // Amount Converted = the total per-year conversion schedule (projected through LE).
  const scenTable = results?.v2ScenarioTables?.[scenario.id];
  const lifeExp   = parseFloat(form?.lifeExpectancy) || 90;
  const outOfPocketToConvert = scenTable
    ? Math.round(scenTable.reduce((s, r) => s + (r.externalTaxPaid || 0), 0)) : null;
  const amountConvertedByLE  = scenTable
    ? Math.round(scenTable.reduce((s, r) => s + (r.conversionAmount || 0), 0))
    : Math.round(scheduleTotal);
  const scheduleReadout = (
    <div className="schedule-readout">
      <div className="schedule-readout-item">
        <span>Out-of-Pocket Cost to Convert</span>
        <strong>{outOfPocketToConvert != null ? `$${outOfPocketToConvert.toLocaleString()}` : '—'}</strong>
      </div>
      <div className="schedule-readout-item">
        <span>Amount Converted by Age {lifeExp}</span>
        <strong>${amountConvertedByLE.toLocaleString()}</strong>
      </div>
    </div>
  );

  const setSchedule = next => onChange({ ...scenario, conversionSchedule: next, rmdAutoCalc: false });
  const updScheduleAmount = (i, val) =>
    setSchedule(schedule.map((e, idx) => idx === i ? { ...e, amount: parseFloat(val) || 0 } : e));

  // ── TFLI Bucket (third sleeve) — per-year allocation out of each conversion ──
  const tfliOn    = !!scenario.tfliEnabled;
  const tfliSched = Array.isArray(scenario.tfliSchedule) ? scenario.tfliSchedule : [];
  const tfliAmtForAge = a => {
    const e = tfliSched.find(x => Number(x.age) === Number(a));
    return e ? (parseFloat(e.amount) || 0) : 0;
  };
  const updTfliAmount = (a, val) => {
    const amt = parseFloat(val) || 0;
    const rest = tfliSched.filter(x => Number(x.age) !== Number(a));
    const next = amt > 0 ? [...rest, { age: Number(a), amount: amt }] : rest;
    onChange({ ...scenario, tfliSchedule: next.sort((x, y) => x.age - y.age) });
  };
  const tfliTotal = tfliSched.reduce((s, e) => s + (parseFloat(e.amount) || 0), 0);
  const applyTwoTierPreset = () =>
    setSchedule(clientAge != null ? deriveConversionSchedule(scenario, clientAge) : []);
  const addScheduleYear = () => {
    const nextAge = schedule.length ? schedule[schedule.length - 1].age + 1 : (clientAge ?? 65);
    setSchedule([...schedule, { age: nextAge, amount: 0 }]);
  };
  const removeScheduleYear = i => setSchedule(schedule.filter((_, idx) => idx !== i));
  const showAgeWarning = convEndAge > 80;
  const initialIRA = (parseFloat(form?.balance401k) || 0) + (parseFloat(form?.balanceIRA) || 0);

  // ── Zoom 1 Default Scenario Optimizer (design locked Aug 6 2026) ────────────
  // The proposal is ordinary form state: it pre-fills the per-year schedule, the
  // CE target, and the engine seed source. Every field stays editable; per-year
  // binding-constraint tags render in the grid; editing a year clears its tag.
  const [optBusy, setOptBusy] = useState(false);
  const [optError, setOptError] = useState(null);
  const optMeta = scenario.optimizerMeta || null;
  const optPace = scenario.optPace || 'self_funding';
  const optBracket = parseFloat(scenario.optBracket) || 24;
  const optIrmaaTier = (scenario.optIrmaaTier === '' || scenario.optIrmaaTier == null)
    ? null : parseInt(scenario.optIrmaaTier, 10);
  const canPropose = clientAge != null && initialIRA > 0;
  const optSel = { fontSize: '12px', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', background: '#fff', color: '#1B2B5E' };
  const optChip = { display: 'inline-block', marginLeft: '6px', padding: '1px 6px', borderRadius: '9999px', fontSize: '10px', fontWeight: 700, background: '#eef2ff', color: '#1B2B5E', border: '1px solid #c7d2fe', verticalAlign: 'middle', whiteSpace: 'nowrap' };
  const optChipWarn = { ...optChip, background: '#fff7ed', color: '#9a3412', border: '1px solid #fdba74' };

  const runProposal = () => {
    if (!canPropose || optBusy) return;
    setOptBusy(true);
    setOptError(null);
    // Let the button repaint before the synchronous solve (sub-second, but real work).
    setTimeout(() => {
      try {
        const spouseAgeNow = form?.includeSpouse && form?.spouseDOB ? attainedAge(form.spouseDOB) : null;
        const res = proposeSchedule(form, scenario, { clientAge, spouseAge: spouseAgeNow }, {
          pace: optPace,
          bracketRate: optBracket / 100,
          irmaaTier: optIrmaaTier,
        });
        if (!res.ok) {
          setOptError(res.reason || 'Could not build a proposal from the current inputs.');
          setOptBusy(false);
          return;
        }
        const byAge = {};
        res.detail.forEach(d => { byAge[d.age] = d; });
        const firstAmt = (res.detail.find(d => d.amount > 0) || {}).amount || 0;
        onChange({
          ...scenario,
          conversionSchedule: res.schedule,
          ceTargetSize: res.ceTarget,
          engineSeedSource: res.engineSeedSource,
          year1TaxMethod: 'cash',
          years2PlusTaxMethod: 'jbf_distribution',
          year1Amount: res.engineSeedSource === 'nonqual' ? String(firstAmt) : scenario.year1Amount,
          year1ConversionAmount: res.engineSeedSource === 'existing_roth' ? String(firstAmt) : scenario.year1ConversionAmount,
          startYear: 1,
          endYear: res.schedule.length ? (res.schedule[res.schedule.length - 1].age - clientAge + 1) : scenario.endYear,
          rmdAutoCalc: false,
          optPace,
          optBracket: String(optBracket),
          optIrmaaTier: optIrmaaTier == null ? '' : String(optIrmaaTier),
          optimizerMeta: {
            byAge,
            summary: {
              pace: res.pace, requestedPace: res.requestedPace,
              fellBack: res.fellBack, bothIncomplete: res.bothIncomplete,
              ceTarget: res.ceTarget, peakTax: res.peakTax, distRate: res.distRate,
              engineSeedSource: res.engineSeedSource, engineSeedFromRoth: res.engineSeedFromRoth,
              costToStart: res.costToStart, totalOutOfPocket: res.totalOutOfPocket,
              totalConverted: res.totalConverted, conversionCompleteAge: res.conversionCompleteAge,
              unconvertedAtLE: res.unconvertedAtLE, ceFullyFundedAge: res.ceFullyFundedAge,
              fundingExhaustedAges: res.fundingExhaustedAges,
              bracketPct: optBracket, irmaaTier: optIrmaaTier,
              alt: res.alt,
            },
          },
        });
      } catch (e) {
        setOptError('Proposal failed: ' + ((e && e.message) || e));
      }
      setOptBusy(false);
    }, 30);
  };

  const breakEvenAge = results?.v2BreakEvenAges?.[scenario.id];
  const summary = results?.v2Summaries?.[scenario.id];
  const jbfExtras = scenario.vehicle === 'jbf' ? results?.v2JBFExtras?.[scenario.id] : null;
  // Tier-aware engine rates for this scenario (legacy scenarios without a tier: 11%/3%).
  const scenTier     = CE_TIERS[scenario.tier] || null;
  const scenDistRate = scenTier ? scenTier.planningRate : 0.11;
  const scenDistPct  = Math.round(scenDistRate * 100);

  return (
    <div className={`scenario-block${isLive ? ' scenario-block-live' : ''}`}>
      <div className="scenario-block-header">
        <span className="scenario-block-num">Scenario {index + 1}</span>
        <div className="scenario-block-actions">
          <button
            className={`scenario-live-btn${isLive ? ' scenario-live-active' : ''}`}
            onClick={onSetLive}
            title="Set as the active scenario in the annual table"
          >
            {isLive ? '● LIVE' : 'Set as LIVE'}
          </button>
          <button className="scenario-remove-btn" onClick={onRemove}>Remove</button>
        </div>
      </div>

      {showAgeWarning && (
        <div className="scenario-age-warning">
          Note: Conversion completes at age {convEndAge}. Consider accelerating the timeline.
        </div>
      )}

      <FieldRow>
        <TextField label="Scenario Name" value={scenario.name} onChange={upd('name')} placeholder="e.g. JBF Convert Engine" />
      </FieldRow>
      <FieldRow>
        <SelectField label="Investment Vehicle" value={scenario.vehicle} onChange={upd('vehicle')} options={VEHICLE_OPTIONS} />
      </FieldRow>

      {/* Per-scenario SS claiming age — makes "claim at 67" vs "claim at 70" first-class
          scenarios that each re-solve the conversion schedule. Benefit is rescaled from
          the stated basis age using FRA-based factors; delaying produces a larger check. */}
      <FieldRow>
        <AgeField label="SS Claiming Age (this scenario)" value={scenario.ssClaimingAge ?? form?.clientSSClaimingAge ?? 67} onChange={upd('ssClaimingAge')} hint="62–70; overrides the default" />
        {form?.includeSpouse && (
          <AgeField label="Spouse SS Claiming Age" value={scenario.spouseSSClaimingAge ?? form?.spouseSSClaimingAge ?? 67} onChange={upd('spouseSSClaimingAge')} hint="62–70; overrides the default" />
        )}
      </FieldRow>

      {/* ── JBF Convert Engine Fields ─────────────────────────────────────── */}
      {scenario.vehicle === 'jbf' && (
        <>
          {CE_TIERS[scenario.tier] ? (() => {
            const tier = CE_TIERS[scenario.tier];
            const pct = Math.round(tier.planningRate * 100);
            const tierUnlocked = !!(form?.riskScore?.composite && form?.zoom2Date);
            const recommended = tierUnlocked ? tierForRiskScore(form.riskScore.composite) : null;
            const recTier = recommended ? CE_TIERS[recommended] : null;
            return (
              <div className="jbf-model-banner">
                <div className="jbf-model-title">JBF Convert Engine — {tier.label} ({pct}%)</div>
                <div className="jbf-model-desc">
                  Year 1 seeds the <strong>CE Income Portfolio (Bucket A)</strong> — {tier.sub} — distributing <strong>{pct}%</strong> to
                  self-fund each year's conversion tax. Engine principal is modeled flat (coupons distribute; no growth is assumed
                  on the engine sleeve). New conversion dollars flow into the <strong>Standard Roth (Bucket B)</strong> at the
                  risk-selected ROR; at conversion completion both buckets merge and re-rate to that ROR.
                </div>
                <div className={`ce-tier-picker${tierUnlocked ? '' : ' ce-tier-locked'}`}>
                  <span className="ce-tier-picker-label">
                    {tierUnlocked ? 'Portfolio Tier' : `Portfolio Tier — provisional: ${CE_TIERS.T2.label} (${Math.round(CE_TIERS.T2.planningRate * 100)}%) until Risk Score`}
                  </span>
                  {tierUnlocked ? (
                    <>
                      <select
                        className="ce-tier-select"
                        value={scenario.tier}
                        onChange={e => onChange({
                          ...scenario,
                          tier: e.target.value,
                          tierOverridden: e.target.value !== recommended,
                        })}
                      >
                        {Object.values(CE_TIERS).map(t => (
                          <option key={t.key} value={t.key}>
                            {t.label} — {Math.round(t.planningRate * 100)}% · {t.sub}
                          </option>
                        ))}
                      </select>
                      {recTier && (
                        <span className="ce-tier-reco">
                          Risk Score {form.riskScore.composite} → recommended: <strong>{recTier.label}</strong>
                          {scenario.tier !== recommended && (
                            <button
                              className="ce-tier-apply-btn"
                              onClick={() => onChange({ ...scenario, tier: recommended, tierOverridden: false })}
                            >Apply</button>
                          )}
                          {scenario.tierOverridden && scenario.tier !== recommended && (
                            <em className="ce-tier-override-flag"> · advisor override</em>
                          )}
                        </span>
                      )}
                    </>
                  ) : (
                    <span className="ce-tier-lock-hint">
                      🔒 Unlocks after Risk Score + Zoom 2 booked
                    </span>
                  )}
                </div>
                <p className="jbf-yield-disclaimer">
                  Planning rate — not a guaranteed yield. Indicative note coupon 9.75% (SPX/NDX worst-of, memory);
                  the tier rate is set below it by design. See Important Disclosures on last page.
                </p>
              </div>
            );
          })() : (
          <div className="jbf-model-banner">
            <div className="jbf-model-title">JBF Convert Engine — Two-Bucket Conversion</div>
            <div className="jbf-model-desc">
              Year 1 seeds the <strong>JBF Income Portfolio (Bucket A)</strong> at 14% gross / 11% distribution / 3% retention.
              Years 2+ use the 11% JBF yield to self-fund each year's conversion tax — new conversion dollars flow into the <strong>Standard Roth (Bucket B)</strong> at the risk-selected ROR.
              At conversion completion, both buckets merge and re-rate to the risk-selected ROR.
            </div>
            <p className="jbf-yield-disclaimer">
              For plan design purposes only — not a guaranteed yield. See Important Disclosures on last page.
            </p>
          </div>
          )}
          {scenario.rmdAutoCalc && (
            <div className="rmd-autocalc-banner">
              Auto-calculated to convert by age {scenario.rmdStartAge} (RMD start) — edit any field below to override
            </div>
          )}
          <FieldRow>
            {(scenario.engineSeedSource ?? 'nonqual') === 'existing_roth' ? (
              <div className="engine-seed-readonly-field">
                <span className="engine-seed-readonly-label">Engine Seed (from existing Roth)</span>
                <span className="engine-seed-readonly-value">${(parseFloat(form?.balanceRothIRA) || 0).toLocaleString()}</span>
              </div>
            ) : (
              <CurrencyField label="Year 1 Amount (JBF Seed)" value={scenario.year1Amount} onChange={updManual('year1Amount')} hint="Initial JBF bucket funding" />
            )}
            <CurrencyField label="Annual Amount Years 2+" value={scenario.conversionAmount} onChange={updManual('conversionAmount')} hint="Per year into Standard Roth" />
          </FieldRow>
          {(scenario.engineSeedSource ?? 'nonqual') === 'existing_roth' && (
            <FieldRow>
              <CurrencyField
                label="Year 1 Conversion Amount"
                value={scenario.year1ConversionAmount ?? scenario.conversionAmount}
                onChange={updManual('year1ConversionAmount')}
                hint="IRA → Roth conversion in Year 1"
              />
            </FieldRow>
          )}
          <div className="engine-seed-row">
            <span className="engine-seed-label">Engine Seed Source</span>
            <label className="engine-seed-option">
              <input
                type="radio"
                name={`engineSeedSource-${scenario.id}`}
                value="nonqual"
                checked={(scenario.engineSeedSource ?? 'nonqual') === 'nonqual'}
                onChange={() => upd('engineSeedSource')('nonqual')}
              />
              New sleeve (Phase 1 from non-qual)
            </label>
            <label className="engine-seed-option">
              <input
                type="radio"
                name={`engineSeedSource-${scenario.id}`}
                value="existing_roth"
                checked={(scenario.engineSeedSource ?? 'nonqual') === 'existing_roth'}
                onChange={() => upd('engineSeedSource')('existing_roth')}
              />
              Existing Roth as engine seed
            </label>
          </div>
          {(scenario.engineSeedSource ?? 'nonqual') === 'existing_roth' && (() => {
            const rothSeed = parseFloat(form?.balanceRothIRA) || 0;
            const availYield = Math.round(rothSeed * scenDistRate);
            return (
              <div className="engine-seed-info">
                Engine seeded with <strong>${rothSeed.toLocaleString()}</strong> existing Roth.
                Year 1 distributions: ~<strong>${availYield.toLocaleString()}</strong> available for conversion tax.
                {jbfExtras?.year1TopUpFromNQ > 0 && (
                  <span className="engine-seed-warning">
                    &nbsp; Year 1 engine yield insufficient — <strong>${jbfExtras.year1TopUpFromNQ.toLocaleString()}</strong> topped up from non-qual.
                  </span>
                )}
              </div>
            );
          })()}
          <FieldRow>
            <NumberField label="Start Year" value={scenario.startYear} onChange={upd('startYear')} min={1} max={20} step={1} suffix="yr" hint="From now" />
            <NumberField label="End Year" value={scenario.endYear} onChange={updManual('endYear')} min={1} max={30} step={1} suffix="yr" />
          </FieldRow>

          {/* ── Zoom 1 Default Scenario Optimizer ─────────────────────────
             One click proposes the starting schedule + CE target from the quick-
             build variables. Self-funding ramp by default (existing Roth seeds
             the engine, January top-ups scale conversions until the CE target is
             full, then flat at the bracket top); auto-fallback to fastest-fill
             when the ramp can't finish by life expectancy. Pure pre-fill — the
             advisor sees the binding constraint on every year and can edit all
             of it before it's shown to a prospect. */}
          <div style={{ margin: '4px 0 10px', border: '1px solid #C9A84C', borderRadius: '6px', background: '#fffdf6', padding: '10px 12px' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', flexWrap: 'wrap', gap: '8px' }}>
              <span style={{ fontSize: '13px', fontWeight: 700, color: '#1B2B5E' }}>⚡ Default Scenario Optimizer</span>
              <div style={{ display: 'flex', gap: '6px', alignItems: 'center', flexWrap: 'wrap' }}>
                <select style={optSel} value={optPace} onChange={e => onChange({ ...scenario, optPace: e.target.value })}
                  title="Self-funding: each year converts only what the engine's distributions can pay the tax on — out-of-pocket collapses to the cost to start. Fastest: fill the bracket every year.">
                  <option value="self_funding">Self-funding pace</option>
                  <option value="fastest">Fastest (bracket-fill)</option>
                </select>
                <select style={optSel} value={String(optBracket)} onChange={e => onChange({ ...scenario, optBracket: e.target.value })}
                  title="Governing federal bracket ceiling for every conversion year (indexed forward exactly like the engine).">
                  <option value="22">Top of 22%</option>
                  <option value="24">Top of 24%</option>
                  <option value="32">Top of 32%</option>
                </select>
                <select style={optSel} value={scenario.optIrmaaTier ?? ''} onChange={e => onChange({ ...scenario, optIrmaaTier: e.target.value })}
                  title="Optional hard MAGI cap at an IRMAA tier bound (minus a $10K buffer), applied to conversion-year MAGI — the premium lands two years later. 'Warn only' still tags any year that crosses a tier.">
                  <option value="">IRMAA: warn only</option>
                  <option value="0">IRMAA: surcharge-free</option>
                  <option value="1">IRMAA: cap at tier 1</option>
                  <option value="2">IRMAA: cap at tier 2</option>
                </select>
                <button type="button" onClick={runProposal} disabled={!canPropose || optBusy}
                  style={{ fontSize: '12px', fontWeight: 700, color: optBusy ? '#9ca3af' : '#fff', background: optBusy ? '#e5e7eb' : '#1B2B5E', border: '1px solid #C9A84C', borderRadius: '4px', padding: '5px 12px', cursor: canPropose && !optBusy ? 'pointer' : 'not-allowed' }}>
                  {optBusy ? 'Proposing…' : 'Propose schedule'}
                </button>
              </div>
            </div>
            {!canPropose && (
              <div style={{ marginTop: '6px', fontSize: '11px', color: '#6b7280' }}>Enter client DOB and an IRA / 401k balance to enable the optimizer.</div>
            )}
            {optError && (
              <div style={{ marginTop: '6px', fontSize: '12px', color: '#b91c1c' }}>{optError}</div>
            )}
            {optMeta && optMeta.summary && (() => { const os = optMeta.summary; return (
              <div style={{ marginTop: '8px', fontSize: '12px', color: '#1B2B5E', lineHeight: 1.55 }}>
                {os.fellBack && (
                  <div style={{ color: '#9a3412', background: '#fff7ed', borderLeft: '3px solid #d97706', padding: '6px 10px', borderRadius: '4px', marginBottom: '6px' }}>
                    Self-funding pace can't finish converting by life expectancy{os.alt && os.alt.pace === 'self_funding' && os.alt.unconvertedAtLE > 0 ? <> ({formatDollarFull(os.alt.unconvertedAtLE)} would be stranded)</> : null} — auto-switched to <strong>Fastest</strong>. Flip the pace selector and re-propose to override.
                  </div>
                )}
                {os.bothIncomplete && (
                  <div style={{ color: '#9a3412', background: '#fff7ed', borderLeft: '3px solid #d97706', padding: '6px 10px', borderRadius: '4px', marginBottom: '6px' }}>
                    ⚠ Neither pace finishes by life expectancy at this ceiling — {formatDollarFull(os.unconvertedAtLE)} remains unconverted. Consider a higher bracket ceiling.
                  </div>
                )}
                <div>
                  <strong>{os.pace === 'self_funding' ? 'Self-funding pace' : 'Fastest (bracket-fill)'}</strong>
                  {' · fill to top of '}{os.bracketPct}%{os.irmaaTier != null ? ` · MAGI capped at IRMAA tier ${os.irmaaTier}` : ''}
                  {' · CE target '}<strong>{formatDollarFull(os.ceTarget)}</strong> (peak tax {formatDollarFull(os.peakTax)} ÷ {Math.round(os.distRate * 100)}%)
                  {os.engineSeedSource === 'existing_roth' ? <> · seeded with <strong>{formatDollarFull(os.engineSeedFromRoth)}</strong> existing Roth</> : null}
                </div>
                <div>
                  Cost to start <strong>{formatDollarFull(os.costToStart)}</strong>
                  {' · total out-of-pocket '}<strong>{formatDollarFull(os.totalOutOfPocket)}</strong>
                  {' · converts '}<strong>{formatDollarFull(os.totalConverted)}</strong>
                  {os.conversionCompleteAge != null
                    ? <> · IRA fully converted at age <strong>{os.conversionCompleteAge}</strong></>
                    : <> · <span style={{ color: '#b45309' }}>{formatDollarFull(os.unconvertedAtLE)} unconverted at life expectancy</span></>}
                  {os.ceFullyFundedAge != null ? <> · engine full at age {os.ceFullyFundedAge}</> : null}
                </div>
                {Array.isArray(os.fundingExhaustedAges) && os.fundingExhaustedAges.length > 0 && (
                  <div style={{ color: '#b91c1c', marginTop: '4px' }}>
                    ⚠ Funding exhausted at age{os.fundingExhaustedAges.length > 1 ? 's' : ''} {os.fundingExhaustedAges.join(', ')} — outside cash can't cover expenses + conversion tax there. Review before presenting.
                  </div>
                )}
                <div style={{ color: '#6b7280', marginTop: '2px' }}>Every year below is editable — editing a year clears its tag. Re-propose any time.</div>
              </div>
            ); })()}
          </div>

          {/* ── Flexible per-year conversion schedule ─────────────────────────
             The two-tier fields above (Year 1 / Annual / Start / End) act as a
             PRESET. "Apply preset" expands them into the editable grid below,
             which is the source of truth the projection reads. Edit any year to
             recompute the whole projection downstream. */}
          <div style={{ margin: '4px 0 10px', border: '1px solid #e2e6ef', borderRadius: '6px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8f9fc', padding: '8px 12px', borderBottom: '1px solid #e2e6ef' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#1B2B5E' }}>Conversion Schedule — per year</span>
              <button
                type="button"
                onClick={applyTwoTierPreset}
                style={{ fontSize: '12px', fontWeight: 600, color: '#1B2B5E', background: '#fff', border: '1px solid #C9A84C', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer' }}
              >
                Apply two-tier preset →
              </button>
            </div>
            {clientAge == null ? (
              <div style={{ padding: '10px 12px' }}>{scheduleReadout}</div>
            ) : (
              <div style={{ padding: '8px 12px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#6b7280', fontSize: '11px', textTransform: 'uppercase' }}>
                      <th style={{ padding: '4px 6px', width: tfliOn ? '24%' : '30%' }}>Age</th>
                      <th style={{ padding: '4px 6px' }}>Conversion $</th>
                      {tfliOn && <th style={{ padding: '4px 6px' }}>→ TFLI $</th>}
                      <th style={{ padding: '4px 6px', width: '36px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.map((entry, i) => (
                      <tr key={`${entry.age}-${i}`}>
                        <td style={{ padding: '3px 6px', fontWeight: 600, color: '#1B2B5E' }}>
                          {entry.age}
                          {(() => {
                            const m = optMeta && optMeta.byAge ? optMeta.byAge[entry.age] : null;
                            if (!m || Number(m.amount) !== Number(entry.amount)) return null;
                            const bl = boundLabel(m.boundBy, { bracketRate: ((optMeta.summary && optMeta.summary.bracketPct) || 24) / 100 });
                            const warnIrmaa = (m.irmaaTierHit || 0) > 0 && (!optMeta.summary || optMeta.summary.irmaaTier == null);
                            const tip = `Taxable $${Math.round(m.taxableIncome || 0).toLocaleString()} of $${Math.round(m.ceiling || 0).toLocaleString()} ceiling · conv tax $${Math.round(m.conversionTaxCost || 0).toLocaleString()} · CE pays $${Math.round(m.cePays || 0).toLocaleString()} · you pay $${Math.round(m.youPay || 0).toLocaleString()}${(m.rmd || 0) > 0 ? ` · RMD $${Math.round(m.rmd).toLocaleString()}` : ''}`;
                            return (
                              <>
                                <span style={optChip} title={tip}>{m.seedYear ? 'Seed · ' : ''}{bl}</span>
                                {warnIrmaa && <span style={optChipWarn} title="This year's MAGI crosses an IRMAA tier — the surcharge lands two years later.">IRMAA T{m.irmaaTierHit}</span>}
                              </>
                            );
                          })()}
                        </td>
                        <td style={{ padding: '3px 6px' }}>
                          <input
                            type="number" step="1000" min="0"
                            value={entry.amount}
                            onChange={e => updScheduleAmount(i, e.target.value)}
                            style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
                          />
                        </td>
                        {tfliOn && (
                          <td style={{ padding: '3px 6px' }}>
                            <input
                              type="number" step="1000" min="0"
                              value={tfliAmtForAge(entry.age) || ''}
                              placeholder="0"
                              onChange={e => updTfliAmount(entry.age, e.target.value)}
                              title="Portion of this year's conversion routed to the TFLI bucket"
                              style={{ width: '100%', padding: '4px 6px', border: '1px solid #C9A84C', borderRadius: '4px', fontSize: '13px', background: '#fffdf5' }}
                            />
                          </td>
                        )}
                        <td style={{ padding: '3px 6px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => removeScheduleYear(i)}
                            title="Remove year"
                            style={{ border: 'none', background: 'none', color: '#b91c1c', cursor: 'pointer', fontSize: '16px', lineHeight: 1 }}
                          >×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '1px solid #e2e6ef' }}>
                      <td style={{ padding: '5px 6px', fontWeight: 600 }}>Total</td>
                      <td style={{ padding: '5px 6px', fontWeight: 700, color: '#1B2B5E' }}>${Math.round(scheduleTotal).toLocaleString()}</td>
                      {tfliOn && <td style={{ padding: '5px 6px', fontWeight: 700, color: '#8a6d1f' }}>${Math.round(tfliTotal).toLocaleString()}</td>}
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
                <button
                  type="button"
                  onClick={addScheduleYear}
                  style={{ marginTop: '8px', fontSize: '12px', fontWeight: 600, color: '#1B2B5E', background: '#fff', border: '1px dashed #9ca3af', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer' }}
                >
                  ＋ Add year
                </button>
              </div>
            )}
          </div>
          {clientAge != null && scheduleReadout}

          {/* ── TFLI Bucket (third sleeve) ─────────────────────────────────────
             Routes part of each year's conversion into a TFLI income portfolio
             (e.g. SN sleeve) inside the Roth. Pays the yield below as TAX-FREE
             income into cash-flow coverage — flat principal, for life. */}
          <details open={tfliOn} style={{ margin: '8px 0', border: '1px solid #e2e6f0', borderRadius: '6px', padding: '10px 12px', background: '#fbfcff' }}>
            <summary style={{ cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#1B2B5E' }}>
              TFLI Bucket — Tax-Free Lifetime Income sleeve
            </summary>
            <div style={{ marginTop: '10px' }}>
              <FieldRow>
                <label className="toggle-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                  <input
                    type="checkbox"
                    className="toggle-checkbox"
                    checked={tfliOn}
                    onChange={e => upd('tfliEnabled')(e.target.checked)}
                  />
                  <span className="toggle-track"><span className="toggle-thumb" /></span>
                  Enable TFLI bucket (adds a “→ TFLI $” column to the schedule grid)
                </label>
                <NumberField
                  label="TFLI Income Yield"
                  value={scenario.tfliYield ?? 8}
                  onChange={upd('tfliYield')}
                  min={6} max={10} step={0.25} suffix="%"
                  hint="Distribution rate paid as tax-free income (typical SN sleeve: 6–10%)."
                />
              </FieldRow>
              {tfliOn && (
                <div style={{ fontSize: '12px', color: '#1B2B5E', background: '#f8f9fc', borderLeft: '3px solid #C9A84C', padding: '8px 12px', borderRadius: '4px', margin: '4px 0 4px' }}>
                  TFLI total: <strong>${Math.round(tfliTotal).toLocaleString()}</strong> routed from conversions.
                  The bucket pays <strong>{parseFloat(scenario.tfliYield ?? 8)}%</strong> of its balance each year as tax-free
                  income toward expenses (surplus spills to Standard Roth). Principal stays flat and pays for life —
                  it never merges at end of conversions. TFLI dollars do NOT fill the CE target, so size the CE
                  target from the conversion remainder.
                </div>
              )}
            </div>
          </details>
          <FieldRow>
            <CurrencyField
              label="CE Target Size"
              value={scenario.ceTargetSize}
              onChange={upd('ceTargetSize')}
              hint="Engine fills to this, then conversions spill to Standard Roth. Blank = Year-1 seed only."
            />
            <NumberField
              label="Year-1 CE months (dividends)"
              value={scenario.year1CEMonths ?? 12}
              onChange={upd('year1CEMonths')}
              min={1} max={12} step={1} suffix="mo"
              hint="Months the CE pays dividends in Year 1 (drives cost-to-start)."
            />
            <NumberField
              label="LTFI Yield"
              value={scenario.ltfiYield ?? 6}
              onChange={upd('ltfiYield')}
              min={4} max={9} step={0.5} suffix="%"
              hint="Tax-Free Lifetime Income rate."
            />
            <NumberField
              label="Unconverted IRA Growth"
              value={scenario.unconvertedIraGrowthRate ?? (parseFloat(form?.growthRate) || 7)}
              onChange={upd('unconvertedIraGrowthRate')}
              min={0} max={12} step={0.25} suffix="%"
              hint="Growth on the traditional IRA still to be converted. Default = portfolio ROR."
            />
          </FieldRow>
          {jbfExtras?.ceTargetCoversPeak === false && (
            <div style={{ fontSize: '12px', color: '#7a4a00', background: '#fff7ed', borderLeft: '3px solid #d97706', padding: '8px 12px', borderRadius: '4px', margin: '0 0 8px' }}>
              ⚠ <strong>CE Target may underfund the peak tax year.</strong> The largest annual
              conversion tax is <strong>${Math.round(jbfExtras.peakConvTaxCost || 0).toLocaleString()}</strong>,
              which needs a target of ~<strong>${Math.round(jbfExtras.ceMinTargetForPeak || 0).toLocaleString()}</strong> to
              cover it from the {scenDistPct}% distribution — the current target is
              <strong> ${Math.round(jbfExtras.cePeakTargetShortfall || 0).toLocaleString()}</strong> short.
              {scenTier
                ? 'The tiered engine is modeled flat, so consider sizing the target up.'
                : "The engine's retained 3% growth may still catch up, but consider sizing the target up."}
            </div>
          )}
          <div style={{ fontSize: '12px', color: '#1B2B5E', background: '#f8f9fc', borderLeft: '3px solid #C9A84C', padding: '8px 12px', borderRadius: '4px', margin: '0 0 8px' }}>
            {jbfExtras?.ceFundedYear
              ? <>CE fully funded at: <strong>${(parseFloat(scenario.ceTargetSize) || 0).toLocaleString()}</strong> after {jbfExtras.ceFundedYear} year{jbfExtras.ceFundedYear !== 1 ? 's' : ''} (age {jbfExtras.ceFundedAge})</>
              : (parseFloat(scenario.ceTargetSize) > 0
                  ? <>Engine fills toward <strong>${(parseFloat(scenario.ceTargetSize)).toLocaleString()}</strong> — run Calculate to confirm the fill year.</>
                  : <>No target set — Year 1 seeds the engine, all later conversions go to Standard Roth.</>)}
          </div>
          <FieldRow>
            <SelectField
              label="Risk Tier (Roth ROR)"
              value={scenario.riskTier ?? 'M'}
              onChange={upd('riskTier')}
              options={[
                { value: 'L', label: 'Low — 6% return' },
                { value: 'M', label: 'Medium — 7% return' },
                { value: 'H', label: 'High — 9% return' },
              ]}
            />
            <SelectField
              label="Year 1 Tax Method"
              value={scenario.year1TaxMethod ?? 'cash'}
              onChange={upd('year1TaxMethod')}
              options={[
                { value: 'cash',             label: 'Cash Account — pay from outside savings' },
                { value: 'internal',         label: 'Internal — withhold from conversion amount' },
                { value: 'jbf_distribution', label: 'JBF CE Distribution — yield pays tax' },
              ]}
            />
            <SelectField
              label="Years 2+ Tax Method"
              value={scenario.years2PlusTaxMethod ?? 'jbf_distribution'}
              onChange={upd('years2PlusTaxMethod')}
              options={[
                { value: 'jbf_distribution', label: 'JBF CE Distribution — yield pays tax' },
                { value: 'cash',             label: 'Cash Account — pay from outside savings' },
                { value: 'internal',         label: 'Internal — withhold from conversion amount' },
              ]}
            />
          </FieldRow>
          <FieldRow>
            <label className="toggle-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
              <input
                type="checkbox"
                className="toggle-checkbox"
                checked={!!scenario.includeRothContribInJBF}
                onChange={e => upd('includeRothContribInJBF')(e.target.checked)}
              />
              Include Roth 401k contributions in JBF bucket during conversion
              <span style={{ color: '#8aa', fontSize: '11px' }}>(boosts JBF yield available to self-fund conversion tax)</span>
            </label>
          </FieldRow>
          <div className="jbf-model-rates">
            {scenTier
              ? <>CE Bucket ({scenTier.label}): {scenDistPct}% distribution · flat principal &nbsp;|&nbsp;</>
              : <>JBF Bucket: 14% gross · 11% distribution · 3% retention &nbsp;|&nbsp;</>}
            Standard Roth: {parseFloat((RISK_TIERS[scenario.riskTier ?? 'M'] * 100).toFixed(2))}% ROR &nbsp;|&nbsp;
            Post-conversion: both at {parseFloat((RISK_TIERS[scenario.riskTier ?? 'M'] * 100).toFixed(2))}% (net)
          </div>

          {/* ── SN Maturity Payoff (Regime B) — only when the freeze is active ──── */}
          {scenario.unconvertedIraGrowthRate != null && scenario.unconvertedIraGrowthRate !== '' && (() => {
            const snBaseRate = parseFloat(scenario.snBaseRate ?? 7) || 0;
            const snTenor = parseFloat(scenario.snTenor ?? 7) || 0;
            const credit = snCreditMultiple({ baseRate: snBaseRate / 100, tenorYears: snTenor });
            return (
              <details className="sn-payoff-block" open={!!scenario.snPayoff} style={{ margin: '8px 0', border: '1px solid #e2e6f0', borderRadius: '6px', padding: '10px 12px', background: '#fbfcff' }}>
                <summary style={{ cursor: 'pointer', fontSize: '13px', fontWeight: 600, color: '#1B2B5E' }}>
                  SN Maturity Payoff (Regime B)
                </summary>
                <div style={{ marginTop: '10px' }}>
                  <FieldRow>
                    <label className="toggle-label" style={{ display: 'flex', alignItems: 'center', gap: '8px', fontSize: '13px' }}>
                      <input
                        type="checkbox"
                        className="toggle-checkbox"
                        checked={!!scenario.snPayoff}
                        onChange={e => upd('snPayoff')(e.target.checked)}
                      />
                      <span className="toggle-track"><span className="toggle-thumb" /></span>
                      Model SN maturity payoff (Regime B)
                    </label>
                  </FieldRow>
                  <FieldRow>
                    <NumberField
                      label="Note credit rate % (mirror plan ROR)"
                      value={scenario.snBaseRate ?? 7}
                      onChange={upd('snBaseRate')}
                      min={0} max={12} step={0.25} suffix="%"
                    />
                    <NumberField
                      label="Note tenor (yrs)"
                      value={scenario.snTenor ?? 7}
                      onChange={upd('snTenor')}
                      min={1} max={15} step={1} suffix="yr"
                    />
                  </FieldRow>
                  <FieldRow>
                    <NumberField
                      label="Years conversion → maturity"
                      value={scenario.snMatLag ?? 2}
                      onChange={upd('snMatLag')}
                      min={0} max={15} step={1} suffix="yr"
                    />
                    <NumberField
                      label="First N conversion years at market (no note)"
                      value={scenario.snNoteStartOffset ?? 2}
                      onChange={upd('snNoteStartOffset')}
                      min={0} max={20} step={1} suffix="yr"
                    />
                  </FieldRow>
                  <div style={{ fontSize: '12px', color: '#1B2B5E', background: '#f8f9fc', borderLeft: '3px solid #C9A84C', padding: '8px 12px', borderRadius: '4px', margin: '4px 0 8px' }}>
                    Each note credits ≈ {(credit * 100).toFixed(1)}% at maturity ({(1 + credit).toFixed(2)}x converted value).
                  </div>
                  <div style={{ fontSize: '11px', color: '#8a94a8', fontStyle: 'italic', lineHeight: 1.4 }}>
                    SN maturity values are directional planning illustrations — the note is held at fair-market value during the freeze, then credited the plan's base return compounded over the note term. Not a projection of any security; actual results depend on index performance. For planning discussion only.
                  </div>
                </div>
              </details>
            );
          })()}
        </>
      )}

      {/* ── Non-JBF (Accelerated Conversion) fields ─────────────────── */}
      {scenario.vehicle !== 'jbf' && (
        <>
          <FieldRow>
            <CurrencyField label="Annual Conversion Amount" value={scenario.conversionAmount} onChange={upd('conversionAmount')} hint="Per year" />
            <NumberField label="Start Year" value={scenario.startYear} onChange={upd('startYear')} min={0} max={30} step={1} suffix="yr" hint="From now" />
            <NumberField label="End Year" value={scenario.endYear} onChange={upd('endYear')} min={0} max={30} step={1} suffix="yr" />
          </FieldRow>
          <FieldRow>
            <SelectField label="Tax Payment Method" value={scenario.taxMethod} onChange={upd('taxMethod')} options={TAX_METHOD_OPTIONS} />
          </FieldRow>
          {scenario.vehicle === 'proposed' && (
            <FieldRow>
              <NumberField label="Custom Rate of Return" value={scenario.ror} onChange={upd('ror')} min={0} max={30} step={0.1} suffix="%" />
            </FieldRow>
          )}

          {/* ── Flexible per-year conversion schedule (Accelerated Conversion) ──
             Mirrors the JBF Convert Engine grid. "Annual Conversion Amount" +
             Start/End Year above act as a PRESET. "Apply preset" expands them into
             this editable grid, which becomes the source of truth the projection
             reads — edit any year to size that year's conversion independently. */}
          <div style={{ margin: '4px 0 10px', border: '1px solid #e2e6ef', borderRadius: '6px', overflow: 'hidden' }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', background: '#f8f9fc', padding: '8px 12px', borderBottom: '1px solid #e2e6ef' }}>
              <span style={{ fontSize: '13px', fontWeight: 600, color: '#1B2B5E' }}>Conversion Schedule — custom per year</span>
              <button
                type="button"
                onClick={applyTwoTierPreset}
                style={{ fontSize: '12px', fontWeight: 600, color: '#1B2B5E', background: '#fff', border: '1px solid #C9A84C', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer' }}
              >
                Apply annual amount to each year →
              </button>
            </div>
            {clientAge == null ? (
              <div style={{ padding: '10px 12px' }}>{scheduleReadout}</div>
            ) : (
              <div style={{ padding: '8px 12px' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
                  <thead>
                    <tr style={{ textAlign: 'left', color: '#6b7280', fontSize: '11px', textTransform: 'uppercase' }}>
                      <th style={{ padding: '4px 6px', width: '30%' }}>Age</th>
                      <th style={{ padding: '4px 6px' }}>Conversion $</th>
                      <th style={{ padding: '4px 6px', width: '36px' }}></th>
                    </tr>
                  </thead>
                  <tbody>
                    {schedule.map((entry, i) => (
                      <tr key={`${entry.age}-${i}`}>
                        <td style={{ padding: '3px 6px', fontWeight: 600, color: '#1B2B5E' }}>{entry.age}</td>
                        <td style={{ padding: '3px 6px' }}>
                          <input
                            type="number" step="1000" min="0"
                            value={entry.amount}
                            onChange={e => updScheduleAmount(i, e.target.value)}
                            style={{ width: '100%', padding: '4px 6px', border: '1px solid #d1d5db', borderRadius: '4px', fontSize: '13px' }}
                          />
                        </td>
                        <td style={{ padding: '3px 6px', textAlign: 'center' }}>
                          <button
                            type="button"
                            onClick={() => removeScheduleYear(i)}
                            title="Remove year"
                            style={{ border: 'none', background: 'none', color: '#b91c1c', cursor: 'pointer', fontSize: '16px', lineHeight: 1 }}
                          >×</button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                  <tfoot>
                    <tr style={{ borderTop: '1px solid #e2e6ef' }}>
                      <td style={{ padding: '5px 6px', fontWeight: 600 }}>Total</td>
                      <td style={{ padding: '5px 6px', fontWeight: 700, color: '#1B2B5E' }}>${Math.round(scheduleTotal).toLocaleString()}</td>
                      <td></td>
                    </tr>
                  </tfoot>
                </table>
                <button
                  type="button"
                  onClick={addScheduleYear}
                  style={{ marginTop: '8px', fontSize: '12px', fontWeight: 600, color: '#1B2B5E', background: '#fff', border: '1px dashed #9ca3af', borderRadius: '4px', padding: '4px 10px', cursor: 'pointer' }}
                >
                  ＋ Add year
                </button>
              </div>
            )}
          </div>
          {clientAge != null && scheduleReadout}
        </>
      )}

      {/* ── QCD (per-scenario) ─────────────────────────────────────── */}
      <div style={{ marginTop: '14px', border: '1px solid #d1d5db', borderRadius: '6px', padding: '12px 14px', background: '#fcfcfd' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#1B2B5E', marginBottom: '8px' }}>Qualified Charitable Distributions (QCD)</div>
        <FieldRow>
          <CurrencyField label="Annual QCD Amount" value={scenario.qcdAnnual ?? ''} onChange={upd('qcdAnnual')} hint="IRA → charity; excluded from income" />
          <NumberField label="QCD Start Age" value={scenario.qcdStartAge ?? ''} onChange={upd('qcdStartAge')} min={70} max={95} step={1} suffix="age" hint="Blank = RMD age" />
        </FieldRow>
        <div style={{ marginTop: '8px' }}>
          <label className="toggle-label">
            <input
              type="checkbox"
              className="toggle-checkbox"
              checked={!!scenario.qcdOffsetRmd}
              onChange={e => upd('qcdOffsetRmd')(e.target.checked)}
            />
            <span className="toggle-track"><span className="toggle-thumb" /></span>
            Auto-offset residual RMDs (QCD each year = that year&apos;s IRA RMD, once RMDs start)
          </label>
        </div>
      </div>

      <div style={{ marginTop: '14px', border: '1px solid #d1d5db', borderRadius: '6px', padding: '12px 14px', background: '#fcfcfd' }}>
        <div style={{ fontSize: '13px', fontWeight: 600, color: '#1B2B5E', marginBottom: '8px' }}>Client Goals &amp; Notes</div>
        <textarea
          value={scenario.clientGoals ?? ''}
          onChange={e => upd('clientGoals')(e.target.value)}
          placeholder="e.g. Leave $1,000,000 to each of 3 children tax-free by age 90. Eliminate all RMDs. Paula wants simplicity."
          rows={4}
          style={{ width: '100%', resize: 'vertical', fontSize: '13px', fontFamily: 'inherit', padding: '8px 10px', border: '1px solid #d1d5db', borderRadius: '4px', boxSizing: 'border-box' }}
        />
      </div>
      {/* The goal-framed CE dashboard now lives ONCE on the Scenario Comparison table
          (AnnualTableSection). Non-JBF scenarios keep the plain strip below. */}
      {summary && !jbfExtras && (
        <div className="scenario-summary-strip">
          <div className="scenario-summary-item">
            <span>Total Converted</span>
            <strong>${Math.round(totalConverted).toLocaleString()}</strong>
          </div>
          <div className="scenario-summary-item">
            <span>Legacy Delta vs Baseline</span>
            <strong className={summary.legacyDelta >= 0 ? 'delta-pos' : 'delta-neg'}>
              {summary.legacyDelta >= 0 ? '+' : ''}${Math.round(summary.legacyDelta).toLocaleString()}
            </strong>
          </div>
          <div className="scenario-summary-item">
            <span>Tax Savings vs Baseline</span>
            <strong className={summary.totalTaxDelta >= 0 ? 'delta-pos' : 'delta-neg'}>
              {summary.totalTaxDelta >= 0 ? '+' : ''}${Math.round(summary.totalTaxDelta).toLocaleString()}
            </strong>
          </div>
          <div className="scenario-summary-item">
            <span>Break-Even Age</span>
            <strong>{breakEvenAge != null ? `Age ${breakEvenAge}` : 'Not by age 90'}</strong>
          </div>
        </div>
      )}

      {/* Compute detail table — calculation verification */}
      {results?.v2ScenarioTables?.[scenario.id] && (
        <ComputeDetailTable
          scenario={scenario}
          results={results}
          initialIRA={initialIRA}
          form={form}
        />
      )}
    </div>
  );
}

function ConversionGoalSelector({ form, set }) {
  const fullRothTarget = num(form.balance401k) + num(form.balanceIRA);

  let target = null;
  if (form.conversionGoal === 'full_roth') {
    target = fullRothTarget > 0 ? fullRothTarget : null;
  } else {
    target = num(form.customTarget) || null;
  }

  return (
    <div className="conv-goal-selector">
      <div className="conv-goal-title">Conversion Goal</div>
      <div className="conv-goal-options">
        <label className={`conv-goal-option${form.conversionGoal === 'full_roth' ? ' conv-goal-selected' : ''}`}>
          <input type="radio" name="conversionGoal" value="full_roth"
            checked={form.conversionGoal === 'full_roth'}
            onChange={() => set('conversionGoal')('full_roth')} />
          <span className="conv-goal-opt-label">
            Full Roth Conversion
            {form.conversionGoal === 'full_roth' && fullRothTarget > 0 && (
              <span className="conv-goal-inline-target"> — {formatDollarFull(fullRothTarget)}</span>
            )}
          </span>
        </label>
        <label className={`conv-goal-option${form.conversionGoal === 'custom' ? ' conv-goal-selected' : ''}`}>
          <input type="radio" name="conversionGoal" value="custom"
            checked={form.conversionGoal === 'custom'}
            onChange={() => set('conversionGoal')('custom')} />
          <span className="conv-goal-opt-label">Custom Target</span>
        </label>
      </div>
      {form.conversionGoal === 'custom' && (
        <div className="conv-goal-custom-row">
          <div className="field-adornment-wrap">
            <span className="field-adornment">$</span>
            <input className="field-input adornment-left" type="number" min="0" step="1000"
              value={form.customTarget}
              onChange={e => set('customTarget')(e.target.value)}
              placeholder="0" />
          </div>
        </div>
      )}
      {target != null && target > 0 && (
        <div className="conv-goal-active-target">
          Conversion Target: <span className="conv-goal-amount">{formatDollarFull(target)}</span>
        </div>
      )}
    </div>
  );
}

function SectionCard({ title, children }) {
  return (
    <div className="section-card">
      <div className="section-header">
        <h2 className="section-title">{title}</h2>
      </div>
      <div className="section-body">{children}</div>
    </div>
  );
}

function FieldRow({ children }) {
  return <div className="field-row">{children}</div>;
}

// ---- RESULTS COMPONENTS ----

function ResultsSection({ results }) {
  if (!results) return null;
  if (results.error) {
    return (
      <div className="results-wrapper">
        <div className="results-error">{results.error}</div>
      </div>
    );
  }

  return (
    <div className="results-wrapper">
      <div className="results-inner">
        {/* Print Plan button */}
        <div className="results-print-row">
          <button className="print-plan-btn" onClick={() => window.print()}>
            Print Plan
          </button>
        </div>
      </div>
    </div>
  );
}

// ---- ANNUAL TABLE SECTION ----

function fmtD(v) {
  if (v == null) return '—';
  return '$' + Math.round(v).toLocaleString();
}

function fmtDM(v) {
  if (v == null) return '—';
  const abs = Math.abs(v);
  if (abs >= 1_000_000) return '$' + (v / 1_000_000).toFixed(2) + 'M';
  return '$' + Math.round(v).toLocaleString();
}

async function exportToExcelStyled(form, results, printScenarioIds = [], liveScenarioId = null) {
  const ExcelJS = (await import('exceljs')).default;

  const NAVY  = 'FF1B2B5E';
  const GOLD  = 'FFC9A84C';
  const ALT   = 'FFEEF2FA';
  const WHITE = 'FFFFFFFF';
  const GREEN = 'FF1B5E20';
  const SOFT_BLUE = 'FFDDEEFF';

  const thinBorder = { style: 'thin', color: { argb: 'FFD1D5DB' } };
  const cellBorder = { top: thinBorder, left: thinBorder, bottom: thinBorder, right: thinBorder };

  // Scenarios to export: those checked in "Include in Print", kept in form order.
  // Fall back to the primary JBF scenario if nothing is checked.
  const checkedSet = new Set(printScenarioIds || []);
  let scenariosToExport = form.rothScenarios.filter(s => checkedSet.has(s.id));
  if (scenariosToExport.length === 0) {
    const fallback = form.rothScenarios.find(s => s.vehicle === 'jbf') || form.rothScenarios[0];
    scenariosToExport = fallback ? [fallback] : [];
  }

  // The Summary sheet is anchored to the scenario the user set LIVE, so the band,
  // comparison table, and header label all describe the same scenario. If nothing is
  // LIVE (liveScenarioId null / baseline), fall back to the first exported scenario.
  const primaryScenario =
    form.rothScenarios.find(s => s.id === liveScenarioId) || scenariosToExport[0] || null;
  const ceTable     = primaryScenario ? results.v2ScenarioTables?.[primaryScenario.id] : null;
  const summary     = primaryScenario ? results.v2Summaries?.[primaryScenario.id] : null;
  const jbfExtras   = primaryScenario ? results.v2JBFExtras?.[primaryScenario.id] : null;
  const breakEvenAge = primaryScenario ? results.v2BreakEvenAges?.[primaryScenario.id] : null;
  const baselineTable = results.v2BaselineTable || [];

  // Goal lens — same as the dashboard/PrintPlan. Income lens leads with Tax-Free
  // Lifetime Income = end-of-conversions Roth × ltfiYield (same basis everywhere).
  const goalLean = form?.goalLean === 'ltfi' ? 'ltfi' : 'legacy';
  const ltfiRate = (parseFloat(primaryScenario?.ltfiYield) || 6) / 100;
  const endConvRoth = jbfExtras?.conversionCompleteRow?.rothBalance
    ?? (ceTable ? [...ceTable].reverse().find(r => (r.conversionAmount || 0) > 0)?.rothBalance : 0) ?? 0;
  const ltfiIncome = Math.round(endConvRoth * ltfiRate);
  const baseLTFI = Math.round((baselineTable.at(-1)?.rothBalance || 0) * ltfiRate);

  const clientFull = [form.clientFirstName, form.clientLastName].filter(Boolean).join(' ');
  const spouseFull = form.includeSpouse
    ? [form.spouseFirstName, form.spouseLastName].filter(Boolean).join(' ')
    : '';
  const namesLine = spouseFull ? `${clientFull} & ${spouseFull}` : (clientFull || 'Client');
  const dateStr = new Date().toLocaleDateString('en-US', { month: 'long', day: 'numeric', year: 'numeric' });
  const subtitle = `${namesLine}  |  Prepared by Jesse Bradin  |  ${dateStr}`;

  const wb = new ExcelJS.Workbook();
  wb.creator = 'J Bradin Financial';
  wb.created = new Date();

  // ───── SHEET 1: SUMMARY ─────
  const s1 = wb.addWorksheet('Summary', {
    properties: { tabColor: { argb: GOLD } },
    views: [{ showGridLines: false }],
  });
  s1.columns = [
    { width: 38 }, { width: 18 }, { width: 18 }, { width: 18 },
  ];

  // Row 1: title bar
  s1.mergeCells('A1:D1');
  const r1 = s1.getCell('A1');
  r1.value = 'J Bradin Financial — Convert Engine Plan';
  r1.font = { name: 'Arial', size: 14, bold: true, color: { argb: WHITE } };
  r1.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
  r1.alignment = { horizontal: 'center', vertical: 'middle' };
  s1.getRow(1).height = 26;

  // Row 2: subtitle bar
  s1.mergeCells('A2:D2');
  const r2 = s1.getCell('A2');
  r2.value = subtitle;
  r2.font = { name: 'Arial', size: 10, italic: true, color: { argb: WHITE } };
  r2.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
  r2.alignment = { horizontal: 'center', vertical: 'middle' };
  s1.getRow(2).height = 20;

  // Row 3: spacer
  s1.getRow(3).height = 8;

  // Rows 4-6: three metric cards (stacked, each spans A:D)
  const cards = [
    goalLean === 'ltfi'
      ? {
          label: 'TAX-FREE INCOME FOR LIFE',
          value: ltfiIncome,
          sign: 'pos',
          suffix: '/yr',
          color: GOLD,
        }
      : {
          label: 'LEGACY ADVANTAGE',
          value: summary ? summary.legacyDelta : 0,
          sign: 'pos', // positive number = improvement; format with leading +
          color: GOLD,
        },
    {
      label: 'LIFETIME TAX SAVINGS',
      value: summary ? summary.totalTaxDelta : 0,
      sign: 'advantage', // baseline − plan; positive = savings (same convention as the table)
      color: GREEN,
    },
    {
      label: 'RMDs ELIMINATED',
      value: summary ? summary.totalRMDDelta : 0,
      sign: 'advantage', // baseline − plan; positive = RMDs eliminated
      color: GREEN,
    },
  ];
  cards.forEach((c, i) => {
    const rowNum = 4 + i;
    s1.mergeCells(`A${rowNum}:B${rowNum}`);
    s1.mergeCells(`C${rowNum}:D${rowNum}`);
    const lbl = s1.getCell(`A${rowNum}`);
    const val = s1.getCell(`C${rowNum}`);
    lbl.value = c.label;
    lbl.font = { name: 'Arial', size: 11, bold: true, color: { argb: WHITE } };
    lbl.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
    lbl.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    const abs = Math.abs(c.value || 0);
    const fmt = abs >= 1_000_000
      ? '$' + (abs / 1_000_000).toFixed(2) + 'M'
      : '$' + Math.round(abs).toLocaleString();
    // 'advantage' derives the sign from the value (positive = beats baseline), matching
    // the table below; 'pos'/'neg' force a fixed prefix.
    const prefix = c.sign === 'advantage'
      ? (c.value > 0 ? '+' : c.value < 0 ? '-' : '')
      : (c.sign === 'pos' ? '+' : '-');
    val.value = prefix + fmt + (c.suffix || '');
    val.font = { name: 'Arial', size: 18, bold: true, color: { argb: c.color } };
    val.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT } };
    val.alignment = { horizontal: 'right', vertical: 'middle', indent: 1 };
    s1.getRow(rowNum).height = 30;
  });

  // Row 7: spacer
  s1.getRow(7).height = 8;
  // Row 8: spacer
  s1.getRow(8).height = 8;

  // Row 9: metrics table header
  const compareLabel = primaryScenario?.name || 'Convert Engine';
  const headers9 = ['Metric', 'Baseline', compareLabel, 'Advantage'];
  headers9.forEach((h, i) => {
    const c = s1.getRow(9).getCell(i + 1);
    c.value = h;
    c.font = { name: 'Arial', size: 11, bold: true, color: { argb: WHITE } };
    c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
    c.alignment = { horizontal: 'center', vertical: 'middle' };
    c.border = cellBorder;
  });
  s1.getRow(9).height = 22;

  const lastCe = ceTable && ceTable.length ? ceTable[ceTable.length - 1] : null;
  const lastBase = baselineTable.length ? baselineTable[baselineTable.length - 1] : null;
  const lifeExp = num(form.lifeExpectancy) || 90;

  const totalRMDBase = baselineTable.reduce((s, r) => s + (r.rmd || 0), 0);
  const totalRMDCe   = (ceTable || []).reduce((s, r) => s + (r.rmd || 0), 0);

  // TFLI three-stage breakdown (only when the live scenario runs a TFLI bucket).
  const tfliConvRowsS = (ceTable || []).filter(r => (r.conversionAmount || 0) > 0);
  const tfliYear1S    = ceTable?.[0]?.tfliIncome || 0;
  const tfliSteadyS   = tfliConvRowsS.length ? Math.max(...tfliConvRowsS.map(r => r.tfliIncome || 0)) : 0;
  const tfliPaidS     = (ceTable || []).reduce((s, r) => s + (r.tfliIncome || 0), 0);
  const hasTfliS      = tfliPaidS > 0;

  const metricRows = [
    ...(goalLean === 'ltfi'
      ? (hasTfliS
          ? [
              { label: 'Tax-Free Income — Available This Year', base: 0, ce: tfliYear1S, advDir: 'pos', numFmt: '"$"#,##0"/yr"' },
              { label: 'Tax-Free Income — Steady During Conversion', base: 0, ce: tfliSteadyS, advDir: 'pos', numFmt: '"$"#,##0"/yr"' },
              { label: 'Tax-Free Income — Maximum at Full Conversion (up to)', base: baseLTFI, ce: ltfiIncome, advDir: 'pos', numFmt: '"Up to $"#,##0"/yr"' },
            ]
          : [{ label: 'Tax-Free Lifetime Income', base: baseLTFI, ce: ltfiIncome, advDir: 'pos', numFmt: '"$"#,##0"/yr"' }])
      : []),
    { label: `Final Legacy at Age ${lifeExp}`, base: summary?.finalLegacyBaseline, ce: summary?.finalLegacy, advDir: 'pos' },
    { label: 'Lifetime Taxes (Fed + State + IRMAA)', base: summary?.totalTaxBaseline, ce: summary?.totalTaxScenario, advDir: 'neg' },
    { label: 'Total RMDs', base: totalRMDBase, ce: totalRMDCe, advDir: 'neg' },
    { label: 'Break-Even Age', base: '—', ce: breakEvenAge != null ? `Age ${breakEvenAge}` : 'Not by 90', advDir: 'text' },
    { label: 'Paid by your Convert Engine (from inside your account)', base: 0, ce: jbfExtras?.totalJBFTaxFunded || 0, advDir: 'pos' },
    ...(hasTfliS ? [{ label: 'Total Paid to You by your TFLI (cumulative tax-free income)', base: 0, ce: tfliPaidS, advDir: 'pos' }] : []),
    { label: 'Out-of-Pocket Cost to Convert', base: summary?.totalTaxBaseline, ce: jbfExtras?.totalExternalTaxPaid || 0, advDir: 'neg' },
    { label: `Roth Balance at Age ${lifeExp}`, base: lastBase?.rothBalance || 0, ce: lastCe?.rothBalance || 0, advDir: 'pos' },
  ];

  metricRows.forEach((m, i) => {
    const rowNum = 10 + i;
    const row = s1.getRow(rowNum);
    const altRow = i % 2 === 1;
    const setCell = (col, val, opts = {}) => {
      const cell = row.getCell(col);
      cell.value = val;
      cell.font = { name: 'Arial', size: 11, ...(opts.font || {}) };
      if (altRow) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT } };
      cell.alignment = opts.alignment || { horizontal: col === 1 ? 'left' : 'right', vertical: 'middle', indent: 1 };
      cell.border = cellBorder;
      if (opts.numFmt) cell.numFmt = opts.numFmt;
    };
    setCell(1, m.label);
    const isNum = typeof m.base === 'number';
    if (typeof m.base === 'number') setCell(2, m.base, { numFmt: m.numFmt || '"$"#,##0' });
    else setCell(2, m.base ?? '—', { alignment: { horizontal: 'center', vertical: 'middle' } });
    if (typeof m.ce === 'number') setCell(3, m.ce, { numFmt: m.numFmt || '"$"#,##0' });
    else setCell(3, m.ce ?? '—', { alignment: { horizontal: 'center', vertical: 'middle' } });

    let advVal = '';
    let advColor = null;
    if (m.advDir === 'text') {
      advVal = m.ce ?? '—';
    } else if (isNum && typeof m.ce === 'number') {
      const advNum = m.advDir === 'pos' ? (m.ce - m.base) : (m.base - m.ce);
      const abs = Math.abs(advNum);
      const txt = abs >= 1_000_000 ? '$' + (abs / 1_000_000).toFixed(2) + 'M' : '$' + Math.round(abs).toLocaleString();
      advVal = (advNum > 0 ? '+' : (advNum < 0 ? '-' : '')) + txt;
      advColor = advNum > 0 ? GOLD : null;
    }
    setCell(4, advVal, advColor
      ? { font: { bold: true, color: { argb: advColor } }, alignment: { horizontal: 'right', vertical: 'middle', indent: 1 } }
      : { alignment: { horizontal: m.advDir === 'text' ? 'center' : 'right', vertical: 'middle', indent: 1 } }
    );
    row.height = 18;
  });

  // ── Convert Engine cost-story block ──────────────────────────────────────────
  // Word-for-word identical to the on-screen totals strip and PrintPlan:
  // "You pay $[Y1] to start, $[total] total, to turn $[converted] into tax-free Roth."
  if (ceTable && ceTable.length) {
    const money = (v) => '$' + Math.round(v || 0).toLocaleString();
    const y1ExtTax       = ceTable.find(r => (r.conversionAmount || 0) > 0)?.externalTaxPaid || 0;
    const totalExtTax    = ceTable.reduce((s, r) => s + (r.externalTaxPaid || 0), 0);
    const totalConverted = ceTable.reduce((s, r) => s + (r.conversionAmount || 0), 0);
    const storyRow = 10 + metricRows.length + 1; // one spacer row below the metrics table
    s1.getRow(storyRow - 1).height = 8;
    s1.mergeCells(`A${storyRow}:D${storyRow}`);
    const sc = s1.getCell(`A${storyRow}`);
    sc.value = `You pay ${money(y1ExtTax)} to start, ${money(totalExtTax)} total, to turn ${money(totalConverted)} into tax-free Roth.`;
    sc.font = { name: 'Arial', size: 12, bold: true, color: { argb: NAVY } };
    sc.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: GOLD } };
    sc.alignment = { horizontal: 'left', vertical: 'middle', indent: 1 };
    s1.getRow(storyRow).height = 28;
  }

  // ───── SHEET 2: CONVERT ENGINE ─────
  // One worksheet per checked scenario, each tab named after the scenario label.
  // 'Summary'/'Baseline' are reserved so scenario tabs never collide with them.
  const usedSheetNames = new Set(['summary', 'baseline']);
  scenariosToExport.forEach(scenario => {
    const table = results.v2ScenarioTables?.[scenario.id] || [];
    const label = scenario.name || `Scenario ${form.rothScenarios.indexOf(scenario) + 1}`;
    const sheetName = makeSheetName(label, usedSheetNames);
    // Tax-Free Income per row = that row's Roth Balance × this scenario's LTFI yield
    // (the same yield the dashboard/Summary use). Present in both lenses; emphasis switches.
    const scenLtfiRate = (parseFloat(scenario.ltfiYield) || 6) / 100;
    const hasQcd = table.some(r => (r.qcd || 0) > 0);
    const hasTfli = table.some(r => (r.tfliBucketBalance || 0) > 0 || (r.tfliIncome || 0) > 0);
    const ceColumns = [
      { hdr: 'Age',            width: 6,  key: 'age',                  type: 'num' },
      { hdr: 'IRA Balance',    width: 14, key: 'iraBalance',           type: 'currency' },
      { hdr: 'Conversion',     width: 12, key: 'conversionAmount',     type: 'currency' },
      { hdr: 'Conv. Bracket',  width: 11, type: 'pct', compute: (r) => (r.conversionAmount > 0 && r.convMarginalRate > 0) ? Math.round(r.convMarginalRate * 100) : null },
      { hdr: 'Conversion Cost',width: 13, key: 'conversionTaxCost',    type: 'currency' },
      { hdr: 'CE Pays',        width: 11, key: 'jbfYieldUsedForTax',   type: 'currency' },
      { hdr: 'You Pay',        width: 11, key: 'externalTaxPaid',      type: 'currency' },
      { hdr: 'Convert Engine', width: 13, key: 'jbfBucketBalance',     type: 'currency' },
      { hdr: 'Std Roth',       width: 12, key: 'standardRothBalance',  type: 'currency' },
      ...(hasTfli ? [
        { hdr: 'TFLI Bucket', width: 12, key: 'tfliBucketBalance', type: 'currency' },
        { hdr: 'TFLI Income', width: 12, key: 'tfliIncome',        type: 'currency' },
      ] : []),
      ...(form.showAdvisoryFee ? [{ hdr: 'Advisory Fee', width: 11, key: 'advisoryFee', type: 'currency' }] : []),
      { hdr: 'RMD',            width: 12, key: 'rmd',                  type: 'currency' },
      ...(hasQcd ? [{ hdr: 'QCD', width: 10, key: 'qcd', type: 'currency' }] : []),
      { hdr: 'IRA Gap Draw',   width: 13, key: 'iraGapGross',          type: 'currency' },
      { hdr: 'SS Income',      width: 12, key: 'ssIncome',             type: 'currency' },
      { hdr: 'Pension',        width: 11, key: 'pensionIncome',        type: 'currency' },
      { hdr: 'Other Income',   width: 12, key: 'otherIncome',          type: 'currency' },
      { hdr: 'Total Income',   width: 13, key: 'totalIncome',          type: 'currency' },
      { hdr: 'SS Taxable %',   width: 11, key: 'ssTaxablePct',         type: 'pct' },
      { hdr: 'Gross Taxable',  width: 13, key: 'grossTaxableIncome',   type: 'currency' },
      { hdr: 'Fed Tax',        width: 10, key: 'fedTax',               type: 'currency' },
      { hdr: 'State Tax',      width: 10, key: 'stateTax',             type: 'currency' },
      { hdr: 'IRMAA',          width: 9,  key: 'irmaa',                type: 'currency' },
      { hdr: 'Total Tax',      width: 11, key: 'totalTax',             type: 'currency' },
      { hdr: 'Net Income',     width: 12, key: 'netIncome',            type: 'currency' },
      { hdr: 'Expenses',       width: 11, key: 'annualExpenses',       type: 'currency' },
      { hdr: 'Roth Balance',   width: 14, key: 'rothBalance',          type: 'currency' },
      { hdr: 'Legacy',         width: 12, key: 'legacy',               type: 'currency' },
      { hdr: 'Sustainable Tax-Free Income', width: 22, type: 'currency', compute: (r) => Math.round((r.rothBalance || 0) * scenLtfiRate) },
    ];
    writeDataSheet(wb, sheetName, NAVY, `${label} — Year-by-Year`, subtitle, ceColumns, table, {
      // Emphasize the goal's final output column on the last row: Tax-Free Income (income
      // lens = Sustainable) or Legacy (legacy lens). Index counted from the merged ceColumns
      // (main's IRA Gap Draw + this branch's Conv. Bracket / Conversion Cost / CE Pays /
      // You Pay all sit before Legacy).
      legacyCol: (goalLean === 'ltfi' ? 26 : 25) + (hasQcd ? 1 : 0) + (form.showAdvisoryFee ? 1 : 0) + (hasTfli ? 2 : 0),
      lastRowLegacyFill: GOLD, highlightRothAfterIraZero: true,
    });
  });

  // ───── SHEET 3: BASELINE ─────
  const baseColumns = [
    { hdr: 'Age',           width: 6,  key: 'age',                type: 'num' },
    { hdr: 'IRA Balance',   width: 14, key: 'iraBalance',         type: 'currency' },
    { hdr: 'RMD',           width: 12, key: 'rmd',                type: 'currency' },
    { hdr: 'SS Income',     width: 12, key: 'ssIncome',           type: 'currency' },
    { hdr: 'SS Taxable %',  width: 11, key: 'ssTaxablePct',       type: 'pct' },
    { hdr: 'Gross Taxable', width: 13, key: 'grossTaxableIncome', type: 'currency' },
    { hdr: 'Fed Tax',       width: 10, key: 'fedTax',             type: 'currency' },
    { hdr: 'State Tax',     width: 10, key: 'stateTax',           type: 'currency' },
    { hdr: 'IRMAA',         width: 9,  key: 'irmaa',              type: 'currency' },
    { hdr: 'Total Tax',     width: 11, key: 'totalTax',           type: 'currency' },
    { hdr: 'Net Income',    width: 12, key: 'netIncome',          type: 'currency' },
    { hdr: 'Expenses',      width: 11, key: 'annualExpenses',     type: 'currency' },
    { hdr: 'Legacy',        width: 12, key: 'legacy',             type: 'currency' },
  ];
  writeDataSheet(wb, 'Baseline', null, 'Baseline — No Roth Conversion', subtitle, baseColumns, baselineTable, {
    legacyCol: 13, lastRowLegacyFill: SOFT_BLUE,
  });

  // ───── DOWNLOAD ─────
  const lastName = form.clientLastName?.trim() || '';
  const fileName = lastName ? `JBF_${lastName}_Plan.xlsx` : 'JBF_RetirementPlan.xlsx';
  const buffer = await wb.xlsx.writeBuffer();
  const blob = new Blob([buffer], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = fileName;
  a.click();
  URL.revokeObjectURL(url);

  // Build a valid, unique Excel sheet name from a scenario label.
  // Excel rules: max 31 chars, must be unique (case-insensitive), and may not
  // contain : \ / ? * [ ]. `used` is a Set of already-claimed lowercased names.
  function makeSheetName(label, used) {
    let base = (label || 'Scenario')
      .replace(/[:\\/?*[\]]/g, '')   // strip Excel-illegal chars
      .replace(/\s+/g, ' ')
      .trim()
      .slice(0, 31);
    if (!base) base = 'Scenario';

    let name = base;
    let n = 2;
    while (used.has(name.toLowerCase())) {
      // Append " (n)" while staying within the 31-char cap.
      const suffix = ` (${n})`;
      name = base.slice(0, 31 - suffix.length) + suffix;
      n++;
    }
    used.add(name.toLowerCase());
    return name;
  }

  function writeDataSheet(wb, name, tabColor, title, subtitle, columns, data, opts) {
    const sh = wb.addWorksheet(name, {
      ...(tabColor ? { properties: { tabColor: { argb: tabColor } } } : {}),
      views: [{ showGridLines: false, state: 'frozen', ySplit: 4 }],
    });
    sh.columns = columns.map(c => ({ width: c.width }));

    const lastColLetter = String.fromCharCode(64 + columns.length);

    // Row 1: title
    sh.mergeCells(`A1:${lastColLetter}1`);
    const t = sh.getCell('A1');
    t.value = title;
    t.font = { name: 'Arial', size: 12, bold: true, color: { argb: WHITE } };
    t.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
    t.alignment = { horizontal: 'center', vertical: 'middle' };
    sh.getRow(1).height = 22;

    // Row 2: subtitle
    sh.mergeCells(`A2:${lastColLetter}2`);
    const st = sh.getCell('A2');
    st.value = subtitle;
    st.font = { name: 'Arial', size: 10, color: { argb: WHITE } };
    st.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
    st.alignment = { horizontal: 'center', vertical: 'middle' };
    sh.getRow(2).height = 18;

    // Row 3: spacer
    sh.getRow(3).height = 6;

    // Row 4: column headers
    columns.forEach((col, i) => {
      const c = sh.getRow(4).getCell(i + 1);
      c.value = col.hdr;
      c.font = { name: 'Arial', size: 10, bold: true, color: { argb: WHITE } };
      c.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: NAVY } };
      c.alignment = { horizontal: 'center', vertical: 'middle', wrapText: true };
      c.border = cellBorder;
    });
    sh.getRow(4).height = 28;

    // Data rows
    const lastDataIdx = data.length - 1;
    data.forEach((row, idx) => {
      const rowNum = 5 + idx;
      const isLastRow = idx === lastDataIdx;
      const altRow = idx % 2 === 1;
      const xlRow = sh.getRow(rowNum);
      columns.forEach((col, ci) => {
        const cell = xlRow.getCell(ci + 1);
        const raw = col.compute ? col.compute(row) : row[col.key];
        if (col.type === 'currency') {
          if (raw === 0 || raw == null) {
            cell.value = raw === 0 ? 0 : '';
            cell.alignment = { horizontal: raw === 0 ? 'right' : 'center', vertical: 'middle', indent: 1 };
          } else {
            cell.value = raw;
            cell.alignment = { horizontal: 'right', vertical: 'middle', indent: 1 };
          }
          cell.numFmt = '"$"#,##0';
        } else if (col.type === 'pct') {
          if (raw == null || raw === 0) {
            cell.value = '';
            cell.alignment = { horizontal: 'center', vertical: 'middle' };
          } else {
            cell.value = raw / 100;
            cell.numFmt = '0%';
            cell.alignment = { horizontal: 'right', vertical: 'middle', indent: 1 };
          }
        } else {
          cell.value = raw ?? '';
          cell.alignment = { horizontal: 'center', vertical: 'middle' };
        }
        cell.font = { name: 'Arial', size: 10 };
        if (altRow) cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: ALT } };
        cell.border = cellBorder;

        // Legacy column on last row: branded fill + bold
        if (opts?.legacyCol && (ci + 1) === opts.legacyCol && isLastRow && opts.lastRowLegacyFill) {
          cell.fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: opts.lastRowLegacyFill } };
          cell.font = { name: 'Arial', size: 10, bold: true };
        }
        // Roth Balance: bold once IRA reaches zero (conversion complete)
        if (opts?.highlightRothAfterIraZero && col.key === 'rothBalance' && row.conversionComplete) {
          cell.font = { name: 'Arial', size: 10, bold: true };
        }
        // Final row: bold across all cells
        if (isLastRow) {
          const existing = cell.font || { name: 'Arial', size: 10 };
          cell.font = { ...existing, bold: true };
        }
      });
      xlRow.height = 16;
    });
  }
}

function exportAllTablesToXLSX(results, form) {
  const doExport = async (XLSX) => {
    const wb = XLSX.utils.book_new();

    const activeBtnBefore = document.querySelector('.atv-btn-active');
    const tabButtons = Array.from(document.querySelectorAll('.atv-btn'));

    const toggleBtn = document.querySelector('.annual-table-toggle-btn');
    const wasCollapsed = toggleBtn && toggleBtn.textContent.includes('Show');
    if (wasCollapsed) toggleBtn.click();
    await new Promise(r => setTimeout(r, 200));

    const tabNames = tabButtons.map(b => b.textContent.trim());

    for (const btn of tabButtons) {
      btn.click();
      await new Promise(r => setTimeout(r, 200));

      const table = document.querySelector('.annual-table');
      if (!table) continue;

      const headers = Array.from(table.querySelectorAll('thead th'))
        .map(th => th.textContent.trim());
      const rows = Array.from(table.querySelectorAll('tbody tr'))
        .map(tr => Array.from(tr.querySelectorAll('td')).map(td => {
          const text = td.textContent.trim().replace(/[$,]/g, '');
          const num = Number(text);
          return text !== '' && !isNaN(num) ? num : td.textContent.trim();
        }));

      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      ws['!cols'] = headers.map(() => ({ wch: 14 }));
      ws['!freeze'] = { xSplit: 0, ySplit: 1 };

      const sheetName = btn.textContent.trim().substring(0, 31);
      XLSX.utils.book_append_sheet(wb, ws, sheetName);
    }

    activeBtnBefore?.click();
    if (wasCollapsed && toggleBtn) toggleBtn.click();

    const scsTable = document.querySelector('.annual-table-section .scs-table-wrap table');
    if (scsTable) {
      const headers = Array.from(scsTable.querySelectorAll('thead th'))
        .map(th => th.textContent.trim());
      const rows = Array.from(scsTable.querySelectorAll('tbody tr'))
        .map(tr => Array.from(tr.querySelectorAll('td')).map(td => td.textContent.trim()));
      const ws = XLSX.utils.aoa_to_sheet([headers, ...rows]);
      ws['!cols'] = headers.map(() => ({ wch: 18 }));
      XLSX.utils.book_append_sheet(wb, ws, 'Scenario Comparison');
    }

    const lastName = form.clientLastName?.trim() || '';
    const fileName = lastName ? `JBF_${lastName}_Plan.xlsx` : 'JBF_RetirementPlan.xlsx';
    XLSX.writeFile(wb, fileName);
  };

  const run = (XLSX) => doExport(XLSX).catch(err => console.error('Export failed:', err));

  if (window.XLSX) {
    run(window.XLSX);
  } else {
    const script = document.createElement('script');
    script.src = 'https://cdnjs.cloudflare.com/ajax/libs/xlsx/0.18.5/xlsx.full.min.js';
    script.onload = () => run(window.XLSX);
    document.head.appendChild(script);
  }
}


function AnnualTableSection({ results, form, set, liveScenarioId, setLiveScenarioId, printScenarioIds, setPrintScenarioIds, setShowCEReport }) {
  const goalLean = form?.goalLean === 'ltfi' ? 'ltfi' : 'legacy';
  // Per-scenario end-of-conversions Roth → Tax-Free Lifetime Income at the scenario's yield.
  const ltfiFor = (s) => {
    const ext = results?.v2JBFExtras?.[s.id];
    if (!ext) return 0;
    const t = results?.v2ScenarioTables?.[s.id];
    const roth = ext.conversionCompleteRow?.rothBalance
      ?? (t ? [...t].reverse().find(r => r.conversionAmount > 0)?.rothBalance : 0) ?? 0;
    return Math.round(roth * ((parseFloat(s.ltfiYield) || 6) / 100));
  };
  // TFLI three-stage income breakdown (Aug 2026): year-1 sleeve income, the sleeve's
  // plateau during the conversion window, and the age the max (whole-Roth) figure starts.
  const tfliStagesFor = (s) => {
    const t = results?.v2ScenarioTables?.[s.id];
    if (!t || !t.length) return null;
    const convRowsT = t.filter(r => (r.conversionAmount || 0) > 0);
    const year1  = t[0]?.tfliIncome || 0;
    const steady = convRowsT.length ? Math.max(...convRowsT.map(r => r.tfliIncome || 0)) : 0;
    // Plateau start = first year income reaches the steady level (±$1 rounding), NOT the
    // first year of any income — the year-1 partial ($30K) belongs to the row above.
    const startAge    = steady > 0 ? (t.find(r => (r.tfliIncome || 0) >= steady - 1)?.age ?? null) : null;
    const lastConvAge = convRowsT.length ? convRowsT[convRowsT.length - 1].age : null;
    const paidTotal   = t.reduce((sum, r) => sum + (r.tfliIncome || 0), 0);
    return { year1, steady, startAge, lastConvAge, paidTotal };
  };
  const [collapsed, setCollapsed] = useState(true);

  if (!results || results.error) return null;
  if (!results.v2BaselineTable || results.v2BaselineTable.length === 0) return null;

  const liveTable = liveScenarioId != null
    ? (results.v2ScenarioTables?.[liveScenarioId] ?? results.v2BaselineTable)
    : results.v2BaselineTable;

  const liveScenario = form.rothScenarios.find(s => s.id === liveScenarioId);
  const liveLabel = liveScenario
    ? (liveScenario.name || `Scenario ${form.rothScenarios.indexOf(liveScenario) + 1}`)
    : 'No Conversion Baseline';

  const isJBFLive = liveScenario?.vehicle === 'jbf';

  const retirementRows = liveTable;
  // LTFI yield for the live scenario — same basis the export/Summary use.
  const scenLtfiRate = (parseFloat(liveScenario?.ltfiYield) || 6) / 100;
  // Only surface the QCD column when the live scenario actually uses QCDs.
  const hasQcd = !!retirementRows?.some(r => (r.qcd || 0) > 0);
  const hasTfli = !!retirementRows?.some(r => (r.tfliBucketBalance || 0) > 0 || (r.tfliIncome || 0) > 0);
  const firstExhausted = retirementRows?.find(r => r.fundingExhausted) || null;

  return (
    <div className="annual-table-section">
      <div className="annual-table-header">
        <div>
          <h2 className="annual-table-title">Annual Retirement Income Table</h2>
          <div className="annual-table-subtitle">
            Showing: <strong>{liveLabel}</strong>
              {form.survivorEnabled && (
              <span className="survivor-note"> · Survivor view active at age {form.survivorAge}</span>
            )}
          </div>
        </div>
        <div className="annual-table-view-toggle">
          <span className="atv-label">View:</span>
          <button
            className={`atv-btn${liveScenarioId == null ? ' atv-btn-active' : ''}`}
            onClick={() => setLiveScenarioId(null)}
          >Baseline</button>
          {form.rothScenarios.map((s, i) => (
            <button
              key={s.id}
              className={`atv-btn${liveScenarioId === s.id ? ' atv-btn-active' : ''}`}
              onClick={() => setLiveScenarioId(s.id)}
            >
              {s.name || `Scenario ${i + 1}`}
            </button>
          ))}
        </div>
        {form.rothScenarios.length > 0 && (
          <div className="atv-print-select">
            <span className="atv-label">Include in Print:</span>
            {form.rothScenarios.map((s, i) => {
              const checked = printScenarioIds.includes(s.id);
              return (
                <label key={s.id} className="atv-print-check">
                  <input
                    type="checkbox"
                    checked={checked}
                    onChange={() => setPrintScenarioIds(prev =>
                      checked ? prev.filter(id => id !== s.id) : [...prev, s.id]
                    )}
                  />
                  <span>{s.name || `Scenario ${i + 1}`}</span>
                </label>
              );
            })}
          </div>
        )}
        <div style={{ display: 'flex', gap: '8px', marginTop: '8px', flexWrap: 'wrap' }}>
          <button
            onClick={async () => {
              try {
                await exportToExcelStyled(form, results, printScenarioIds, liveScenarioId);
              } catch (err) {
                console.error('Styled export failed, falling back to legacy export:', err);
                exportAllTablesToXLSX(results, form);
              }
            }}
            style={{
              padding: '8px 16px',
              background: '#1a3a5c',
              color: 'white',
              border: '1px solid #4a90d9',
              borderRadius: '6px',
              cursor: 'pointer',
              fontSize: '13px',
              fontWeight: '600',
            }}
          >
            📥 Export to Excel
          </button>
{form.rothScenarios.some(s => s.vehicle === 'jbf') && (
            <button
              onClick={() => setShowCEReport(true)}
              style={{
                padding: '8px 16px',
                background: '#C9A84C',
                color: 'white',
                border: 'none',
                borderRadius: '6px',
                cursor: 'pointer',
                fontSize: '13px',
                fontWeight: '600',
              }}
            >
              Generate CE Portfolio
            </button>
          )}
        </div>
      </div>

      {firstExhausted && (
        <div className="funding-exhausted-banner">
          <strong>⚠ Underfunded from age {firstExhausted.age}.</strong> Expenses and/or
          conversion tax exceed the assets available outside the IRA (guaranteed income,
          Roth, non-qualified, annuity). The IRA is preserved rather than raided — the
          shortfall shows as an unmet income gap in the flagged years.
        </div>
      )}

      <div className="annual-table-scroll-wrap">
        <button className="annual-table-toggle-btn" onClick={() => setCollapsed(v => !v)}>
          {collapsed ? 'Show Annual Table' : 'Hide Annual Table'}
        </button>

        {!collapsed && (
          <div className="atv-col-toggles" style={{ display: 'flex', gap: '16px', flexWrap: 'wrap', margin: '8px 0 12px', fontSize: '13px' }}>
            <span className="atv-label">Optional columns:</span>
            {isJBFLive && (
              <label className="atv-col-check" style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
                <input
                  type="checkbox"
                  checked={!!form.showAdvisoryFee}
                  onChange={() => set && set('showAdvisoryFee')(!form.showAdvisoryFee)}
                />
                <span>Advisory Fee</span>
              </label>
            )}
            <label className="atv-col-check" style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!form.showRothIncome}
                onChange={() => set && set('showRothIncome')(!form.showRothIncome)}
              />
              <span>Roth Income</span>
            </label>
            <label className="atv-col-check" style={{ display: 'flex', alignItems: 'center', gap: '5px', cursor: 'pointer' }}>
              <input
                type="checkbox"
                checked={!!form.showBaseTaxes}
                onChange={() => set && set('showBaseTaxes')(!form.showBaseTaxes)}
              />
              <span>Total Base Taxes</span>
            </label>
          </div>
        )}

        {!collapsed && (
          <div className="annual-table-scroll">
            <table className="annual-table">
              <thead>
                <tr>
                  <th>Age</th>
                  <th>IRA Balance</th>
                  <th>Conversion</th>
                  <th>Conv. Bracket</th>
                  {isJBFLive && <th className="at-jbf-hdr">Conversion Cost</th>}
                  {isJBFLive && <th className="at-jbf-hdr">CE Pays</th>}
                  {isJBFLive && <th className="at-jbf-hdr">You Pay</th>}
                  {isJBFLive && <th className="at-jbf-hdr">Convert Engine</th>}
                  {isJBFLive && <th className="at-jbf-hdr">Std Roth</th>}
                  {isJBFLive && hasTfli && <th className="at-jbf-hdr">TFLI Bucket</th>}
                  {isJBFLive && hasTfli && <th className="at-jbf-hdr">TFLI Income</th>}
                  {isJBFLive && form.showAdvisoryFee && <th className="at-jbf-hdr">Advisory Fee</th>}
                  <th>RMD</th>
                  {hasQcd && <th>QCD</th>}
                  <th>IRA Gap Draw</th>
                  <th>SS Income</th>
                  <th>Pension</th>
                  <th>Other Income</th>
                  <th>Total Income</th>
                  {form.showRothIncome && <th>Roth Income</th>}
                  <th>SS Taxable %</th>
                  <th>Gross Taxable</th>
                  <th>Fed Tax</th>
                  <th>State Tax</th>
                  <th>IRMAA</th>
                  <th>Total Tax</th>
                  {form.showBaseTaxes && <th>Total Base Taxes</th>}
                  <th>Net Income</th>
                  <th>Expenses</th>
                  <th>Income Gap</th>
                  <th>Roth Balance</th>
                  <th>Legacy</th>
                  <th>Sustainable Tax-Free Income</th>
                </tr>
              </thead>
              <tbody>
                {retirementRows.map((row, i) => {
                  const hasGap = row.incomeGap > 0;
                  const isSurvivor = form.survivorEnabled && row.age >= (parseFloat(form.survivorAge) || 80);
                  const isExhausted = !!row.fundingExhausted;
                  return (
                    <tr
                      key={row.age}
                      className={[
                        i % 2 === 1 ? 'at-alt' : '',
                        hasGap ? 'at-gap' : '',
                        isSurvivor ? 'at-survivor' : '',
                        isExhausted ? 'at-exhausted' : '',
                      ].filter(Boolean).join(' ')}
                    >
                      <td className="at-age">
                        {row.age}
                        {isSurvivor && <span className="at-survivor-badge">S</span>}
                        {isExhausted && <span className="at-exhausted-badge" title="Funding source exhausted — shortfall not drawn from the IRA">⚠</span>}
                      </td>
                      <td>{fmtDM((row.iraBalance || 0) + (liveScenarioId == null ? (row.annuityBalance || 0) : 0))}</td>
                      <td>{row.conversionAmount > 0 ? fmtD(row.conversionAmount) : '—'}</td>
                      <td>{row.conversionAmount > 0 && row.convMarginalRate > 0 ? `${Math.round(row.convMarginalRate * 100)}%` : '—'}</td>
                      {isJBFLive && <td className="at-jbf">{row.conversionTaxCost > 0 ? fmtD(row.conversionTaxCost) : '—'}</td>}
                      {isJBFLive && <td className="at-jbf">{row.jbfYieldUsedForTax > 0 ? fmtD(row.jbfYieldUsedForTax) : '—'}</td>}
                      {isJBFLive && <td className="at-jbf">{row.externalTaxPaid > 0 ? fmtD(row.externalTaxPaid) : '—'}</td>}
                      {isJBFLive && <td className="at-jbf">{row.jbfBucketBalance > 0 ? fmtDM(row.jbfBucketBalance) : '—'}</td>}
                      {isJBFLive && <td className="at-jbf">{row.standardRothBalance > 0 ? fmtDM(row.standardRothBalance) : '—'}</td>}
                      {isJBFLive && hasTfli && <td className="at-jbf">{row.tfliBucketBalance > 0 ? fmtDM(row.tfliBucketBalance) : '—'}</td>}
                      {isJBFLive && hasTfli && <td className="at-jbf">{row.tfliIncome > 0 ? fmtD(row.tfliIncome) : '—'}</td>}
                      {isJBFLive && form.showAdvisoryFee && <td className="at-jbf">{row.advisoryFee > 0 ? fmtD(row.advisoryFee) : '—'}</td>}
                      <td>{row.rmd > 0 ? fmtD(row.rmd) : '—'}</td>
                      {hasQcd && <td>{row.qcd > 0 ? fmtD(row.qcd) : '—'}</td>}
                      <td>{row.iraGapGross > 0 ? fmtD(row.iraGapGross) : '—'}</td>
                      <td>{fmtD(row.ssIncome)}</td>
                      <td>{row.pensionIncome > 0 ? fmtD(row.pensionIncome) : '—'}</td>
                      <td>{row.otherIncome > 0 ? fmtD(row.otherIncome) : '—'}</td>
                      <td>{fmtD(row.totalIncome)}</td>
                      {form.showRothIncome && <td>{row.rothDistribution > 0 ? fmtD(row.rothDistribution) : '—'}</td>}
                      <td>{row.ssTaxablePct != null ? `${row.ssTaxablePct}%` : '—'}</td>
                      <td>{fmtD(row.grossTaxableIncome)}</td>
                      <td>{fmtD(row.fedTax)}</td>
                      <td>{row.stateTax > 0 ? fmtD(row.stateTax) : '—'}</td>
                      <td>{row.irmaa > 0 ? fmtD(row.irmaa) : '—'}</td>
                      <td className="at-tax">{fmtD(row.totalTax)}</td>
                      {form.showBaseTaxes && <td>{fmtD(Math.max(0, (row.totalTax || 0) - (row.conversionTaxCost || 0)))}</td>}
                      <td className="at-net">{fmtD(row.netIncome)}</td>
                      <td>{fmtD(row.annualExpenses)}</td>
                      <td className={hasGap ? 'at-gap-val' : 'at-ok'}>
                        {hasGap ? fmtD(row.incomeGap) : '✓'}
                      </td>
                      <td>{fmtDM(row.rothBalance)}</td>
                      <td className="at-legacy">{fmtDM(row.legacy)}</td>
                      <td>{fmtD(Math.round((row.rothBalance || 0) * scenLtfiRate))}</td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {/* Scenario comparison table vs baseline */}
      {form.rothScenarios.length > 0 && results.v2Summaries && (() => {
        const validScenarios = form.rothScenarios.filter(s => results.v2Summaries[s.id]);
        if (validScenarios.length === 0) return null;
        const anySummary = results.v2Summaries[validScenarios[0].id];

        return (
          <div className="scs-table-wrap">
            <div className="scs-dash-header">
              <div className="scs-dash-q">
                Does a Roth conversion help your goal? — <span className="scs-dash-yes">YES</span>
                <span className="scs-dash-goal">Goal: {goalLean === 'ltfi' ? 'Tax-Free Lifetime Income' : 'Legacy'}</span>
              </div>
              <div className="goal-lean-toggle">
                <button type="button" className={`goal-lean-btn${goalLean === 'legacy' ? ' goal-lean-active' : ''}`}
                  onClick={() => set && set('goalLean')('legacy')}>Legacy</button>
                <button type="button" className={`goal-lean-btn${goalLean === 'ltfi' ? ' goal-lean-active' : ''}`}
                  onClick={() => set && set('goalLean')('ltfi')}>Tax-Free Lifetime Income</button>
              </div>
            </div>
            <div className="scs-table-scroll">
              <table className="scs-table">
                <thead>
                  <tr>
                    <th className="scs-th-metric">Metric</th>
                    <th className="scs-th-baseline">Baseline<br/><span className="scs-th-sub">No Conversion</span></th>
                    {validScenarios.map((s, i) => (
                      <th key={s.id} className={`scs-th-scenario${liveScenarioId === s.id ? ' scs-th-live' : ''}`}>
                        {s.name || `Scenario ${i + 1}`}
                        {liveScenarioId === s.id && <span className="scs-live-pip"> ●</span>}
                      </th>
                    ))}
                  </tr>
                </thead>
                <tbody>
                  {/* Lead row — goal-dependent: Tax-Free Lifetime Income (LTFI) or Final Legacy.
                     When any scenario runs a TFLI bucket, the single LTFI number expands into a
                     three-stage income story: available THIS year, steady during the conversion
                     window, and the maximum once the IRA is fully converted. Plans without a
                     TFLI bucket keep the original single row, pixel-identical. */}
                  {goalLean === 'ltfi' ? (
                    validScenarios.some(s => (tfliStagesFor(s)?.year1 || tfliStagesFor(s)?.steady) > 0) ? (
                      <>
                        <tr className="scs-lead-row">
                          <td className="scs-metric-label">Tax-Free Income — Available This Year<br/><span className="scs-metric-sub">paid by the TFLI sleeve in year 1</span></td>
                          <td className="scs-baseline-val scs-baseline-na">—</td>
                          {validScenarios.map(s => {
                            const st = s.vehicle === 'jbf' ? tfliStagesFor(s) : null;
                            return (
                              <td key={s.id} className="scs-scenario-val">
                                <div className="scs-val-main scs-jbf-funded">
                                  {st && st.year1 > 0 ? `${fmtDM(st.year1)}/yr` : (s.vehicle === 'jbf' ? '$0' : '—')}
                                </div>
                              </td>
                            );
                          })}
                        </tr>
                        <tr className="scs-lead-row">
                          <td className="scs-metric-label">Steady During Conversion<br/><span className="scs-metric-sub">TFLI sleeve plateau while converting</span></td>
                          <td className="scs-baseline-val scs-baseline-na">—</td>
                          {validScenarios.map(s => {
                            const st = s.vehicle === 'jbf' ? tfliStagesFor(s) : null;
                            return (
                              <td key={s.id} className="scs-scenario-val">
                                <div className="scs-val-main scs-jbf-funded">
                                  {st && st.steady > 0 ? `${fmtDM(st.steady)}/yr` : (s.vehicle === 'jbf' ? '$0' : '—')}
                                </div>
                                {st && st.steady > 0 && st.startAge != null && st.lastConvAge != null && (
                                  <div className="scs-jbf-label">ages {st.startAge}–{st.lastConvAge}</div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                        <tr className="scs-lead-row">
                          <td className="scs-metric-label">Maximum at Full Conversion<br/><span className="scs-metric-sub">your income dial's upper end — end-of-conversions Roth × yield</span></td>
                          <td className="scs-baseline-val scs-baseline-na">—</td>
                          {validScenarios.map(s => {
                            const st = s.vehicle === 'jbf' ? tfliStagesFor(s) : null;
                            return (
                              <td key={s.id} className="scs-scenario-val">
                                <div className="scs-val-main scs-jbf-funded">
                                  {s.vehicle === 'jbf' ? `Up to ${fmtDM(ltfiFor(s))}/yr` : '—'}
                                </div>
                                {st && st.lastConvAge != null && (
                                  <div className="scs-jbf-label">age {st.lastConvAge + 1}+</div>
                                )}
                              </td>
                            );
                          })}
                        </tr>
                      </>
                    ) : (
                    <tr className="scs-lead-row">
                      <td className="scs-metric-label">Tax-Free Lifetime Income<br/><span className="scs-metric-sub">end-of-conversions Roth × yield</span></td>
                      <td className="scs-baseline-val scs-baseline-na">—</td>
                      {validScenarios.map(s => (
                        <td key={s.id} className="scs-scenario-val">
                          <div className="scs-val-main scs-jbf-funded">
                            {s.vehicle === 'jbf' ? `${fmtDM(ltfiFor(s))}/yr` : '—'}
                          </div>
                        </td>
                      ))}
                    </tr>
                    )
                  ) : (
                    <tr className="scs-lead-row">
                      <td className="scs-metric-label">Final Legacy<br/><span className="scs-metric-sub">at end of plan</span></td>
                      <td className="scs-baseline-val">
                        {fmtDM(anySummary.finalLegacyBaseline)}
                        {num(form.annuityBalance) > 0 && (() => {
                          const annBal = results.v2BaselineTable?.at(-1)?.annuityBalance ?? 0;
                          return annBal > 0 ? <div className="scs-annuity-sub">Incl. {fmtDM(annBal)} annuity</div> : null;
                        })()}
                      </td>
                      {validScenarios.map(s => {
                        const sum = results.v2Summaries[s.id];
                        return (
                          <td key={s.id} className="scs-scenario-val">
                            <div className="scs-val-main">{fmtDM(sum.finalLegacy)}</div>
                            <div className={`scs-delta ${sum.legacyDelta >= 0 ? 'scs-delta-pos' : 'scs-delta-neg'}`}>
                              {sum.legacyDelta >= 0 ? '▲ +' : '▼ '}{fmtDM(sum.legacyDelta)}
                            </div>
                            {num(form.annuityBalance) > 0 && (() => {
                              const annBal = results.v2ScenarioTables?.[s.id]?.at(-1)?.annuityBalance ?? 0;
                              return annBal > 0 ? <div className="scs-annuity-sub">Incl. {fmtDM(annBal)} annuity</div> : null;
                            })()}
                          </td>
                        );
                      })}
                    </tr>
                  )}
                  {/* Lifetime Taxes */}
                  <tr className="scs-row-alt">
                    <td className="scs-metric-label">Lifetime Taxes<br/><span className="scs-metric-sub">fed + state + IRMAA</span></td>
                    <td className="scs-baseline-val">{fmtDM(anySummary.totalTaxBaseline)}</td>
                    {validScenarios.map(s => {
                      const sum = results.v2Summaries[s.id];
                      return (
                        <td key={s.id} className="scs-scenario-val">
                          <div className="scs-val-main">{fmtDM(sum.totalTaxScenario)}</div>
                          <div className={`scs-delta ${sum.totalTaxDelta >= 0 ? 'scs-delta-pos' : 'scs-delta-neg'}`}>
                            {sum.totalTaxDelta >= 0 ? '▼ -' : '▲ +'}{fmtDM(Math.abs(sum.totalTaxDelta))}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  {/* Total RMDs */}
                  <tr>
                    <td className="scs-metric-label">Total RMDs<br/><span className="scs-metric-sub">cumulative lifetime</span></td>
                    <td className="scs-baseline-val">{fmtDM(anySummary.totalRMDBaseline)}</td>
                    {validScenarios.map(s => {
                      const sum = results.v2Summaries[s.id];
                      return (
                        <td key={s.id} className="scs-scenario-val">
                          <div className="scs-val-main">{fmtDM(sum.totalRMDScenario)}</div>
                          <div className={`scs-delta ${sum.totalRMDDelta >= 0 ? 'scs-delta-pos' : 'scs-delta-neg'}`}>
                            {sum.totalRMDDelta >= 0 ? '▼ -' : '▲ +'}{fmtDM(Math.abs(sum.totalRMDDelta))}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  {/* Break-Even */}
                  <tr className="scs-row-alt">
                    <td className="scs-metric-label">Break-Even Age<br/><span className="scs-metric-sub">legacy exceeds baseline</span></td>
                    <td className="scs-baseline-val scs-baseline-na">—</td>
                    {validScenarios.map(s => {
                      const bea = results.v2BreakEvenAges[s.id];
                      return (
                        <td key={s.id} className="scs-scenario-val">
                          <div className={`scs-val-main ${bea != null ? 'scs-bea-hit' : 'scs-bea-miss'}`}>
                            {bea != null ? `Age ${bea}` : 'Not by age 90'}
                          </div>
                        </td>
                      );
                    })}
                  </tr>
                  {/* Self-Converting Benefit demoted to the Summary narrative line (not a headline KPI). */}
                  {/* IRMAA Impact */}
                  <tr className="scs-row">
                    <td className="scs-metric-label">IRMAA Impact<br/><span className="scs-metric-sub">vs no-conversion baseline</span></td>
                    <td className="scs-baseline-val scs-baseline-na">—</td>
                    {validScenarios.map(s => {
                      // Net lifetime IRMAA = sum of the scenario's yearly IRMAA minus the
                      // baseline's, both from the same V2 engine output the tables use.
                      // Negative = the conversion strategy pays LESS IRMAA (a saving).
                      const sTable = results.v2ScenarioTables?.[s.id];
                      const bTable = results.v2BaselineTable;
                      const scenIRMAA = sTable ? sTable.reduce((sum, r) => sum + (r.irmaa || 0), 0) : 0;
                      const baseIRMAA = bTable ? bTable.reduce((sum, r) => sum + (r.irmaa || 0), 0) : 0;
                      const delta = scenIRMAA - baseIRMAA;
                      const saves = delta < 0; // strategy reduces lifetime IRMAA
                      return (
                        <td key={s.id} className="scs-scenario-val">
                          <div className={delta === 0 ? '' : (saves ? 'scs-val-positive' : 'scs-val-negative')}>
                            {delta === 0 ? '$0' : (saves ? '−' : '+') + fmtD(Math.abs(delta))}
                          </div>
                          <div className="scs-jbf-label">{saves ? 'saved vs baseline' : 'vs baseline'}</div>
                        </td>
                      );
                    })}
                  </tr>
                  {/* JBF-specific rows */}
                  {validScenarios.some(s => s.vehicle === 'jbf') && (
                    <>
                      <tr className="scs-jbf-row">
                        <td className="scs-metric-label">Paid by your Convert Engine<br/><span className="scs-metric-sub">from inside your account — tax-free income</span></td>
                        <td className="scs-baseline-val scs-baseline-na">—</td>
                        {validScenarios.map(s => {
                          const ext = results.v2JBFExtras?.[s.id];
                          return (
                            <td key={s.id} className="scs-scenario-val">
                              {ext ? (
                                <>
                                  <div className="scs-val-main scs-jbf-funded">{fmtDM(ext.totalJBFTaxFunded)}</div>
                                  <div className="scs-jbf-label">self-funded by JBF</div>
                                </>
                              ) : <div className="scs-baseline-na">—</div>}
                            </td>
                          );
                        })}
                      </tr>
                      {validScenarios.some(s => (tfliStagesFor(s)?.paidTotal || 0) > 0) && (
                        <tr className="scs-jbf-row">
                          <td className="scs-metric-label">Total Paid to You by your TFLI<br/><span className="scs-metric-sub">cumulative tax-free income, every year of the plan</span></td>
                          <td className="scs-baseline-val scs-baseline-na">—</td>
                          {validScenarios.map(s => {
                            const st = s.vehicle === 'jbf' ? tfliStagesFor(s) : null;
                            const le = num(form.lifeExpectancy) || 90;
                            return (
                              <td key={s.id} className="scs-scenario-val">
                                {st && st.paidTotal > 0 ? (
                                  <>
                                    <div className="scs-val-main scs-jbf-funded">{fmtDM(st.paidTotal)}</div>
                                    <div className="scs-jbf-label">
                                      {Math.abs(st.year1 - st.steady) > 1
                                        ? `${fmtD(st.year1)} yr 1 → ${fmtD(st.steady)}/yr to age ${le}`
                                        : `${fmtD(st.steady)}/yr every year to age ${le}`}
                                    </div>
                                  </>
                                ) : <div className="scs-baseline-na">—</div>}
                              </td>
                            );
                          })}
                        </tr>
                      )}
                      <tr className="scs-jbf-row">
                        <td className="scs-metric-label">Your cost to start this year<br/><span className="scs-metric-sub">Year-1 tax after CE coverage</span></td>
                        <td className="scs-baseline-val scs-baseline-na">—</td>
                        {validScenarios.map(s => {
                          const ext = results.v2JBFExtras?.[s.id];
                          return (
                            <td key={s.id} className="scs-scenario-val">
                              {ext ? <div className="scs-val-main scs-jbf-external">{fmtDM(ext.year1ExternalTax || 0)}</div>
                                   : <div className="scs-baseline-na">—</div>}
                            </td>
                          );
                        })}
                      </tr>
                      <tr className="scs-jbf-row">
                        <td className="scs-metric-label">Out-of-Pocket Cost to Convert<br/><span className="scs-metric-sub">total out of pocket, from outside cash</span></td>
                        <td className="scs-baseline-val scs-baseline-na">—</td>
                        {validScenarios.map(s => {
                          const ext = results.v2JBFExtras?.[s.id];
                          return (
                            <td key={s.id} className="scs-scenario-val">
                              {ext ? (
                                <>
                                  <div className="scs-val-main scs-jbf-external">{fmtDM(ext.totalExternalTaxPaid)}</div>
                                  <div className="scs-jbf-label">= {fmtDM(ext.totalExternalTaxPaid)} out of pocket to convert {fmtDM(ext.scheduleTotal || 0)}</div>
                                </>
                              ) : <div className="scs-baseline-na">—</div>}
                            </td>
                          );
                        })}
                      </tr>
                      <tr className="scs-jbf-row">
                        <td className="scs-metric-label">Roth Balance at Legacy<br/><span className="scs-metric-sub">tax-free at end of plan</span></td>
                        <td className="scs-baseline-val scs-baseline-na">—</td>
                        {validScenarios.map(s => {
                          const tbl = results.v2ScenarioTables?.[s.id] ?? results.v2BaselineTable;
                          const lastRow = tbl?.[tbl.length - 1];
                          const rothVal = lastRow?.rothBalance ?? 0;
                          return (
                            <td key={s.id} className="scs-scenario-val">
                              {rothVal > 0 ? (
                                <div className="scs-val-main">{fmtDM(rothVal)}</div>
                              ) : <div className="scs-baseline-na">—</div>}
                            </td>
                          );
                        })}
                      </tr>
                    </>
                  )}
                </tbody>
              </table>
            </div>
          </div>
        );
      })()}
    </div>
  );
}

// ---- ZOOM 1 KPI DASHBOARD ----

function parseMidpoint(str) {
  if (!str || str === '--') return null;
  const clean = str.replace(/[$\s]/g, '');
  const parts = clean.split(/[–-]/);
  const parse = (s) => {
    if (!s) return 0;
    const n = parseFloat(s);
    if (s.toUpperCase().includes('M')) return n * 1_000_000;
    if (s.toUpperCase().includes('K')) return n * 1_000;
    return n;
  };
  const vals = parts.map(parse).filter(v => !isNaN(v));
  if (vals.length === 0) return null;
  return vals.reduce((a, b) => a + b, 0) / vals.length;
}

function formatDelta(n) {
  if (n === null) return '--';
  const abs = Math.abs(n);
  const sign = n >= 0 ? '+' : '-';
  if (abs >= 1_000_000) return `${sign}${(abs / 1_000_000).toFixed(2)}M`;
  if (abs >= 1_000)     return `${sign}${Math.round(abs / 1_000)}k`;
  return `${sign}${Math.round(abs)}`;
}

function getKpi(kpis, ...keys) {
  for (const key of keys) {
    if (kpis[key] !== undefined) return kpis[key];
  }
  return '--';
}

// =====================================================================
// Asset Table — PROSPECT Zoom 1 view
// =====================================================================
function AssetTable({ rows, setRows, onSumsChange }) {
  const ACCOUNT_TYPES_BY_BUCKET = {
    '401k':  ['401(k)', '403(b)', '457(b)', 'TSP', 'SEP-IRA', 'SIMPLE IRA'],
    'ira':   ['Traditional IRA', 'Rollover IRA', 'Inherited IRA'],
    'roth':  ['Roth IRA', 'Roth 401(k)', 'Roth 403(b)', 'Inherited Roth'],
    'other': ['Brokerage', 'Savings', 'Money Market', 'CD', 'Taxable', 'Other'],
  };

  const emitSums = (currentRows) => {
    const sums = { '401k': 0, 'ira': 0, 'roth': 0, 'other': 0 };
    currentRows.forEach(r => {
      const bal = parseFloat(r.balance) || 0;
      if (sums[r.bucket] !== undefined) sums[r.bucket] += bal;
    });
    onSumsChange(sums);
  };

  const updateRow = (idx, patch) => {
    const next = rows.map((r, i) => (i === idx ? { ...r, ...patch } : r));
    if (patch.bucket && patch.bucket !== rows[idx].bucket) {
      next[idx].accountType = ACCOUNT_TYPES_BY_BUCKET[patch.bucket][0];
    }
    setRows(next);
    emitSums(next);
  };

  const addRow = () => {
    const next = [...rows, {
      id: Date.now() + Math.random(),
      owner: 'Client',
      custodian: '',
      bucket: 'ira',
      accountType: 'Traditional IRA',
      balance: '',
      monthlyContrib: '',
    }];
    setRows(next);
    emitSums(next);
  };

  const removeRow = (idx) => {
    const next = rows.filter((_, i) => i !== idx);
    setRows(next);
    emitSums(next);
  };

  return (
    <div className="asset-table-wrap">
      <div className="asset-table-header-row">
        <div className="asset-table-title">INVESTMENT &amp; RETIREMENT ACCOUNTS</div>
        <button className="asset-table-add-btn" onClick={addRow}>+ Add Account</button>
      </div>
      <div className="asset-table-rule" />
      <div className="asset-table">
        <div className="asset-table-head">
          <div>OWNER</div>
          <div>CUSTODIAN</div>
          <div>BUCKET</div>
          <div>ACCOUNT TYPE</div>
          <div>BALANCE</div>
          <div>MONTHLY CONTRIB.</div>
          <div />
        </div>
        {rows.length === 0 && (
          <div className="asset-table-empty">
            No accounts yet. Click <strong>+ Add Account</strong> to begin.
          </div>
        )}
        {rows.map((row, idx) => (
          <div className="asset-table-row" key={row.id}>
            <select
              className="asset-cell-select"
              value={row.owner}
              onChange={e => updateRow(idx, { owner: e.target.value })}
            >
              <option>Client</option>
              <option>Spouse</option>
              <option>Joint</option>
            </select>
            <input
              className="asset-cell-input"
              type="text"
              placeholder="e.g. Schwab"
              value={row.custodian}
              onChange={e => updateRow(idx, { custodian: e.target.value })}
            />
            <div className="asset-cell-radio">
              {['401k', 'ira', 'roth', 'other'].map(b => (
                <label key={b} className={`asset-radio-pill${row.bucket === b ? ' active' : ''}`}>
                  <input
                    type="radio"
                    name={`bucket-${row.id}`}
                    value={b}
                    checked={row.bucket === b}
                    onChange={() => updateRow(idx, { bucket: b })}
                  />
                  <span>{b === '401k' ? '401(k)' : b === 'ira' ? 'IRA' : b === 'roth' ? 'Roth' : 'NQ'}</span>
                </label>
              ))}
            </div>
            <select
              className="asset-cell-select"
              value={row.accountType}
              onChange={e => updateRow(idx, { accountType: e.target.value })}
            >
              {ACCOUNT_TYPES_BY_BUCKET[row.bucket].map(t => (
                <option key={t}>{t}</option>
              ))}
            </select>
            <input
              className="asset-cell-input asset-cell-currency"
              type="text"
              placeholder="$0"
              value={row.balance}
              onChange={e => updateRow(idx, { balance: e.target.value.replace(/[^0-9.]/g, '') })}
            />
            <input
              className="asset-cell-input asset-cell-currency"
              type="text"
              placeholder="$0/mo"
              value={row.monthlyContrib}
              onChange={e => updateRow(idx, { monthlyContrib: e.target.value.replace(/[^0-9.]/g, '') })}
            />
            <button className="asset-cell-remove" onClick={() => removeRow(idx)} title="Remove">✕</button>
          </div>
        ))}
      </div>
    </div>
  );
}

function Zoom1Dashboard({ form, throttledData, upliftedData, setUpliftedData, zoom1View, setZoom1View }) {
  const [generating, setGenerating] = useState(false);
  const [generateError, setGenerateError] = useState(null);
  const [generateSuccess, setGenerateSuccess] = useState(false);

  const clientName = [form.clientFirstName, form.clientLastName].filter(Boolean).join(' ') || 'Client';
  const startingKpis = throttledData?.parameters?.kpis || {};
  const targetKpis   = upliftedData?.parameters?.kpis  || {};
  const kpis = zoom1View === 'starting' ? startingKpis : targetKpis;
  const showTarget = upliftedData != null;

  const riskBand = upliftedData?.parameters?.risk_band || null;
  const rorRaw   = upliftedData?.parameters?.ror;
  const rorPct   = rorRaw != null ? Math.round(parseFloat(rorRaw) * 100) : null;

  const subtitle = zoom1View === 'starting'
    ? 'Conservative 5% baseline — where they are today'
    : `Optimized at ${riskBand || '—'} risk${rorPct != null ? ` — ${rorPct}% return` : ''}`;

  const legacyBaseline  = getKpi(kpis, 'legacyBaseline',     'Legacy Baseline');
  const legacyRoth      = getKpi(kpis, 'legacyRoth',         'Legacy Roth');
  const legacyGap       = getKpi(kpis, 'legacy_gap');
  const ltTaxBaseline   = getKpi(kpis, 'ltTaxBaseline',      'Lifetime Tax Baseline');
  const ltTaxRoth       = getKpi(kpis, 'ltTaxRoth',          'Lifetime Tax Roth');
  const lifetimeTaxGap  = getKpi(kpis, 'lifetime_tax_gap');
  const rmdBaseline     = getKpi(kpis, 'rmdBaseline',        'RMD Baseline');
  const rmdRoth         = getKpi(kpis, 'rmdRoth',            'RMD Roth');
  const riskProfile     = getKpi(startingKpis, 'risk_profile_label', 'risk_band');

  const isTargetView = zoom1View === 'target';

  const legacyGapTarget = (() => {
    const withRoth = parseMidpoint(legacyRoth);
    const baseline = parseMidpoint(legacyBaseline);
    if (withRoth === null || baseline === null) return null;
    return withRoth - baseline;
  })();

  const taxGapTarget = (() => {
    const baseline = parseMidpoint(ltTaxBaseline);
    const withRoth = parseMidpoint(ltTaxRoth);
    if (baseline === null || withRoth === null) return null;
    return baseline - withRoth;
  })();

  const rmdEliminated = rmdRoth !== '--' &&
    (rmdRoth === '$0' || rmdRoth === 0 || String(rmdRoth).replace(/[^0-9.]/g, '') === '0');

  const riskBadgeLabel = isTargetView
    ? (riskBand || 'Unknown Risk')
    : (riskProfile !== '--' ? riskProfile : 'Unknown Risk');

  function kpiVal(v) { return v !== '--' ? v : '—'; }

  async function handleGenerate() {
    const caseId = throttledData?.parameters?.case_id || throttledData?.case_id;
    if (!caseId) return;
    setGenerating(true);
    setGenerateError(null);

    try {
      await fetch('https://eo9spu5h897sxe5.m.pipedream.net', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ stage: 'uplifted', case_id: caseId }),
      });
    } catch (_) {
      // fire-and-forget
    }

    let attempts = 0;
    const maxAttempts = 10;

    const poll = async () => {
      attempts++;
      const { data } = await supabase
        .from('rc_case_versions')
        .select('id, parameters, stage, case_id, outputs')
        .eq('case_id', caseId)
        .eq('stage', 'uplifted')
        .limit(1);

      if (data && data.length > 0) {
        setUpliftedData(data[0]);
        setZoom1View('target');
        setGenerating(false);
        setGenerateSuccess(true);
        setTimeout(() => setGenerateSuccess(false), 3000);
      } else if (attempts >= maxAttempts) {
        setGenerating(false);
        setGenerateError('Generation timed out — try again');
      } else {
        setTimeout(poll, 2000);
      }
    };

    setTimeout(poll, 3000);
  }

  const caseId = throttledData?.parameters?.case_id || throttledData?.case_id;
  const showGenerateBtn = !showTarget && !!caseId;

  return (
    <div className="zoom1-dashboard">
      <div className="zoom1-header">
        <div className="zoom1-client-info">
          <div className="zoom1-client-name">{clientName}</div>
          <div className="zoom1-client-email">{form.email}</div>
        </div>
        <div className="zoom1-toggle-group">
          <div className="zoom1-toggles">
            <button
              className={`zoom1-toggle-btn${zoom1View === 'starting' ? ' zoom1-toggle-active' : ''}`}
              onClick={() => setZoom1View('starting')}
            >Starting Point</button>
            <button
              className={`zoom1-toggle-btn${zoom1View === 'target' ? ' zoom1-toggle-active' : ''}`}
              onClick={() => showTarget && setZoom1View('target')}
              disabled={!showTarget}
            >Target Plan →</button>
          </div>
          <div className="zoom1-subtitle">{subtitle}</div>
        </div>
      </div>

      <div className="zoom1-kpi-grid">
        <div className="zoom1-kpi-card">
          <div className="zoom1-kpi-label">Legacy Without Roth</div>
          <div className="zoom1-kpi-value">{kpiVal(legacyBaseline)}</div>
        </div>
        <div className="zoom1-kpi-card">
          <div className="zoom1-kpi-label">Legacy With Roth</div>
          <div className="zoom1-kpi-value">{kpiVal(legacyRoth)}</div>
        </div>
        <div className="zoom1-kpi-card zoom1-kpi-gold">
          {isTargetView ? (
            <>
              <div className="zoom1-kpi-label">Legacy Uplift</div>
              <div className="zoom1-kpi-value zoom1-kpi-green">{formatDelta(legacyGapTarget)}</div>
              <div className="zoom1-kpi-sub">additional wealth with Roth</div>
            </>
          ) : (
            <>
              <div className="zoom1-kpi-label">Legacy Gap</div>
              <div className="zoom1-kpi-value">{kpiVal(legacyGap)}</div>
            </>
          )}
        </div>

        <div className="zoom1-kpi-card">
          <div className="zoom1-kpi-label">Lifetime Tax Without Roth</div>
          <div className="zoom1-kpi-value">{kpiVal(ltTaxBaseline)}</div>
        </div>
        <div className="zoom1-kpi-card">
          <div className="zoom1-kpi-label">Lifetime Tax With Roth</div>
          <div className="zoom1-kpi-value">{kpiVal(ltTaxRoth)}</div>
        </div>
        <div className="zoom1-kpi-card zoom1-kpi-gold">
          {isTargetView ? (
            <>
              <div className="zoom1-kpi-label">Tax Savings</div>
              <div className="zoom1-kpi-value zoom1-kpi-green">{formatDelta(taxGapTarget)}</div>
              <div className="zoom1-kpi-sub">lifetime tax reduction</div>
            </>
          ) : (
            <>
              <div className="zoom1-kpi-label">Tax Gap</div>
              <div className="zoom1-kpi-value">{kpiVal(lifetimeTaxGap)}</div>
            </>
          )}
        </div>

        <div className="zoom1-kpi-card">
          <div className="zoom1-kpi-label">RMD at 73 Without Roth</div>
          <div className="zoom1-kpi-value">{kpiVal(rmdBaseline)}</div>
        </div>
        <div className="zoom1-kpi-card">
          <div className="zoom1-kpi-label">RMD With Roth</div>
          {rmdEliminated
            ? <div className="zoom1-kpi-value zoom1-kpi-eliminated">ELIMINATED</div>
            : <div className="zoom1-kpi-value">{kpiVal(rmdRoth)}</div>
          }
        </div>
        <div className="zoom1-kpi-card zoom1-kpi-gold">
          <div className="zoom1-kpi-label">Risk Profile</div>
          <div className="zoom1-kpi-value">{kpiVal(riskProfile)}</div>
        </div>
      </div>

      {showGenerateBtn && (
        <div className="zoom1-generate-wrap">
          {generateSuccess && (
            <div className="zoom1-generate-banner">✓ Target Plan generated</div>
          )}
          {generateError && (
            <div className="zoom1-generate-error">{generateError}</div>
          )}
          <button
            className="zoom1-generate-btn"
            onClick={handleGenerate}
            disabled={generating}
          >
            {generating ? 'Generating… please wait' : '⚡ Generate Target Plan'}
          </button>
        </div>
      )}

      <div className="zoom1-footer">
        <div className="zoom1-footer-text">Complete the profile below to build their full retirement plan →</div>
        <div className="zoom1-risk-badge">{riskBadgeLabel}</div>
      </div>
    </div>
  );
}

// ---- MAIN APP ----

export default
function App() {
  // The app is deployed publicly and holds client cases, and the only key a
  // browser can hold is the public anon key. So access is decided by a session
  // rather than by knowing the URL — a URL read off a shared screen lands on the
  // sign-in box, not on somebody's plan.
  const [session, setSession] = useState(undefined);   // undefined = still checking
  useEffect(() => {
    supabase.auth.getSession().then(({ data }) => setSession(data.session ?? null));
    const { data: sub } = supabase.auth.onAuthStateChange((_e, next) => setSession(next));
    return () => sub.subscription.unsubscribe();
  }, []);

  const [form, setForm] = useState(INITIAL_STATE);
  const [results, setResults] = useState(null);
  const [liveScenarioId, setLiveScenarioId] = useState(null);
  const [printScenarioIds, setPrintScenarioIds] = useState([]);
  const resultsRef = useRef(null);

  const set = useCallback((key) => (val) => setForm(prev => ({ ...prev, [key]: val })), []);

  // Survivor View is a "view" toggle (label: "switch to Single filing at survivor age"),
  // so it must re-project the plan live. The calc is otherwise button-driven, so without
  // this the toggle only moves the cosmetic "S" badge while the tax numbers stay stale.
  // Recompute only once a plan already exists; reads current `form` (no stale closure).
  // Intentionally narrow deps — adding `results` would loop (setResults → new ref → re-run).
  // Live-recompute signature: changes whenever a per-year conversion schedule is
  // edited, so editing any year re-runs the whole projection downstream (once a
  // plan has been calculated). Survivor toggles keep their existing live behavior.
  const scheduleSig = form.rothScenarios
    .map(s => JSON.stringify(s.conversionSchedule ?? null))
    .join('|');
  useEffect(() => {
    if (results) setResults(calculatePlan(form));
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [form.survivorEnabled, form.survivorAge, form.showAdvisoryFee, scheduleSig]);

  const scenarioId = useRef(10);

  function addScenario() {
    const id = scenarioId.current++;
    const autoFill = calcRMDAutoFill(form, 'M');
    const autoEnd = autoFill ? autoFill.conversionWindow : 3;
    setForm(prev => ({
      ...prev,
      rothScenarios: [...prev.rothScenarios, {
        // Conversion amounts start blank — the advisor sets the per-year schedule (the
        // source of truth); we no longer pre-fill an IRA-draining amortized amount.
        id, name: '', conversionAmount: '', startYear: 1, endYear: autoEnd,
        taxMethod: 'cash', vehicle: 'jbf', ror: 7,
        // CE portfolio tier (revised Aug 6 2026): new scenarios start on the T2 middle
        // tier (Income Blend, 10%) — the Zoom 1 quick-build convention, shown as
        // provisional until the Risk Score picks the band. Selector unlocks after
        // Risk Score + Zoom 2 booked. Loaded legacy scenarios have no tier → 14/11/3.
        tier: 'T2', tierOverridden: false,
        bucketAGrossYield: 14, bucketADistRate: 11,
        annuityBonusPct: 18, annuityGrowthRate: 4,
        year1Amount: '', year1ConversionAmount: '', riskTier: 'M', year1TaxMethod: 'cash',
        years2PlusTaxMethod: 'jbf_distribution',
        conversionSchedule: [],
        ssClaimingAge: parseFloat(form.clientSSClaimingAge) || 67,
        spouseSSClaimingAge: parseFloat(form.spouseSSClaimingAge) || 67,
        ceTargetSize: '',
        unconvertedIraGrowthRate: '',
        snPayoff: false, snBaseRate: 7, snTenor: 7, snMatLag: 2, snNoteStartOffset: 2,
        // TFLI Bucket (third sleeve): off by default; per-year allocations by age.
        tfliEnabled: false, tfliSchedule: [], tfliYield: 8,
        clientGoals: '',
        engineSeedSource: 'nonqual',
        enableSecondNote: false, tfnManualNote1: '', tfnManualNote2: '', tfn2CreditRate: '',
        rmdAutoCalc: false,
        rmdStartAge: autoFill?.rmdStartAge ?? null,
      }],
    }));
  }

  function removeScenario(index) {
    // If the removed scenario was the LIVE one, clear the flag so the narrative falls
    // back to the default instead of anchoring on a now-deleted scenario.
    const removedId = form.rothScenarios[index]?.id;
    setForm(prev => ({
      ...prev,
      rothScenarios: prev.rothScenarios.filter((_, i) => i !== index),
    }));
    setLiveScenarioId(prev => (prev === removedId ? null : prev));
  }

  function updateScenario(index, updated) {
    setForm(prev => ({
      ...prev,
      rothScenarios: prev.rothScenarios.map((s, i) => i === index ? updated : s),
    }));
  }

  function handlePrefill() {
    setForm({
      clientFirstName: 'Robert',
      clientLastName: 'Johnson',
      email: 'robert.johnson@example.com',
      state: 'NC',
      clientDOB: '1965-05-15',
      retirementAge: 67,
      includeSpouse: true,
      spouseFirstName: 'Mary',
      spouseLastName: 'Johnson',
      spouseDOB: '1967-09-22',
      balance401k: '450000',
      balanceIRA: '125000',
      balanceRothIRA: '45000',
      balanceOther: '80000',
      monthlyContribution: '2500',
      clientSSMonthly: '2800',
      clientSSClaimingAge: 67,
      spouseSSMonthly: '1400',
      spouseSSClaimingAge: 67,
      pensionMonthly: '0',
      monthlyExpenses: '9500',
      growthRate: 7,
      currentTaxRate: '24',
      retirementTaxRate: '22',
      ssCOLARate: 1.8,
      rothScenarios: [
        { id: 1, name: 'Scenario 1 — JBF Two-Bucket', conversionAmount: '50000', startYear: 1, endYear: 3, taxMethod: 'cash', vehicle: 'jbf', ror: 7, bucketAGrossYield: 14, bucketADistRate: 11, annuityBonusPct: 18, annuityGrowthRate: 4, year1Amount: '150000', riskTier: 'M', year1TaxMethod: 'cash' },
        { id: 2, name: 'Scenario 2 — JBF High Growth', conversionAmount: '50000', startYear: 1, endYear: 5, taxMethod: 'cash', vehicle: 'jbf', ror: 9, bucketAGrossYield: 14, bucketADistRate: 11, annuityBonusPct: 18, annuityGrowthRate: 4, year1Amount: '150000', riskTier: 'H', year1TaxMethod: 'cash' },
        { id: 3, name: 'Scenario 3 — JBF Conservative', conversionAmount: '40000', startYear: 1, endYear: 4, taxMethod: 'cash', vehicle: 'jbf', ror: 6, bucketAGrossYield: 14, bucketADistRate: 11, annuityBonusPct: 18, annuityGrowthRate: 4, year1Amount: '100000', riskTier: 'L', year1TaxMethod: 'internal' },
      ],
      conversionGoal: 'full_roth',
      customTarget: '',
    });
    setResults(null);
  }

  const [saving, setSaving] = useState(false);
  const [loadedPlanId, setLoadedPlanId] = useState(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [searching, setSearching] = useState(false);
  const [loadedMsg, setLoadedMsg] = useState(null);
  const [throttledData, setThrottledData] = useState(null);
  const [upliftedData, setUpliftedData] = useState(null);
  const [zoom1View, setZoom1View] = useState('starting');
  const [loadedRecordType, setLoadedRecordType] = useState(null);
  const [riskModalOpen, setRiskModalOpen] = useState(false);
  const [bookModalOpen, setBookModalOpen] = useState(false);   // embedded Zoom 2 calendar
  const [confirmOpen, setConfirmOpen] = useState(false);       // end-of-Zoom-1 confirm screen
  const [assetRows, setAssetRows] = useState([]);
  const [whySelected, setWhySelected] = useState([]);
  const [loadedProspectId, setLoadedProspectId] = useState(null);
  const [zoom2Date, setZoom2Date] = useState('');
  const [zoom2Time, setZoom2Time] = useState('');
  const [zoom2Saving, setZoom2Saving] = useState(false);
  const [zoom2Saved, setZoom2Saved] = useState(null);
  const [planReady, setPlanReady] = useState(false);
  const [riskCompleted, setRiskCompleted] = useState(false);
  const [prospectBanner, setProspectBanner] = useState(null);
  const [showCEReport, setShowCEReport] = useState(false);

  async function handleSaveClient() {
    setSaving(true);

    function formatCurrency(val) {
      if (val == null) return null;
      const n = Math.round(val);
      return '$' + n.toLocaleString();
    }

    const payload = {
        first_name: form.clientFirstName,
        last_name: form.clientLastName,
        email: form.email,
        state: form.state,
        plan_data: {
          kpis: {
            rmdRoth: '$0',
            ltTaxRoth: results ? formatCurrency(results.totalTaxWithRoth || 0) : null,
            legacyRoth: results ? formatCurrency(results.portfolioAtRetirement + (results.primarySleeveSize || 0)) : null,
            legacy_gap: results ? formatCurrency(results.primarySleeveSize || 0) : null,
            rmdBaseline: results ? formatCurrency(results.estimatedRMD || 0) : null,
            depletion_age: results ? results.depletionAge : null,
            ltTaxBaseline: results ? formatCurrency(results.totalTaxWithoutRoth || 0) : null,
            legacyBaseline: results ? formatCurrency(results.portfolioAtRetirement || 0) : null,
            lifetime_tax_gap: results ? formatCurrency(results.totalTaxSavings || 0) : null,
            monthly_income_gap: results ? formatCurrency(results.monthlyGap || 0) : null,
            risk_profile_label: 'Moderate',
            required_sleeve_size: results ? formatCurrency(results.primarySleeveSize || 0) : null,
            portfolio_at_retirement: results ? formatCurrency(results.portfolioAtRetirement || 0) : null,
          },
          jbf_planner: {
            assets: {
              retirement401k: form.balance401k,
              iraBalance: form.balanceIRA,
              rothIRABalance: form.balanceRothIRA,
              otherInvestments: form.balanceOther,
              annuityBalance: form.annuityBalance,
              annuityROR: form.annuityROR,
              monthlyContribution: form.monthlyContribution,
              monthlyRothContribution: form.monthlyRothContribution,
              monthlyOtherContribution: form.monthlyOtherContribution,
            },
            income: {
              clientSS: form.clientSSMonthly,
              clientSSAge: form.clientSSClaimingAge,
              clientSSBasisAge: form.clientSSBasisAge,
              spouseSS: form.spouseSSMonthly,
              spouseSSAge: form.spouseSSClaimingAge,
              spouseSSBasisAge: form.spouseSSBasisAge,
              pensionIncome: form.pensionMonthly,
              preRetirementIncome: form.preRetirementIncome,
              incomeRows: form.incomeRows,
            },
            expenses: { monthlyExpenses: form.monthlyExpenses },
            assumptions: {
              growthRate: form.growthRate,
              ssCOLARate: form.ssCOLARate,
              currentTaxRate: form.currentTaxRate,
              retirementTaxRate: form.retirementTaxRate,
              lifeExpectancy: form.lifeExpectancy,
              // Global assumptions that previously never persisted (filing status was
              // silently reverting to MFJ on load). Older saved plans lack these keys
              // and fall back to the INITIAL_STATE defaults on load — unchanged behavior.
              filingStatus: form.filingStatus,
              dependents: form.dependents,
              inflationRate: form.inflationRate,
              stateFlatRate: form.stateFlatRate,
              year1CEMonths: form.year1CEMonths,
              ltfiYield: form.ltfiYield,
              goalLean: form.goalLean,
              showAdvisoryFee: form.showAdvisoryFee || false,
              showRothIncome: form.showRothIncome || false,
              showBaseTaxes: form.showBaseTaxes || false,
            },
            profile: {
              clientDOB: form.clientDOB,
              retirementAge: form.retirementAge,
              currentlyRetired: form.currentlyRetired || false,
              includeSpouse: form.includeSpouse,
              spouseFirstName: form.spouseFirstName,
              spouseLastName: form.spouseLastName,
              spouseDOB: form.spouseDOB,
              survivorEnabled: form.survivorEnabled || false,
              survivorAge: form.survivorAge,
            },
            scenarios: form.rothScenarios,
            // Persist which scenario is LIVE by POSITION. Load reassigns scenario.id to the
            // array index, so the index — not the runtime id — is the stable key that lets the
            // PrintPlan narrative anchor survive save/reload. null when nothing is flagged LIVE;
            // older plans lack this key entirely and fall back to the default on load.
            liveScenarioIndex: (() => {
              const idx = form.rothScenarios.findIndex(s => s.id === liveScenarioId);
              return idx >= 0 ? idx : null;
            })(),
            conversionGoal: form.conversionGoal,
            customTarget: form.customTarget,
            riskScore: form.riskScore || null,
            zoom2Date: form.zoom2Date || null,
          },
        },
    };

    console.log('[save] rothScenarios being saved:', JSON.parse(JSON.stringify(form.rothScenarios)));
    console.log('[save] scenarios count:', form.rothScenarios.length);

    let savedId = null;
    let saveError = null;

    if (loadedPlanId) {
      const upsertPayload = { id: loadedPlanId, ...payload };
      const { data: upserted, error: upsertErr } = await supabase
        .from('jbf_cases')
        .upsert([upsertPayload], { onConflict: 'id' })
        .select('id')
        .single();

      if (!upsertErr) {
        savedId = upserted?.id ?? loadedPlanId;
        console.log('[save] upsert OK, id:', savedId);
      } else {
        const is401 = upsertErr.code === '42501' || upsertErr.message?.includes('401') || upsertErr.message?.includes('permission') || upsertErr.message?.includes('policy');
        console.warn('[save] upsert failed:', upsertErr.message, '— is permission error:', is401);
        if (is401) {
          console.warn('[save] falling back to INSERT (fix RLS to enable UPDATE)');
          const { data: inserted, error: insertErr } = await supabase
            .from('jbf_cases')
            .insert([payload])
            .select('id')
            .single();
          saveError = insertErr;
          if (!insertErr && inserted?.id) {
            savedId = inserted.id;
            console.log('[save] fallback INSERT OK, new id:', savedId);
          }
        } else {
          saveError = upsertErr;
        }
      }
    } else {
      const { data: inserted, error: insertErr } = await supabase
        .from('jbf_cases')
        .insert([payload])
        .select('id')
        .single();
      saveError = insertErr;
      if (!insertErr && inserted?.id) savedId = inserted.id;
    }

    if (savedId) setLoadedPlanId(savedId);
    setSaving(false);

    if (saveError) {
      alert('Save failed: ' + saveError.message);
    } else {
      alert('Plan saved successfully!');
    }
  }

  function handleCalculate() {
    const r = calculatePlan(form);
    setResults(r);
    const defaultPrintId = liveScenarioId != null
      ? liveScenarioId
      : (form.rothScenarios.length > 0 ? form.rothScenarios[0].id : null);
    setPrintScenarioIds(defaultPrintId != null ? [defaultPrintId] : []);
    setLoadedMsg(null);
    setTimeout(() => {
      resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }, 50);
  }

  async function handleSearchClient() {
    const raw = searchTerm.trim();
    if (!raw) return;
    const term = raw.toLowerCase();
    setLoadedMsg(null);
    setForm(INITIAL_STATE);
    setResults(null);
    setSearching(true);
    setSearchResults([]);

    const likeFilter = `first_name.ilike.%${raw}%,last_name.ilike.%${raw}%,email.ilike.%${raw}%`;

    const words = raw.trim().split(/\s+/);
    const prospectFilter = words.length >= 2
      ? `${likeFilter},and(first_name.ilike.%${words[0]}%,last_name.ilike.%${words.slice(1).join(' ')}%)`
      : likeFilter;

    const [
      { data: planData, error: planErr },
      { data: prospectData, error: prospectErr },
    ] = await Promise.all([
      supabase
        .from('jbf_cases')
        .select('id, created_at, first_name, last_name, email, state, plan_data')
        .or(prospectFilter)
        .order('created_at', { ascending: false }),
      supabase
        .from('rc_intake_forms')
        .select('*')
        .or(prospectFilter)
        .order('submitted_at', { ascending: false }),
    ]);

    if (planErr)     console.warn('[search] jbf_cases error:', planErr);
    if (prospectErr) console.warn('[search] rc_intake_forms error:', prospectErr);

    const seenPlanEmails = new Set();
    const planRecords = (planData || [])
      .map(r => ({ ...r, recordType: 'plan' }))
      .filter(r => {
        const key = (r.email || '').toLowerCase() || String(r.id);
        if (seenPlanEmails.has(key)) return false;
        seenPlanEmails.add(key);
        return true;
      });

    const planEmails = new Set(planRecords.map(r => (r.email || '').toLowerCase()));
    const planNames  = new Set(planRecords.map(r =>
      ((r.first_name || '') + ' ' + (r.last_name || '')).toLowerCase().trim()
    ));

    const seenProspectEmails = new Set(
      planRecords.map(r => (r.email || '').toLowerCase()).filter(Boolean)
    );
    const prospectRecords = (prospectData || [])
      .map(r => ({ ...r, recordType: 'lead' }))
      .filter(r => {
        const email = (r.email || '').toLowerCase();
        const name  = ((r.first_name || '') + ' ' + (r.last_name || '')).toLowerCase().trim();
        if (planEmails.has(email) || planNames.has(name)) return false;
        const key = email || String(r.id);
        if (seenProspectEmails.has(key)) return false;
        seenProspectEmails.add(key);
        return true;
      });

    const merged = [...planRecords, ...prospectRecords]
      .sort((a, b) => {
        const dateA = new Date(a.submitted_at || a.created_at);
        const dateB = new Date(b.submitted_at || b.created_at);
        return dateB - dateA;
      });

    setSearchResults(merged);
    setSearching(false);
  }

  async function fetchZoom1KPIs(email) {
    if (!email) return;

    const { data: allRCRows } = await supabase
      .from('rc_case_versions')
      .select('id, case_id, parameters, stage, outputs')
      .order('created_at', { ascending: false })
      .limit(300);

    console.log('RC rows fetched:', allRCRows?.length);
    console.log('Searching for email:', email);

    const throttled = (allRCRows || []).find(row => {
      const rowEmail = (row.parameters?.email || '').toLowerCase();
      console.log('Checking row email:', rowEmail, 'stage:', row.stage);
      return rowEmail === email.toLowerCase() && row.stage === 'throttled';
    });

    console.log('Throttled result:', throttled);
    console.log('Throttled kpis object:', throttled?.parameters?.kpis);
    setThrottledData(throttled || null);

    if (throttled) {
      const caseId = throttled.parameters?.case_id || throttled.case_id;
      if (caseId) {
        const uplifted = (allRCRows || []).find(row =>
          (row.case_id === caseId || row.parameters?.case_id === caseId) &&
          row.stage === 'uplifted'
        );
        setUpliftedData(uplifted || null);
      }
    }
  }

  async function handleLoadClient(row) {
    const name = `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'Client';

    if (row.recordType === 'plan') {
      console.log('[load] raw plan_data:', row.plan_data);
      console.log('[load] jbf_planner.scenarios:', row.plan_data?.jbf_planner?.scenarios);
      const jbf = row.plan_data?.jbf_planner || {};
      const assets = jbf.assets || {};
      const income = jbf.income || {};
      const expenses = jbf.expenses || {};
      const assumptions = jbf.assumptions || {};
      const profile = jbf.profile || {};
      const loadedForm = {
        ...INITIAL_STATE,
        clientFirstName: row.first_name || '',
        clientLastName: row.last_name || '',
        email: row.email || '',
        state: row.state || '',
        clientDOB: profile.clientDOB || '',
        currentlyRetired: profile.currentlyRetired || false,
        retirementAge: (profile.currentlyRetired && profile.clientDOB)
          ? String(dobToAge(profile.clientDOB) ?? profile.retirementAge ?? 65)
          : (profile.retirementAge || 65),
        includeSpouse: profile.includeSpouse || false,
        spouseFirstName: profile.spouseFirstName || '',
        spouseLastName: profile.spouseLastName || '',
        spouseDOB: profile.spouseDOB || '',
        survivorAge: profile.survivorAge !== undefined ? profile.survivorAge : 80,
        balance401k: assets.retirement401k || '',
        balanceIRA: assets.iraBalance || '',
        balanceRothIRA: assets.rothIRABalance || '',
        balanceOther: assets.otherInvestments || '',
        annuityBalance: assets.annuityBalance || '',
        annuityROR: assets.annuityROR !== undefined ? assets.annuityROR : 4,
        monthlyContribution: assets.monthlyContribution || '',
        monthlyRothContribution: assets.monthlyRothContribution || assets.monthlyContribution || '',
        monthlyOtherContribution: assets.monthlyOtherContribution || '',
        clientSSMonthly: income.clientSS || '',
        clientSSClaimingAge: income.clientSSAge || 67,
        clientSSBasisAge: income.clientSSBasisAge ?? income.clientSSAge ?? 67,
        spouseSSMonthly: income.spouseSS || '',
        spouseSSClaimingAge: income.spouseSSAge || 67,
        spouseSSBasisAge: income.spouseSSBasisAge ?? income.spouseSSAge ?? 67,
        pensionMonthly: income.pensionIncome || '',
        preRetirementIncome: income.preRetirementIncome || '',
        incomeRows: Array.isArray(income.incomeRows) ? income.incomeRows : undefined,
        monthlyExpenses: expenses.monthlyExpenses || '',
        growthRate: assumptions.growthRate !== undefined ? assumptions.growthRate : 7,
        currentTaxRate: assumptions.currentTaxRate || '',
        retirementTaxRate: assumptions.retirementTaxRate || '',
        ssCOLARate: assumptions.ssCOLARate !== undefined ? assumptions.ssCOLARate : 1.8,
        lifeExpectancy: assumptions.lifeExpectancy !== undefined ? assumptions.lifeExpectancy : 90,
        // Restore the global assumptions that used to fall back to INITIAL_STATE defaults.
        // ?? (not ||) so legitimate falsy values (dependents 0, stateFlatRate '') round-trip.
        filingStatus: assumptions.filingStatus ?? 'mfj',
        dependents: assumptions.dependents ?? 0,
        inflationRate: assumptions.inflationRate ?? 2.75,
        stateFlatRate: assumptions.stateFlatRate ?? '',
        year1CEMonths: assumptions.year1CEMonths ?? 12,
        ltfiYield: assumptions.ltfiYield ?? 6,
        goalLean: assumptions.goalLean ?? 'legacy',
        showAdvisoryFee: assumptions.showAdvisoryFee ?? false,
        showRothIncome: assumptions.showRothIncome ?? false,
        showBaseTaxes: assumptions.showBaseTaxes ?? false,
        survivorEnabled: profile.survivorEnabled ?? false,
        rothScenarios: (jbf.scenarios || []).map((s, i) => ({
          ...s,
          id: i,
          name: (s.name || '').replace(/JBF Income Engine/g, 'JBF Convert Engine'),
          engineSeedSource:      s.engineSeedSource      ?? 'nonqual',
          year1ConversionAmount: s.year1ConversionAmount ?? s.conversionAmount ?? '',
        })),
        conversionGoal: jbf.conversionGoal || 'full_roth',
        customTarget: jbf.customTarget || '',
        riskScore: jbf.riskScore || null,
        zoom2Date: jbf.zoom2Date || '',
      };
      setForm(migrateIncomeRows(loadedForm));
      setLoadedPlanId(row.id);
      scenarioId.current = (jbf.scenarios || []).length;
      // Restore the LIVE scenario from its persisted position. Loaded scenarios get
      // id = array index (above), so the index maps straight back to the restored id.
      // Out-of-range (scenario since deleted) or missing (older plan) → unset, so the
      // narrative falls back to the default rather than pointing at a stale scenario.
      const liveIdx = jbf.liveScenarioIndex;
      setLiveScenarioId(
        Number.isInteger(liveIdx) && liveIdx >= 0 && liveIdx < loadedForm.rothScenarios.length
          ? loadedForm.rothScenarios[liveIdx].id
          : null
      );
      setSearchResults([]);
      setSearchTerm('');
      const r = calculatePlan(loadedForm);
      setResults(r);
      const firstId = loadedForm.rothScenarios.length > 0 ? loadedForm.rothScenarios[0].id : null;
      setPrintScenarioIds(firstId != null ? [firstId] : []);
      setLoadedRecordType('plan');
      setThrottledData(null);
      setUpliftedData(null);
      setProspectBanner(null);
      setLoadedMsg({ text: `✓ Client loaded: ${name}`, variant: 'success' });
      setTimeout(() => setLoadedMsg(null), 3000);
      setTimeout(() => {
        resultsRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
    } else if (row.recordType === 'lead') {
      const rp = row.raw_payload || {};

      const rpSumAssets = (assets, keywords) =>
        (Array.isArray(assets) ? assets : [])
          .filter(a => keywords.some(kw =>
            (a.type || a.custodian || a.account_type || '').toLowerCase().includes(kw)
          ))
          .reduce((sum, a) => sum + (parseFloat(a.balance || a.value || a.amount || 0) || 0), 0);

      let clientDOB = '';
      const prospectAge = parseInt(row.age);
      if (!isNaN(prospectAge)) {
        const d = new Date();
        d.setFullYear(d.getFullYear() - prospectAge);
        clientDOB = d.toISOString().split('T')[0];
      }

      let spouseDOB = '';
      const spouseAge = parseInt(rp.spouse_age);
      if (!isNaN(spouseAge)) {
        const d = new Date();
        d.setFullYear(d.getFullYear() - spouseAge);
        spouseDOB = d.toISOString().split('T')[0];
      }

      let filingStatus = (row.married === true || row.married === 'true') ? 'mfj' : 'single';
      if (rp.filing_status) filingStatus = rp.filing_status;

      let clientSSMonthly = '';
      let spouseSSMonthly = '';
      let pensionMonthly = '';
      const incomeSrc = rp.income_sources || {};
      if (Array.isArray(incomeSrc)) {
        for (const src of incomeSrc) {
          const kind = (src.type || src.kind || '').toLowerCase();
          const amt = String(src.monthly_amount || src.amount || '');
          if (kind.includes('ss') || kind.includes('social_security') || kind.includes('social security')) {
            if (!clientSSMonthly) clientSSMonthly = amt;
            else spouseSSMonthly = amt;
          } else if (kind.includes('pension')) {
            pensionMonthly = amt;
          }
        }
      } else {
        clientSSMonthly  = String(incomeSrc.ss_client      || '');
        spouseSSMonthly  = String(incomeSrc.ss_spouse      || '');
        pensionMonthly   = String(incomeSrc.pension_client || '');
      }

      const prospectForm = {
        ...INITIAL_STATE,
        clientFirstName: row.first_name || '',
        clientLastName: row.last_name || '',
        email: row.email || '',
        state: row.state || '',
        clientDOB,
        spouseDOB,
        filingStatus,
        currentlyRetired: row.retired === true || row.retired === 'true',
        retirementAge: (row.retired === true || row.retired === 'true') && clientDOB
          ? String(dobToAge(clientDOB) ?? 65)
          : 65,
        includeSpouse: filingStatus === 'mfj',
        preRetirementIncome: String(parseFloat(row.est_taxable_income_2026) || ''),
        balanceIRA: String(parseFloat(rp.ira_balance) || rpSumAssets(rp.assets, ['ira', '401k', '401(k)', '403b', '403(b)', 'rollover', 'inherited']) || ''),
        balanceOther: String(parseFloat(rp.nonqual_balance) || rpSumAssets(rp.assets, ['brokerage', 'savings', 'non-qual', 'nonqual', 'money market', 'taxable']) || ''),
        balanceRothIRA: String(parseFloat(rp.existing_roth_balance) || rpSumAssets(rp.assets, ['roth']) || ''),
        clientSSMonthly,
        spouseSSMonthly,
        pensionMonthly,
      };
      setForm(migrateIncomeRows(prospectForm));
      setSearchResults([]);
      setSearchTerm('');
      setResults(null);
      setLoadedRecordType('lead');
      setLoadedProspectId(row.id);
      setZoom2Date(rp.zoom2_date || '');
      setZoom2Time(rp.zoom2_time || '');
      setZoom2Saved(null);
      setThrottledData(null);
      setUpliftedData(null);
      setZoom1View('starting');
      setProspectBanner(
        (rp.zoom2_date || rp.zoom2_time)
          ? { date: rp.zoom2_date || '', time: rp.zoom2_time || '', name }
          : null
      );
      setLoadedMsg({
        text: `✓ Prospect loaded: ${name} — review and complete the profile before running plan`,
        variant: 'warn',
      });
      setTimeout(() => setLoadedMsg(null), 5000);
      fetchZoom1KPIs(row.email);
    } else {
      const kpis = row.parameters?.kpis || {};
      let clientDOB = '';
      const age = parseInt(kpis.age);
      if (!isNaN(age)) {
        const d = new Date();
        d.setFullYear(d.getFullYear() - age);
        clientDOB = d.toISOString().split('T')[0];
      }
      let monthlyExpenses = '';
      const leadIncome = parseFloat(kpis.income);
      if (!isNaN(leadIncome) && leadIncome > 0) {
        monthlyExpenses = String(Math.round(leadIncome / 12 * 0.8));
      }
      const loadedForm = {
        ...INITIAL_STATE,
        clientFirstName: row.first_name || '',
        clientLastName: row.last_name || '',
        email: row.email || '',
        state: row.state || '',
        clientDOB,
        includeSpouse: kpis.married === true || kpis.married === 'true',
        monthlyExpenses,
      };
      setForm(migrateIncomeRows(loadedForm));
      setSearchResults([]);
      setSearchTerm('');
      setResults(null);
      setLoadedRecordType('lead');
      setThrottledData(null);
      setUpliftedData(null);
      setZoom1View('starting');
      setProspectBanner(null);
      setLoadedMsg({
        text: `⚠ Funnel Lead: ${name} — complete the profile to build their retirement plan`,
        variant: 'warn',
      });

      fetchZoom1KPIs(row.email);
    }
  }

  function handleNewClient() {
    setForm(INITIAL_STATE);
    setResults(null);
    setLiveScenarioId(null);
    setSearchTerm('');
    setSearchResults([]);
    setLoadedMsg(null);
    setThrottledData(null);
    setUpliftedData(null);
    setLoadedRecordType(null);
    setProspectBanner(null);
    setZoom1View('starting');
    setLoadedPlanId(null);
    setLoadedProspectId(null);
    setZoom2Date('');
    setZoom2Time('');
    setZoom2Saved(null);
    setPlanReady(false);
  }

  // Placed below every hook in this component, so the early return can never
  // change the order hooks are called in.
  if (session === undefined) return null;          // don't flash a login screen
  if (session === null) return <SignIn />;

  return (
    <div className="app">
      <header className="app-header">
        <div className="header-inner">
          <div className="header-brand">
            <div className="header-logo">JBF</div>
            <div className="header-titles">
              <h1>J Bradin Financial</h1>
              <p>Retirement Planner</p>
            </div>
          </div>
          <button type="button" className="header-signout"
                  onClick={() => supabase.auth.signOut()}>Sign out</button>
        </div>
      </header>

      <div className="form-wrapper">
        <div className="form-grid">

          {/* CLIENT SEARCH CARD */}
          <div className="client-search-card">
            <div className="client-search-header">
              <div className="client-search-title">Load Existing Client</div>
              <button className="new-client-btn" onClick={handleNewClient}>+ New Client</button>
            </div>
            <div className="client-search-row">
              <input
                className="client-search-input"
                type="text"
                placeholder="Search by name or email..."
                value={searchTerm}
                onChange={e => setSearchTerm(e.target.value)}
                onKeyDown={e => e.key === 'Enter' && handleSearchClient()}
              />
              <button className="client-search-btn" onClick={handleSearchClient} disabled={searching}>
                {searching ? 'Searching…' : 'Search'}
              </button>
            </div>
            {loadedMsg && !(loadedRecordType === 'lead' && throttledData !== null) && (
              <div className={`client-loaded-msg${loadedMsg.variant === 'warn' ? ' client-loaded-warn' : ''}`}>
                {loadedMsg.text}
              </div>
            )}
            {searchResults.length > 0 && (
              <div className="client-search-results">
                {searchResults.map(row => {
                  const name = `${row.first_name || ''} ${row.last_name || ''}`.trim() || 'Unknown';
                  const date = new Date(row.created_at).toLocaleDateString('en-US', { year: 'numeric', month: 'short', day: 'numeric' });
                  const rt = row.recordType;
                  const badgeClass = rt === 'plan' ? 'badge-plan' : rt === 'prospect' ? 'badge-prospect' : 'badge-lead';
                  const badgeLabel = rt === 'plan' ? '🟢 PLAN' : rt === 'prospect' ? '🟡 PROSPECT' : '🔵 LEAD';
                  return (
                    <button key={row.id} className="client-result-item" onClick={() => handleLoadClient(row)}>
                      <span className={`client-result-badge ${badgeClass}`}>{badgeLabel}</span>
                      <span className="client-result-name">{name}</span>
                      <span className="client-result-email">{row.email || '—'}</span>
                      <span className="client-result-date">{date}</span>
                    </button>
                  );
                })}
              </div>
            )}
            {searchResults.length === 0 && searchTerm && !searching && (
              <div className="client-search-empty">No clients found matching "{searchTerm}"</div>
            )}
          </div>

          {loadedRecordType === 'lead' && throttledData !== null && (
            <Zoom1Dashboard
              form={form}
              throttledData={throttledData}
              upliftedData={upliftedData}
              setUpliftedData={setUpliftedData}
              zoom1View={zoom1View}
              setZoom1View={setZoom1View}
            />
          )}

          {prospectBanner && (
            <div className="prospect-info-banner">
              <strong>Zoom 2 Appointment — {prospectBanner.name}:</strong>{' '}
              {prospectBanner.date}{prospectBanner.time ? ` at ${prospectBanner.time}` : ''}
              <button className="prospect-banner-dismiss" onClick={() => setProspectBanner(null)}>✕</button>
            </div>
          )}

          <SectionCard title="Section 1 — Client Profile">
            <FieldRow>
              <TextField label="First Name" value={form.clientFirstName} onChange={set('clientFirstName')} placeholder="Jane" />
              <TextField label="Last Name" value={form.clientLastName} onChange={set('clientLastName')} placeholder="Smith" />
            </FieldRow>
            <FieldRow>
              <EmailField label="Email" value={form.email} onChange={set('email')} placeholder="jane.smith@example.com" />
              <TextField label="State" value={form.state} onChange={set('state')} placeholder="FL" />
            </FieldRow>
            <FieldRow>
              <DateField label="Date of Birth" value={form.clientDOB} onChange={set('clientDOB')} />
              {!form.currentlyRetired && <AgeField label="Planned Retirement Age" value={form.retirementAge} onChange={set('retirementAge')} />}
            </FieldRow>
            <div className="spouse-toggle-row">
              <label className="toggle-label">
                <input type="checkbox" checked={!!form.currentlyRetired} onChange={e => { set('currentlyRetired')(e.target.checked); if (e.target.checked) { const age = dobToAge(form.clientDOB); if (age != null) set('retirementAge')(String(age)); } }} className="toggle-checkbox" />
                <span className="toggle-track"><span className="toggle-thumb" /></span>
                Currently Retired
              </label>
            </div>
            <div className="spouse-toggle-row">
              <label className="toggle-label">
                <input type="checkbox" checked={form.includeSpouse} onChange={e => set('includeSpouse')(e.target.checked)} className="toggle-checkbox" />
                <span className="toggle-track"><span className="toggle-thumb" /></span>
                Include Spouse / Partner
              </label>
            </div>
            {form.includeSpouse && (
              <div className="spouse-fields">
                <FieldRow>
                  <TextField label="Spouse First Name" value={form.spouseFirstName} onChange={set('spouseFirstName')} placeholder="John" />
                  <TextField label="Spouse Last Name" value={form.spouseLastName} onChange={set('spouseLastName')} placeholder="Smith" />
                </FieldRow>
                <FieldRow>
                  <DateField label="Spouse Date of Birth" value={form.spouseDOB} onChange={set('spouseDOB')} />
                </FieldRow>
              </div>
            )}
          </SectionCard>

          <SectionCard title="Section 2 — Assets">
            {loadedRecordType === 'lead' && (
              <AssetTable
                rows={assetRows}
                setRows={setAssetRows}
                onSumsChange={(sums) => {
                  if (sums['401k'] > 0) set('balance401k')(String(sums['401k']));
                  if (sums['ira']  > 0) set('balanceIRA')(String(sums['ira']));
                  if (sums['roth'] > 0) set('balanceRothIRA')(String(sums['roth']));
                  if (sums['other'] > 0) set('balanceOther')(String(sums['other']));
                }}
              />
            )}
            <FieldRow>
              <CurrencyField label="401(k) Balance" value={form.balance401k} onChange={set('balance401k')} />
              <CurrencyField label="IRA Balance" value={form.balanceIRA} onChange={set('balanceIRA')} />
            </FieldRow>
            <FieldRow>
              <CurrencyField label="Roth IRA Balance" value={form.balanceRothIRA} onChange={set('balanceRothIRA')} />
              <CurrencyField label="Other Investment Accounts" value={form.balanceOther} onChange={set('balanceOther')} hint="Brokerage, taxable, etc." />
            </FieldRow>
            <div className="field-group-label">Non-Convertible Assets</div>
            <FieldRow>
              <CurrencyField label="Annuity / Non-Convertible Balance" value={form.annuityBalance} onChange={set('annuityBalance')} hint="Pre-tax, cannot be Roth-converted" />
              <SelectField
                label="Annuity Growth Rate"
                value={String(form.annuityROR)}
                onChange={v => set('annuityROR')(parseFloat(v))}
                options={[
                  { value: '2', label: '2%' },
                  { value: '3', label: '3%' },
                  { value: '4', label: '4% (default)' },
                  { value: '5', label: '5%' },
                  { value: '6', label: '6%' },
                ]}
              />
            </FieldRow>
            <FieldRow>
              <CurrencyField label="Roth 401k / Roth IRA Contribution" value={form.monthlyRothContribution} onChange={set('monthlyRothContribution')} hint="Monthly → Roth balance (stops at retirement)" />
              <CurrencyField label="Other / Taxable Contribution" value={form.monthlyOtherContribution} onChange={set('monthlyOtherContribution')} hint="Monthly → Other Investments (stops at retirement)" />
            </FieldRow>
          </SectionCard>

          <SectionCard title="Section 3 — Income">
            {loadedRecordType === 'lead' && (
              <div>{/* IncomeTable coming in Commit 2 */}</div>
            )}
            <FieldRow>
              <CurrencyField label="Client Social Security" value={form.clientSSMonthly} onChange={set('clientSSMonthly')} hint="Monthly benefit at the age below" />
              <AgeField label="Benefit Stated At Age" value={form.clientSSBasisAge} onChange={set('clientSSBasisAge')} hint="Age on SSA statement (62–70)" />
              <AgeField label="Default Claiming Age" value={form.clientSSClaimingAge} onChange={set('clientSSClaimingAge')} hint="Baseline / scenario default (62–70)" />
            </FieldRow>
            {form.includeSpouse && (
              <FieldRow>
                <CurrencyField label="Spouse Social Security" value={form.spouseSSMonthly} onChange={set('spouseSSMonthly')} hint="Monthly benefit at the age below" />
                <AgeField label="Benefit Stated At Age" value={form.spouseSSBasisAge} onChange={set('spouseSSBasisAge')} hint="Age on SSA statement (62–70)" />
                <AgeField label="Default Claiming Age" value={form.spouseSSClaimingAge} onChange={set('spouseSSClaimingAge')} hint="Baseline / scenario default (62–70)" />
              </FieldRow>
            )}
            <FieldRow>
              <CurrencyField label="Pension / Annuity / Other" value={form.pensionMonthly} onChange={set('pensionMonthly')} hint="Monthly guaranteed income" />
            </FieldRow>
            <div className="income-block">
              <div className="income-block-title">Other Income Streams</div>
              <div className="income-block-sub">
                Earned income, dividends, capital gains, rental, and more — each stacks into
                every year's tax within its age window (works even after retirement).
              </div>
              <IncomeSection form={form} set={set} />
            </div>
          </SectionCard>

          <SectionCard title="Section 4 — Retirement Expenses">
            <FieldRow>
              <CurrencyField label="Estimated Monthly Expenses in Retirement" value={form.monthlyExpenses} onChange={set('monthlyExpenses')} hint="In today's dollars" />
            </FieldRow>
          </SectionCard>

          <SectionCard title="Section 5 — Global Assumptions">
            <FieldRow>
              <SelectField
                label="Filing Status"
                value={form.filingStatus}
                onChange={set('filingStatus')}
                options={[
                  { value: 'mfj', label: 'Married Filing Jointly (MFJ)' },
                  { value: 'single', label: 'Single' },
                ]}
              />
              <NumberField
                label="Dependents"
                value={form.dependents}
                onChange={set('dependents')}
                min={0} max={15} step={1}
                hint="CA exemption credit only ($475 each)"
              />
              <SelectField
                label="State"
                value={form.state}
                onChange={set('state')}
                options={[
                  { value: '', label: '— Select State —' },
                  { value: 'CA', label: 'California (CA) — Progressive up to 12.3%, SS exempt' },
                  { value: 'GA', label: 'Georgia (GA) — 5.49% flat, $65k retirement exclusion' },
                  { value: 'LA', label: 'Louisiana (LA) — 3% flat, $12k retirement exclusion' },
                  { value: 'FL', label: 'Florida (FL) — No income tax' },
                  { value: 'NC', label: 'North Carolina (NC) — 4.5% flat' },
                  { value: 'PA', label: 'Pennsylvania (PA) — 3.07% wages only; retirement income & conversions exempt' },
                  { value: 'SC', label: 'South Carolina (SC) — 6.2% flat' },
                  { value: 'TN', label: 'Tennessee (TN) — No income tax' },
                  { value: 'VA', label: 'Virginia (VA) — Progressive 2%–5.75%' },
                  { value: 'OTHER', label: 'Other — Enter flat rate below' },
                ]}
              />
            </FieldRow>
            {form.state === 'OTHER' && (
              <FieldRow>
                <NumberField label="State Income Tax Rate" value={form.stateFlatRate} onChange={set('stateFlatRate')} min={0} max={20} step={0.1} suffix="%" />
              </FieldRow>
            )}
            <FieldRow>
              <NumberField label="Portfolio Growth Rate" value={form.growthRate} onChange={set('growthRate')} min={0} max={20} step={0.1} suffix="%" hint="Pre-retirement, nominal" />
              <NumberField label="SS COLA Rate" value={form.ssCOLARate} onChange={set('ssCOLARate')} min={0} max={10} step={0.1} suffix="%" hint="Social Security COLA" />
              <NumberField label="Inflation Rate" value={form.inflationRate} onChange={set('inflationRate')} min={0} max={10} step={0.25} suffix="%" hint="Expense inflation" />
            </FieldRow>
            <FieldRow>
              <NumberField label="Current Marginal Rate" value={form.currentTaxRate} onChange={set('currentTaxRate')} min={0} max={60} step={0.5} suffix="%" hint="Reference only" />
              <NumberField label="Projected Marginal Rate" value={form.retirementTaxRate} onChange={set('retirementTaxRate')} min={0} max={60} step={0.5} suffix="%" hint="Reference only" />
              <NumberField label="Life Expectancy" value={form.lifeExpectancy} onChange={set('lifeExpectancy')} min={75} max={100} step={1} suffix="yrs" />
            </FieldRow>
            <p className="tax-rate-note">Marginal rates shown for reference. Progressive calculation drives actual tax in the annual table.</p>
            <FieldRow>
              <NumberField label="Survivor Age (Client)" value={form.survivorAge} onChange={set('survivorAge')} min={60} max={90} step={1} suffix="yrs" hint="Age survivor mode begins" />
            </FieldRow>
            <div className="assumptions-toggles">
              <label className="toggle-label">
                <input type="checkbox" checked={form.survivorEnabled} onChange={e => set('survivorEnabled')(e.target.checked)} className="toggle-checkbox" />
                <span className="toggle-track"><span className="toggle-thumb" /></span>
                Survivor View — switch to Single filing at survivor age
              </label>
            </div>
          </SectionCard>

          {loadedRecordType === 'lead' && (
            <>
              <SectionCard title="Why Convert?">
                <div className="why-convert-cards">
                  {[
                    { key: 'legacy',  title: 'Legacy',                 body: 'Pass tax-free wealth to heirs — Roth assets transfer with no income tax burden to beneficiaries.' },
                    { key: 'income',  title: 'Increased Tax-Free Income', body: 'Replace taxable RMDs with tax-free Roth distributions — more income, lower Medicare premiums.' },
                    { key: 'taxes',   title: 'Lower Lifetime Taxes',   body: 'Convert at today\'s rates before RMDs force higher brackets — reduce total lifetime tax exposure.' },
                  ].map(({ key, title, body }) => (
                    <div
                      key={key}
                      className={`why-convert-card${whySelected.includes(key) ? ' active' : ''}`}
                      onClick={() => setWhySelected(prev =>
                        prev.includes(key) ? prev.filter(k => k !== key) : [...prev, key]
                      )}
                    >
                      <div className="why-convert-card-title">{title}</div>
                      <div className="why-convert-card-body">{body}</div>
                    </div>
                  ))}
                </div>
              </SectionCard>

              {/* ── BOOK YOUR ROTH SOLUTION SESSION ── */}
              <SectionCard title="Book Your Roth Solution Session">
                <div className="book-session-cta">
                  <p className="book-session-text">
                    Your data is complete. Book the Roth Solution Session now.
                  </p>

                  {/* Primary CTA — opens GHL calendar in new tab */}
                  <a
                    className="book-session-calendar-btn"
                    href={ZOOM2_BOOKING_URL}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    📅 Book Roth Solution Session
                  </a>

                  {/* Manual date/time entry — saves to Supabase for prospect banner */}
                  <div className="book-session-pickers">
                    <label className="book-session-picker-label">
                      Date
                      <input
                        type="date"
                        className="book-session-picker-input"
                        value={zoom2Date}
                        onChange={e => { setZoom2Date(e.target.value); setZoom2Saved(null); }}
                      />
                    </label>
                    <label className="book-session-picker-label">
                      Time
                      <input
                        type="time"
                        className="book-session-picker-input"
                        value={zoom2Time}
                        onChange={e => { setZoom2Time(e.target.value); setZoom2Saved(null); }}
                      />
                    </label>
                    <button
                      className="book-session-save-btn"
                      disabled={!zoom2Date || !zoom2Time || zoom2Saving || !loadedProspectId}
                      onClick={async () => {
                        setZoom2Saving(true);
                        const { error } = await supabase
                          .from('rc_intake_forms')
                          .update({ zoom2_date: zoom2Date, zoom2_time: zoom2Time })
                          .eq('id', loadedProspectId);
                        setZoom2Saving(false);
                        if (error) {
                          setZoom2Saved({ ok: false, msg: 'Save failed: ' + error.message });
                        } else {
                          setZoom2Saved({ ok: true, msg: `Session booked for ${zoom2Date} at ${zoom2Time}` });
                        }
                      }}
                    >
                      {zoom2Saving ? 'Saving…' : 'Save Session'}
                    </button>
                  </div>
                  {zoom2Saved && (
                    <div className={`book-session-confirm${zoom2Saved.ok ? '' : ' book-session-confirm-error'}`}>
                      {zoom2Saved.msg}
                    </div>
                  )}
                </div>
              </SectionCard>

              <SectionCard title="What's Next">
                <ul className="whats-next-list">
                  <li className="whats-next-item whats-next-done">Assets &amp; Income ✅</li>
                  <li className="whats-next-item whats-next-done">Amount &amp; Time to Convert ✅</li>
                  <li className={`whats-next-item ${riskCompleted ? 'whats-next-done' : 'whats-next-todo'}`}>
                    <span
                      onClick={() => setRiskCompleted(prev => !prev)}
                      style={{ cursor: 'pointer', userSelect: 'none' }}
                    >
                      {riskCompleted ? 'Risk Score ✅' : 'Risk Score ⬜'} <span style={{ fontSize: '11px', color: '#64748b' }}>(completed in Nitrogen)</span>
                    </span>
                  </li>
                  <li className="whats-next-item whats-next-todo">
                    Account Statements ⬜ <span style={{ fontSize: '11px', color: '#64748b' }}>— check confirmation email for secure upload link</span>
                  </li>
                </ul>
              </SectionCard>
            </>
          )}

          {loadedRecordType === 'lead' && !planReady ? (
            <div className="plan-gate">
              <div className="plan-gate-text">Full data COMPLETE! Plan Construction is the next step. Ready?</div>
              <button className="plan-gate-btn" onClick={() => setPlanReady(true)}>Let's Build the Plan</button>
            </div>
          ) : (
          <SectionCard title="Section 6 — Roth Conversion Scenarios">
            <ConversionGoalSelector form={form} set={set} />
            {form.rothScenarios.length === 0 && (
              <p className="no-scenarios-hint">No scenarios yet. Click "Add Scenario" to build out conversion options.</p>
            )}
            {form.rothScenarios.map((scenario, i) => (
              <ScenarioForm
                key={scenario.id}
                scenario={scenario}
                index={i}
                onChange={updated => updateScenario(i, updated)}
                onRemove={() => removeScenario(i)}
                onSetLive={() => setLiveScenarioId(prev => prev === scenario.id ? null : scenario.id)}
                isLive={liveScenarioId === scenario.id}
                clientAge={results?.clientAge ?? attainedAge(form.clientDOB)}
                results={results}
                form={form}
                set={set}
              />
            ))}
            <button className="add-scenario-btn" onClick={addScenario}>+ Add Scenario</button>
          </SectionCard>
          )}

          {/* CALCULATE BUTTON */}
          <div className="calculate-row">
            <button className="prefill-btn" onClick={handlePrefill}>
              Pre-fill Test Data
            </button>
            <div className="save-client-wrap">
              <button className="save-client-btn" onClick={handleSaveClient} disabled={saving}>
                {saving ? 'Saving...' : 'Save Client'}
              </button>
              <span className="save-mode-hint">
                {loadedPlanId ? 'Updating existing plan' : 'Saving new plan'}
              </span>
            </div>
            <button className="calculate-btn" onClick={handleCalculate}>
              Calculate Plan
            </button>
          </div>

          {/* ZOOM 1 CLOSE-OUT — call order top to bottom: Book Zoom 2 → Risk Score → Confirm */}
          <div className="z1-steps">
            <div className="z1-step">
              <span className="z1-step-num">1</span>
              <button className="zoom2-book-btn" onClick={() => setBookModalOpen(true)}>
                Book Zoom 2
              </button>
              <label className="zoom2-date-field">
                <span>Zoom 2 date/time</span>
                <input
                  type="datetime-local"
                  value={form.zoom2Date || ''}
                  onChange={e => set('zoom2Date')(e.target.value)}
                />
              </label>
            </div>
            <div className="z1-step">
              <span className="z1-step-num">2</span>
              {form.riskScore ? (
                <div className="profile-risk-summary">
                  <span className="profile-risk-text">
                    Risk Score: <strong>{form.riskScore.composite}</strong> · {form.riskScore.band} ·{' '}
                    CE: <span className={`ce-dot ce-${form.riskScore.ceSignal.level}`} />{form.riskScore.ceSignal.label} ·{' '}
                    {fmtRiskDate(form.riskScore.completedAt)}
                  </span>
                  <button className="profile-risk-rerun" onClick={() => setRiskModalOpen(true)}>Re-run</button>
                </div>
              ) : (
                <button className="profile-risk-btn" onClick={() => setRiskModalOpen(true)}>Run Risk Score</button>
              )}
            </div>
            <div className="z1-step">
              <span className="z1-step-num">3</span>
              <button
                className="zoom2-confirm-btn"
                disabled={!(form.riskScore?.composite && form.zoom2Date)}
                title={!(form.riskScore?.composite && form.zoom2Date)
                  ? 'Book Zoom 2 and run the Risk Score first' : ''}
                onClick={() => setConfirmOpen(true)}
              >
                Confirm Screen →
              </button>
            </div>
          </div>

        </div>
      </div>

      {/* RESULTS */}
      <div ref={resultsRef}>
        <ResultsSection results={results} form={form} />
      </div>

      {/* V2 ANNUAL TABLE */}
      <AnnualTableSection
        results={results}
        form={form}
        set={set}
        liveScenarioId={liveScenarioId}
        setLiveScenarioId={setLiveScenarioId}
        printScenarioIds={printScenarioIds}
        setPrintScenarioIds={setPrintScenarioIds}
        setShowCEReport={setShowCEReport}
      />

      {/* PRINT PAGES */}
      {(() => {
        let conversionTarget = null;
        if (form.conversionGoal === 'full_roth') conversionTarget = num(form.balance401k) + num(form.balanceIRA) || null;
        else conversionTarget = num(form.customTarget) || null;
        return <PrintPlan form={form} results={results} conversionTarget={conversionTarget} printScenarioIds={printScenarioIds} liveScenarioId={liveScenarioId} />;
      })()}

      {/* CE PORTFOLIO REPORT */}
      {showCEReport && results && (
        <CEPortfolioReport
          form={form}
          results={results}
          onClose={() => setShowCEReport(false)}
        />
      )}

      {/* JBF RISK SCORE */}
      {riskModalOpen && (
        <RiskScoreModal
          existing={form.riskScore}
          clientName={[form.clientFirstName, form.clientLastName].filter(Boolean).join(' ')}
          onSave={(rs) => { set('riskScore')(rs); setRiskModalOpen(false); }}
          onClose={() => setRiskModalOpen(false)}
        />
      )}

      {/* BOOK ZOOM 2 — embedded GHL calendar */}
      {bookModalOpen && (
        <div className="zoom2-overlay" onMouseDown={e => { if (e.target === e.currentTarget) setBookModalOpen(false); }}>
          <div className="zoom2-modal" role="dialog" aria-modal="true" aria-label="Book Zoom 2">
            <div className="zoom2-modal-head">
              <div className="zoom2-modal-title">Book Zoom 2 — Roth Plan Review</div>
              <button className="zoom2-modal-close" onClick={() => setBookModalOpen(false)} aria-label="Close">✕</button>
            </div>
            <iframe
              src={(() => {
                // Prefill the GHL booking form from the plan (GHL reads standard
                // query params). Phone isn't collected in JBFP — prospect types it.
                const p = new URLSearchParams();
                if (form.clientFirstName) p.set('first_name', form.clientFirstName.trim());
                if (form.clientLastName)  p.set('last_name',  form.clientLastName.trim());
                if (form.email)           p.set('email',      form.email.trim());
                const qs = p.toString();
                return qs ? `${ZOOM2_BOOKING_URL}?${qs}` : ZOOM2_BOOKING_URL;
              })()}
              title="Zoom 2 booking calendar"
              className="zoom2-modal-iframe"
            />
            <div className="zoom2-modal-foot">
              After booking, enter the date/time in the Zoom 2 field, then close this window.
            </div>
          </div>
        </div>
      )}

      {/* END-OF-ZOOM-1 CONFIRM SCREEN */}
      {confirmOpen && (
        <ConfirmScreen
          clientName={[form.clientFirstName, form.clientLastName].filter(Boolean).join(' ')}
          riskScore={form.riskScore}
          tier={CE_TIERS[tierForRiskScore(form.riskScore?.composite)] || CE_TIERS.T2}
          zoom2Date={form.zoom2Date}
          uploadUrl={UPLOAD_PAGE_URL}
          onClose={() => setConfirmOpen(false)}
        />
      )}
    </div>
  );
}